<template>
  <div class="playground-container" :class="{ 'mobile-mode': isMobile }">
    <!-- Notification -->
    <div 
      v-if="notification.show" 
      class="notification"
      :class="notification.type"
    >
      {{ notification.message }}
    </div>

    <!-- Header buttons -->
    <div class="header-buttons" :class="{ 'ai-sidebar-open': showAiCopilot }">
      
      <!-- Griot AI Training Status Indicator -->
      <div v-if="selectedBlock" class="griot-train-indicator" :class="{ 'is-trained': isBlockTrained, 'is-training': isTraining }">
        <div class="griot-train-inner">
          <!-- Bouncing ball loader (visible during training) -->
          <div v-if="isTraining" class="griot-bounce-loader">
            <div class="griot-bounce-ball"></div>
          </div>
          <!-- Status dot (visible when NOT training) -->
          <span v-else class="griot-status-dot"></span>
          <!-- Status text -->
          <span class="griot-status-label">
            {{ isTraining ? 'Griot apprend...' : 'Appris' }}
          </span>
        </div>
      </div>



      <!-- Action Bar Group -->
      <div class="playground-action-bar">
        <!-- Retour -->
        <button 
          v-if="selectedBlock || currentMode === 'actions'" 
          class="action-btn" 
          @click="currentMode === 'actions' ? toggleActionsMode() : goBack()" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Retour</strong><br>Revenir à l'éditeur de flux principal."
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>

        <!-- Dupliquer -->
        <button 
          v-if="selectedBlock" 
          class="action-btn" 
          @click="duplicateBlock" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Dupliquer</strong><br>Créer une copie exacte de ce bloc."
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>

        <!-- Dump Flow -->
        <button 
          v-if="selectedBlock && isLocal" 
          class="action-btn" 
          @click="openDumpModal" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Voir le JSON</strong><br>Afficher les données brutes du flux."
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
        </button>

        <div class="action-divider"></div>

        <!-- Test Chat -->
        <button 
          class="action-btn" 
          :class="{ 'is-active': showWidgetChat }"
          @click="toggleWidgetChat" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Tester le chatbot</strong><br>Simuler une conversation en direct."
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>

        <!-- Actions Manager Toggle -->
        <button 
          class="action-btn" 
          :class="{ 'is-active': currentMode === 'actions' }"
          @click="toggleActionsMode" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Gestionnaire d'Actions</strong><br>Créer des crons et déclencheurs planifiés."
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </button>

        <div class="action-divider"></div>

        <!-- Moo (Sidebar Toggle) -->
        <button 
          class="action-btn" 
          :class="{ 'is-active': showAiCopilot }"
          @click="toggleMoo" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Ouvrir Moo</strong><br>Lancer l'assistant IA de construction."
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <path d="M15 3v18"/>
          </svg>
        </button>

        <!-- Griot Builder (IA Page) - Masqué temporairement 
        <button 
          v-if="$route.params.idchatbot !== '69eb5b7c3498c697cc9856e4'"
          class="action-btn" 
          @click="goToGriotBuilder" 
          @mouseenter="showTooltip($event)"
          @mouseleave="hideTooltip"
          data-tooltip="<strong>Griot AI</strong><br>Gérer la base de connaissances de l'IA."
        >
          <span class="ia-text-icon">{{ isEnglish ? 'AI' : 'IA' }}</span>
        </button>
        -->
      </div>
    </div>

    <!-- Sidebar -->
    <div class="sidebar">
      <div class="sidebar-header">
        <div class="header-title">
          <div class="logo">
            <img src="https://dashboard.peelo.io/img/logo-white.52979151.png" alt="Peelo Logo" />
          </div>
          <router-link 
            :to="`/dashboard/chatbots/${$route.params.idchatbot}/home`" 
            class="sidebar-back-btn" 
            @mouseenter="showTooltip" 
            @mouseleave="hideTooltip"
            data-tooltip="Retour au tableau de bord"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </router-link>
        </div>

        
        <!-- Informations du chatbot -->
        <div class="chatbot-info" v-if="chatbot && chatbot.bot_name">
          <div class="chatbot-identity">
            <img 
              v-if="getChatbotAvatar(chatbot)" 
              :src="getChatbotAvatar(chatbot)" 
              alt="Avatar" 
              class="chatbot-avatar"
            />
            <div class="chatbot-details">
              <router-link :to="`/dashboard/chatbots/${$route.params.idchatbot}/home`" class="chatbot-name">
                {{ chatbot.bot_name }}
              </router-link>
              <p class="chatbot-description" v-if="chatbot.description">
                {{ chatbot.description }}
              </p>
            </div>
          </div>
        </div>



        <!-- Griot IA Activation (Sidebar) -->
        <div class="griot-ia-sidebar-section" style="margin-bottom: 8px; padding: 0 15px;">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 6px 8px; border-radius: 6px; color: #9ca3af;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <!-- Indicateur d'état lumineux lié au bloc par défaut -->
              <div :style="{ 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                background: defaultBlock && defaultBlock.jarvis ? '#DAFFD1' : '#4b5563',
                boxShadow: defaultBlock && defaultBlock.jarvis ? '0 0 10px #DAFFD1' : 'none',
                transition: 'all 0.3s ease'
              }"></div>
              
              <router-link 
                :to="`/dashboard/chatbots/${$route.params.idchatbot}/jarvis-knowledge?from=playground`"
                :style="{ 
                  color: defaultBlock && defaultBlock.jarvis ? '#DAFFD1' : 'inherit', 
                  fontSize: '13px', 
                  fontWeight: defaultBlock && defaultBlock.jarvis ? '600' : '500', 
                  textDecoration: 'none', 
                  transition: 'all 0.3s ease' 
                }"
                data-tooltip="Voir les connaissances"
              >
                {{ isEnglish ? 'Griot AI' : 'Griot IA' }}
              </router-link>
            </div>

            <!-- Toggle accessible tout le temps pour l'IA -->
            <label v-if="defaultBlock" class="ai-switch" data-tooltip="Activer/Désactiver Griot IA" style="margin-left: 0;">
              <input 
                type="checkbox" 
                :checked="defaultBlock.jarvis"
                @change="toggleGlobalJarvis"
              >
              <span class="ai-slider"></span>
            </label>
          </div>
        </div>

        <!-- Jauges de consommation IA (Moo + Griot) du mois -->
        <ConsumptionGauges :idbot="$route.params.idchatbot" />

        <!-- System Prompt Sidebar Link (superadmin uniquement) -->
        <div class="prompt-preview-sidebar-section" v-if="chatbot && isSuperAdmin" style="margin-bottom: 12px; padding: 0 15px;">
          <div class="prompt-sidebar-header hover-bg-white-10" @click="openSystemPromptModal" style="display: flex; align-items: center; justify-content: space-between; cursor: pointer; color: #9ca3af; font-size: 13px; font-weight: 500; margin-bottom: 4px; padding: 6px 8px; border-radius: 6px; transition: background 0.2s;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line>
              </svg>
              <span>System Prompt</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
               <span style="font-size: 10px; opacity: 0.6; background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 4px;">~{{ systemPromptTokens }} tkns</span>
               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                 <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line>
               </svg>
            </div>
          </div>
        </div>
        <!-- Liste des variables collapsable -->
        <div class="variables-section" v-if="chatbot && variables.length > 0" style="margin-bottom: 12px; padding: 0 15px;">
          <div class="variables-header-toggle" @click="showVariablesList = !showVariablesList" style="display: flex; align-items: center; justify-content: space-between; cursor: pointer; color: #9ca3af; font-size: 13px; font-weight: 500; margin-bottom: 8px; padding: 6px 8px; border-radius: 6px; transition: background 0.2s;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M7 21h10"/>
                <path d="M12 21v-8"/>
                <path d="M5 11c6-6 6-6 12 0"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span>Variables ({{ variables.length }})</span>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :style="{ transform: showVariablesList ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
          <div v-if="showVariablesList" class="variables-list-dropdown" style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; max-height: 200px; overflow-y: auto;">
            <div v-for="(v, index) in variables" :key="index" class="variable-item-pill" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 6px 10px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; font-size: 12px; color: #d1d5db;">
              <div style="display: flex; align-items: center; gap: 6px; min-width: 0;">
                <span style="font-family: monospace; color: #10b981; flex-shrink: 0;">&#123;&#123;</span>
                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="v.slug">{{ v.slug }}</span>
                <span style="font-family: monospace; color: #10b981; flex-shrink: 0;">&#125;&#125;</span>
              </div>
              <div style="display: flex; align-items: center; gap: 8px; flex-shrink: 0;">
                <span v-if="v.type" style="font-size: 10px; color: #6b7280; text-transform: uppercase;">{{ v.type }}</span>
                <button v-if="v.type !== 'global'" @click.stop="promptDeleteVariable(v)" class="delete-var-btn" title="Supprimer cette variable">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="create-variable" v-if="chatbot" style="margin-bottom: 20px;">
          <button class="create-variable-btn" @click="showCreateVariableModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Créer une variable
          </button>
        </div>

      </div>

      <div class="expanded-sidebar">
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
          <p>Chargement des flux...</p>
        </div>

        <div v-else-if="error" class="error-state">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p>{{ error }}</p>
          <button @click="initializeData" class="retry-btn">Réessayer</button>
        </div>

        <div v-else>
          <!-- Actions Manager Sidebar Link (Compact Premium Card) -->
          <div class="actions-manager-sidebar-section" v-if="chatbot" style="margin-bottom: 16px; padding: 0 15px;">
            <div 
              @click="toggleActionsMode" 
              class="premium-action-card"
              :class="{ 'is-active': currentMode === 'actions' }"
            >
              <div class="premium-action-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div class="premium-action-text">
                <h4>Gestionnaire d'Actions</h4>
              </div>
              <div class="premium-action-arrow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Constitution Sidebar Link (Compact Premium Card) -->
          <div class="constitution-sidebar-section" v-if="chatbot" style="margin-bottom: 16px; padding: 0 15px;">
            <div
              @click="openConstitutionModal"
              class="premium-action-card constitution-action-card"
            >
              <div class="premium-action-icon constitution-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
              </div>
              <div class="premium-action-text constitution-text">
                <h4>Constitution de l'agent</h4>
                <span class="constitution-subtitle">Identité · ton · périmètre</span>
              </div>
              <div class="premium-action-arrow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Main Flows Section -->
          <div class="folder-section">
            <div class="folder-header" @click="toggleFolder('welcome')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'rotated': !foldersState.welcome }">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span>Blocs par défaut</span>
            </div>
            <div v-if="foldersState.welcome" class="folder-content">
              <div 
                v-for="block in filteredPrimaryBlocks" 
                :key="block._id" 
                class="flow-button"
                :class="{ 'active': selectedBlock?._id === block._id }"
                @click="handleBlockClick(block)"
                @dragover.prevent="dragOverBlock($event)"
                @dragleave="dragLeaveBlock($event)"
                @drop="dropOnBlock($event, block)"
              >
                {{ block.displayName }}
              </div>
            </div>
          </div>

          <!-- Standard Flows Section -->
          <div class="folder-section">
            <div class="folder-header" @click="toggleFolder('standard')" style="display: flex; justify-content: space-between; align-items: center;">
              <div style="display: flex; align-items: center;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'rotated': !foldersState.standard }">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
                <span>Blocs créés</span>
              </div>
              <button 
                class="add-btn-subtle" 
                @click.stop="openCreateBlockModal" 
                @mouseenter="showTooltip" 
                @mouseleave="hideTooltip"
                data-tooltip="Créer un nouveau bloc"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
            <div v-if="foldersState.standard" class="folder-content">
              <!-- Search Bar -->
              <div class="search-container">
                <div class="search-input">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input 
                    type="text" 
                    placeholder="Rechercher un bloc..." 
                    v-model="searchQuery" 
                    @input="filterBlocks"
                  />
                </div>
              </div>
              <div 
                v-for="block in filteredSecondaryBlocks" 
                :key="block.idblock" 
                class="flow-button"
                :class="{ 'active': selectedBlock && selectedBlock.idblock === block.idblock }"
                @click="handleBlockClick(block)"
                @dragover.prevent="dragOverBlock($event)"
                @dragleave="dragLeaveBlock($event)"
                @drop="dropOnBlock($event, block)"
              >
                <span class="block-name-text">{{ block.displayName }}</span>
                <button 
                  class="delete-block-icon-btn" 
                  @click.stop="openDeleteModal(block)"
                  title="Supprimer ce bloc"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content" @contextmenu.prevent="handleContextMenu">
      <div class="workspace" :class="{ 'full-canvas': !selectedBlock }">
        
        <!-- Actions Manager -->
        <div v-if="currentMode === 'actions'" style="width: 100%; height: 100%;">
          <ActionsManager :chatbot-id="chatbot._id || chatbot.id" :chatbot="chatbot" :blocks="blocks" :variables="variables" />
        </div>

        <!-- Flow builder canvas -->
          <div v-else-if="!selectedBlock" class="empty-state" style="padding: 0; display: block; overflow: hidden;">
             <FlowGraphCanvas 
               v-if="chatbot && (chatbot._id || chatbot.id)" 
               :chatbot-id="chatbot._id || chatbot.id"
               @node-click="handleGraphNodeClick"
             />
             <div v-else class="loading-graph">Chargement...</div>
          </div>

          <div v-else>
            
            <div class="block-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <div class="block-name-container">
                <input
                  v-if="isEditingBlockName"
                  v-model="editedBlockName"
                  @blur="saveBlockName"
                  @keyup.enter="saveBlockName"
                  @keyup.esc="cancelBlockNameEdit"
                  ref="blockNameInput"
                  class="block-name-input"
                  :placeholder="selectedBlock.blockname ? 'Modifier le nom' : 'Créer un nom pour le bloc'"
                  maxlength="20"
                  @input="validateBlockName"
                />
                <span
                  v-if="isEditingBlockName"
                  class="char-count"
                  :class="{ 'char-count-limit': editedBlockName.length >= 20 }"
                >
                  {{ editedBlockName.length }}/20
                </span>
                <div v-else class="block-name-wrapper" style="width: 100%;">
                  <div class="name-display-row" style="display: flex; flex-direction: column; align-items: flex-start; gap: 0.25rem; width: 100%;">
                    <span
                      @click="startEditingBlockName"
                      class="block-name"
                      :class="{ 'no-name': !selectedBlock.blockname }"
                    >
                      {{ selectedBlock.blockname || 'Cliquez pour ajouter un nom' }}
                    </span>
                  <div class="block-id-container">
                    <span class="block-id">{{ selectedBlock.idblock }}</span>
                    <button 
                      class="copy-id-btn" 
                      @click="copyIdBlock" 
                      :title="`Copier l'ID: ${selectedBlock.idblock}`"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                    <button 
                      class="test-wa-btn" 
                      @click="openWhatsAppTest" 
                      :title="`Tester ce bloc sur WhatsApp (${chatbot?.display_phone_number || 'Non configuré'})`"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                      </svg>
                    </button>
                  </div>
                  </div>
                  
                  <!-- Description du bloc (commentaire) -->
                  <div class="block-description-container">
                    <div v-if="isEditingDescription" class="description-edit-mode">
                      <span class="description-prefix">//</span>
            <textarea
              v-model="editedDescription"
              @blur="saveDescription"
              @keyup.enter="saveDescription"
              @keyup.esc="cancelDescriptionEdit"
              @input="adjustTextareaHeight"
              ref="descriptionInput"
              class="description-input"
              :placeholder="'Décrivez ce que fait ce bloc pour aider Moo...'"
              maxlength="1000"
              rows="1"
            />
                      <span class="description-char-count" :class="{ 'char-limit': editedDescription.length >= 1000 }">
                        {{ editedDescription.length }}/1000
                      </span>
                    </div>
                    <div v-else class="description-display" @click="startEditingDescription">
                      <span class="description-prefix">//</span>
                      <span 
                        class="description-text"
                        :class="{ 'no-description': !selectedBlock.description }"
                      >
                        {{ selectedBlock.description || 'Cliquez pour ajouter une description pour Moo...' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>



 
            <!-- Links Card -->
            <div class="links-card" v-if="selectedBlock">
              <div class="links-header" @click="toggleLinksExpanded">
                <div class="links-header-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 17H7A5 5 0 0 1 7 7h2"/>
                    <path d="M15 7h2a5 5 0 1 1 0 10h-2"/>
                    <line x1="10" y1="12" x2="14" y2="12"/>
                  </svg>
                  <span class="links-title-text">Liens</span>
                  <div class="links-summary">
                    {{ totalLinksCount }}
                  </div>
                </div>
                <svg 
                  class="collapse-icon" 
                  :class="{ 'expanded': isLinksExpanded }"
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>

              <div class="links-content" v-show="isLinksExpanded">
                <!-- Inbound Links -->
                <div class="links-group" v-if="inboundLinks.length > 0 || loadingInboundLinks">
                  <div class="group-header">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    <span>Entrants ({{ inboundLinks.length }})</span>
                  </div>
                  
                  <div v-if="loadingInboundLinks" class="loading-state">
                    <div class="loading-spinner"></div>
                    <span>Chargement...</span>
                  </div>
                  
                  <div v-else-if="inboundLinks.length === 0" class="empty-state">
                    Aucun lien entrant
                  </div>
                  
                  <div v-else class="links-grid">
                    <div 
                      v-for="link in inboundLinks" 
                      :key="`in-${link.idblock}`"
                      class="link-badge inbound"
                      @click="navigateToBlock(link.idblock)"
                      :title="`${link.blockname || link.idblock} - ${link.sourceType || 'Bloc Standard'}`"
                    >
                      <span class="badge-text">{{ link.blockname || link.idblock }}</span>
                      <span class="badge-source" v-if="link.sourceType">{{ link.sourceType }}</span>
                    </div>
                  </div>
                </div>

                <!-- Outbound Links -->
                <div class="links-group" v-if="outboundLinks.length > 0">
                  <div class="group-header">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                    <span>Sortants ({{ outboundLinks.length }})</span>
                  </div>
                  
                  <div class="links-grid">
                    <div 
                      v-for="link in outboundLinks" 
                      :key="`out-${link.id}`"
                      class="link-badge outbound"
                      @click="navigateToBlock(link.targetBlock)"
                      :title="`${link.targetName || link.targetBlock} - ${link.sourceType}`"
                    >
                      <span class="badge-text">{{ link.targetName || link.targetBlock }}</span>
                      <span class="badge-source">{{ link.sourceType }}</span>
                    </div>
                  </div>
                </div>

                <!-- Empty state when no links -->
                <div v-if="!loadingInboundLinks && inboundLinks.length === 0 && outboundLinks.length === 0" class="no-links-state">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
                  </svg>
                  <span>Aucun lien de navigation détecté</span>
                </div>
              </div>
            </div>
          </div>
            <!-- Flow elements displayed using dynamic components -->
          <div
            v-for="(element, index) in selectedBlock?.flow || []"
            :key="element.id_element || index"
            class="flow-element-wrapper"
            :class="['element-type-' + element.type, { 'is-moo-selected': selectedFlowElement && selectedFlowElement.id_element === element.id_element }]"
          >
            <!-- Boutons de déplacement (Monter/Descendre) -->
            <div class="element-move-controls">
              <button 
                class="move-btn" 
                @click.stop="moveElementUp(index)" 
                :disabled="index === 0" 
                title="Monter cet élément"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
              <button 
                class="move-btn" 
                @click.stop="moveElementDown(index)" 
                :disabled="index === selectedBlock.flow.length - 1" 
                title="Descendre cet élément"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
            </div>
            <!-- Bouton de sélection pour Moo IA -->
            <button
              class="moo-select-btn"
              :class="{ 'is-active': selectedFlowElement && selectedFlowElement.id_element === element.id_element }"
              @click.stop="selectElementForMoo(element)"
              title="Sélectionner cet élément pour Moo IA"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
              </svg>
            </button>

            <!-- Utilisation du composant dynamique approprié -->
            <component
              :is="getElementComponent(element.type)"
              :element="element"
              :index="index"
              :id-bot="$route.params.idchatbot"
              :variables="variables"
              :chatbot="chatbot"
              :blocks="element.type === 'audio_wolof' ? blocks : undefined"
              @update="updateElement($event, index)"
              @remove="deleteElement(index)"
              @duplicate="duplicateElement(index)"
              @notification="showNotification"
              class="dynamic-element"
            >
              <!-- Fallback en cas de composant non trouvé -->

            </component>

          </div>

         


        <!-- Tools sidebar -->
        <div class="tools-sidebar" v-if="selectedBlock">

          
          <!-- Text Element -->
          <button class="tool-btn" @click="insertElement('text')" @mouseenter="showElementInfo('text', $event)" @mouseleave="hideElementInfo" title="Texte - Message simple">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
              <path d="M10 9H8"/>
            </svg>
          </button>
          
          <!-- Text with Buttons Element -->
          <button class="tool-btn" @click="insertElement('text_button')" @mouseenter="showElementInfo('text_button', $event)" @mouseleave="hideElementInfo" title="Texte avec boutons - Message avec boutons interactifs">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <path d="M9 9h6"/>
              <path d="M9 15h6"/>
              <rect x="7" y="12" width="2" height="2"/>
              <rect x="7" y="16" width="2" height="2"/>
            </svg>
          </button>
          
          <!-- List Element -->
          <button class="tool-btn" @click="insertElement('list')" @mouseenter="showElementInfo('list', $event)" @mouseleave="hideElementInfo" title="Liste interactive - Menu de sélection">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </button>
          
          <!-- Image Element -->
          <button class="tool-btn" @click="insertElement('image')" @mouseenter="showElementInfo('image', $event)" @mouseleave="hideElementInfo" title="Image - Contenu visuel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
          </button>
          
          <!-- Document Element -->
          <button class="tool-btn" @click="insertElement('document')" @mouseenter="showElementInfo('document', $event)" @mouseleave="hideElementInfo" title="Document - Fichier PDF">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
              <path d="M10 9H8"/>
            </svg>
          </button>
          
          <!-- Audio Element -->
          <button class="tool-btn" @click="insertElement('audio')" @mouseenter="showElementInfo('audio', $event)" @mouseleave="hideElementInfo" title="Audio - Fichier audio">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18V5l12-2v13"/>
              <circle cx="6" cy="18" r="3"/>
              <circle cx="18" cy="16" r="3"/>
            </svg>
          </button>
          

          
          <!-- Audio wolof Element -->
          <button class="tool-btn" @click="insertElement('audio_wolof')" @mouseenter="showElementInfo('audio_wolof', $event)" @mouseleave="hideElementInfo" title="Audio généré - Voix fabriquée à partir d'un texte">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          </button>

          <!-- Video Element -->
          <button class="tool-btn" @click="insertElement('video')" @mouseenter="showElementInfo('video', $event)" @mouseleave="hideElementInfo" title="Vidéo - Fichier vidéo MP4">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          </button>
          
          <!-- Delay Element -->
          <button class="tool-btn" @click="insertElement('delay')" @mouseenter="showElementInfo('delay', $event)" @mouseleave="hideElementInfo" title="Délai - Pause dans le flux">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </button>

          <!-- Action Element -->
          <button class="tool-btn" @click="insertElement('action')" @mouseenter="showElementInfo('action', $event)" @mouseleave="hideElementInfo" title="Action - Tâche en arrière-plan">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
          </button>
          
          <!-- Request Element -->
          <button class="tool-btn" @click="insertElement('request')" @mouseenter="showElementInfo('request', $event)" @mouseleave="hideElementInfo" title="Requête HTTP - Appel API">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
            </svg>
          </button>
          
          <!-- Answer Element -->
          <button class="tool-btn" @click="insertElement('answer')" @mouseenter="showElementInfo('answer', $event)" @mouseleave="hideElementInfo" title="Réponse utilisateur - Capture d'input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              <path d="M13 8H7"/>
              <path d="M17 12H7"/>
            </svg>
          </button>
          
          <!-- Redirect Element -->
          <button class="tool-btn" @click="insertElement('redirect')" @mouseenter="showElementInfo('redirect', $event)" @mouseleave="hideElementInfo" title="Redirection - Changement de flux">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14"/>
              <path d="M12 5l7 7-7 7"/>
            </svg>
          </button>
          
          <!-- Condition Element -->
          <button class="tool-btn" @click="insertElement('condition')" @mouseenter="showElementInfo('condition', $event)" @mouseleave="hideElementInfo" title="Condition - Logique si/alors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 3v12"></path>
              <circle cx="18" cy="6" r="3"></circle>
              <circle cx="6" cy="18" r="3"></circle>
              <path d="M18 9a9 9 0 0 1-9 9"></path>
            </svg>
          </button>
          
          <!-- Notification Element -->
          <button class="tool-btn" @click="insertElement('notification')" @mouseenter="showElementInfo('notification', $event)" @mouseleave="hideElementInfo" title="Notification - Alerte">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
          </button>
          
          <!-- Ask Payment Element -->
          <button class="tool-btn" @click="insertElement('ask_payment')" @mouseenter="showElementInfo('ask_payment', $event)" @mouseleave="hideElementInfo" title="Demander un paiement - Wave">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
          </button>
          
          <!-- Variable Insert Element -->
          <button class="tool-btn" @click="insertElement('variable_insert')" @mouseenter="showElementInfo('variable_insert', $event)" @mouseleave="hideElementInfo" title="Insertion de variable - Gestion de variables">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M7 21h10"/>
              <path d="M12 21v-8"/>
              <path d="M5 11c6-6 6-6 12 0"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </button>
          
          <!-- Location Request Element -->
          <button class="tool-btn" @click="insertElement('location_request_message')" @mouseenter="showElementInfo('location_request_message', $event)" @mouseleave="hideElementInfo" title="Demande de localisation - Demander la position de l'utilisateur">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </button>
          
          <!-- Button Link Element -->
          <button class="tool-btn" @click="insertElement('button_link')" @mouseenter="showElementInfo('button_link', $event)" @mouseleave="hideElementInfo" title="Carte avec bouton - Image, texte et bouton d'action">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="14" rx="2" ry="2"/>
              <path d="M7 17v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/>
              <circle cx="12" cy="9" r="2"/>
            </svg>
          </button>
          <!-- Carousel Element -->
          <button class="tool-btn" @click="insertElement('carousel')" @mouseenter="showElementInfo('carousel', $event)" @mouseleave="hideElementInfo" title="Carrousel - Cartes image/vidéo défilantes avec boutons">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="7" y="4" width="10" height="16" rx="2"/>
              <path d="M3 8v8"/>
              <path d="M21 8v8"/>
            </svg>
          </button>
          <!-- Template Element -->
          <button class="tool-btn" @click="insertElement('template_message')" @mouseenter="showElementInfo('template_message', $event)" @mouseleave="hideElementInfo" title="Template WhatsApp - Message prédéfini">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
          </button>

          <!-- Agent Handoff Element (RED) -->
          <button class="tool-btn" @click="insertElement('agent_handoff')" @mouseenter="showElementInfo('agent_handoff', $event)" @mouseleave="hideElementInfo" title="Stop AI - Désactiver l'IA pour cet utilisateur" style="color: #ef4444; border-color: rgba(239, 68, 68, 0.3);">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
            </svg>
          </button>
        </div>

        <!-- Panneau d'information dynamique -->
        <div v-if="elementInfo.show" class="element-info-panel">
          <h4>{{ elementInfo.title }}</h4>
          <p>{{ elementInfo.description }}</p>
        </div>

        <!-- Test Elements button -->
        
      </div>


    </div>

    <!-- Simulator Bubble (Moved outside of main-content to prevent refresh) -->
    <!-- Simulator Bubble (Replaced by Script Injection) -->
    <!-- The script is injected via loadWidgetScript method -->

    <!-- Context Menu for Creating Elements -->
    <PlaygroundContextMenu
      :show="showContextMenu"
      :position="contextMenuPosition"
      :available-element-types="availableElementTypes"
      @close="closeContextMenu"
      @create-block="handleCreateBlockFromMenu"
      @create-variable="handleCreateVariableFromMenu"
      @insert-element="addContextMenuElement"
    />
  </div>

  <!-- Modal pour créer une nouvelle variable -->
  <div class="modal-overlay dark-mode" v-if="showCreateVariableModal" @click.self="closeCreateVariableModal">
    <div class="modal-content premium-modal">
      <div class="modal-header">
        <h3>Créer une nouvelle variable</h3>
        <button class="close-btn" @click="closeCreateVariableModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <div class="search-container">
          <div class="search-input-wrapper">
             <input 
               type="text" 
               v-model="newVariableName" 
               placeholder="Entrez le nom de la variable..."
               class="premium-search-input"
               @keyup.enter="saveNewVariable"
               autofocus
             />
             <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
               <path d="M12 20h9"/>
               <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
             </svg>
          </div>
        </div>
        
        <p class="modal-subtitle">Aperçu et validation</p>
        
        <div class="agents-list premium-list" style="overflow-y: visible;">
          <div 
            class="agent-item premium-item" 
            @click="saveNewVariable" 
            :style="{ opacity: (!newVariableName || isCreatingVariable) ? 0.5 : 1, pointerEvents: (!newVariableName || isCreatingVariable) ? 'none' : 'auto' }"
          >
            <div class="agent-avatar-wrapper">
              <div class="agent-avatar" style="font-family: monospace; font-size: 16px;">
                {{'{}'}}
              </div>
              <div class="agent-status-dot active"></div>
            </div>
            
            <div class="agent-info">
              <span class="agent-name" style="font-family: monospace;">
                {{ formattedVariableName || 'nom_de_la_variable' }}
              </span>
              <span class="agent-email">Variable Système Globale</span>
            </div>
            
            <button class="select-agent-btn" @click.stop="saveNewVariable">
              <span v-if="!isCreatingVariable">Créer</span>
              <svg v-else style="animation: spin 1s linear infinite;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 2v4"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal pour confirmer la suppression d'une variable -->
  <div class="modal-overlay dark-mode" v-if="showDeleteVariableModal" @click.self="closeDeleteVariableModal">
    <div class="modal-content premium-modal" style="max-width: 400px;">
      <div class="modal-header" style="padding-bottom: 0;">
        <h3>Confirmer la suppression</h3>
        <button class="close-btn" @click="closeDeleteVariableModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
      <div class="modal-body" style="padding: 24px;">
        <p style="text-align: center; margin-bottom: 24px; color: #4b5563; line-height: 1.5;">
          Êtes-vous sûr de vouloir supprimer la variable<br>
          <strong style="color: #daffd1; font-family: monospace; font-size: 16px; display: inline-block; margin: 8px 0;">{{ variableToDelete?.slug }}</strong> ?<br>
          Cette action est irréversible.
        </p>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button @click="closeDeleteVariableModal" style="padding: 10px 20px; border-radius: 12px; border: 1px solid #e5e7eb; background: white; color: #374151; cursor: pointer; font-weight: 500; transition: all 0.2s;">
            Annuler
          </button>
          <button @click="confirmDeleteVariable" :style="{ opacity: isDeletingVariable ? 0.7 : 1, pointerEvents: isDeletingVariable ? 'none' : 'auto' }" style="padding: 10px 20px; border-radius: 12px; border: none; background: #daffd1; color: #0a1a16; cursor: pointer; font-weight: 500; display: flex; align-items: center; gap: 8px; transition: all 0.2s;">
            <span v-if="!isDeletingVariable">Supprimer</span>
            <svg v-else style="animation: spin 1s linear infinite;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 2v4"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal pour créer un nouveau bloc -->
  <div class="modal-overlay dark-mode" v-if="showCreateBlockModal" @click.self="closeCreateBlockModal">
    <div class="modal-content premium-modal" style="max-width: 500px;">
      <div class="modal-header">
        <h3>Créer un nouveau flux</h3>
        <button class="close-btn" @click="closeCreateBlockModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
      <div class="modal-body" style="padding: 24px;">
        <div class="form-group" style="margin-bottom: 20px;">
          <label for="blockName" style="display: block; margin-bottom: 8px; font-weight: 500; color: #9ca3af;">Nom du flux</label>
          <input 
            type="text" 
            id="blockName" 
            v-model="newBlock.name" 
            @input="generateIdBlock"
            placeholder="Entrez le nom du flux"
            maxlength="20"
            style="width: 100%; padding: 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05); color: white;"
          />
          <small class="char-count" style="display: block; margin-top: 4px; font-size: 11px; color: #6b7280;">{{ newBlock.name.length }}/20 caractères</small>
        </div>
        <div class="form-group">
          <label for="blockId" style="display: block; margin-bottom: 8px; font-weight: 500; color: #9ca3af;">ID du flux</label>
          <input 
            type="text" 
            id="blockId" 
            v-model="newBlock.idblock" 
            disabled
            placeholder="Généré automatiquement"
            style="width: 100%; padding: 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: #6b7280;"
          />
        </div>
      </div>
      <div class="modal-footer" style="padding: 20px 24px;">
        <button class="cancel-btn" @click="closeCreateBlockModal" style="padding: 10px 20px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: transparent; color: white;">Annuler</button>
        <button 
          class="create-btn" 
          @click="createNewBlock" 
          :disabled="!newBlock.name || creatingBlock"
          style="padding: 10px 20px; border-radius: 8px; border: none; background: #daffd1; color: #0a1a16; font-weight: 600;"
        >
          <span v-if="!creatingBlock">Créer</span>
          <span v-else>Création en cours...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Modal Transfert Cross-Block -->
  <div class="modal-overlay dark-mode" v-if="showTransferModal" @click.self="cancelTransfer">
    <div class="modal-content premium-modal" style="max-width: 500px;">
      <div class="modal-header">
        <h3 class="modal-title-transfer">Déplacer l'élément</h3>
        <button class="close-btn" @click="cancelTransfer">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
      <div class="modal-body" style="padding: 24px;">
        <p style="color: #9ca3af; line-height: 1.5;">Souhaitez-vous vraiment déplacer cet élément vers le flux <strong style="color: white;">"{{ transferTargetBlock?.displayName }}"</strong> ?</p>
        <p style="font-size: 0.9em; color: #6b7280; margin-top: 10px;">L'élément sera détaché de sa position actuelle et placé à la fin du flux de destination.</p>
      </div>
      <div class="modal-footer" style="padding: 20px 24px;">
        <button class="cancel-btn" @click="cancelTransfer" :disabled="isTransferring" style="padding: 10px 20px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: transparent; color: white;">Annuler</button>
        <button 
          class="confirm-transfer-btn" 
          @click="confirmTransfer" 
          :disabled="isTransferring"
          style="padding: 10px 20px; border-radius: 8px; border: none; background: #daffd1; color: #0a1a16; font-weight: 600;"
        >
          <span v-if="!isTransferring">Oui, déplacer</span>
          <span v-else>Déplacement...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Dump Modal -->
  <div class="modal-overlay dark-mode" v-if="showDumpModal" @click.self="closeDumpModal">
    <div class="modal-content premium-modal dump-modal" style="max-width: 800px; width: 90%;">
      <div class="modal-header with-tabs">
        <div class="modal-tabs">
           <button
             :class="{ active: activeDumpTab === 'json' }"
             @click="activeDumpTab = 'json'"
           >
             JSON du Bloc
           </button>
           <button
             :class="{ active: activeDumpTab === 'prompt' }"
             @click="activeDumpTab = 'prompt'"
           >
             System Prompt (Griot)
           </button>
           <button
             :class="{ active: activeDumpTab === 'moo' }"
             @click="switchToMooTab"
           >
             System Prompt (Moo)
           </button>
        </div>
        <button class="close-btn" @click="closeDumpModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
      <div class="modal-body" style="padding: 0;">
        <div class="dump-container">
          <textarea 
            v-if="activeDumpTab === 'json'"
            readonly 
            class="dump-textarea" 
            :value="dumpContent"
            ref="dumpTextarea"
            style="width: 100%; height: 500px; padding: 20px; background: rgba(0,0,0,0.2); color: #daffd1; font-family: monospace; border: none; resize: none;"
          ></textarea>
          
          <div
            v-else-if="activeDumpTab === 'prompt'"
            class="dump-textarea dump-content-view"
            v-html="highlightedSystemPrompt"
            style="width: 100%; height: 500px; padding: 20px; background: rgba(0,0,0,0.2); color: #e5e7eb; overflow-y: auto; font-family: monospace;"
          ></div>

          <div
            v-else-if="activeDumpTab === 'moo'"
            style="width: 100%; height: 500px; background: rgba(0,0,0,0.2); overflow-y: auto; display: flex; flex-direction: column;"
          >
            <div v-if="mooSystemPromptLoading" style="padding: 20px; color: #9ca3af; font-family: monospace;">
              Chargement du system prompt de Moo…
            </div>
            <div v-else-if="mooSystemPromptError" style="padding: 20px; color: #ef9a9a; font-family: monospace;">
              ⚠️ Impossible de récupérer le system prompt de Moo : {{ mooSystemPromptError }}
            </div>
            <template v-else>
              <div style="padding: 12px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); color: #9ca3af; font-family: monospace; font-size: 12px; display: flex; gap: 16px; flex-wrap: wrap;">
                <span>provider: <strong style="color: #daffd1;">{{ mooSystemPromptMeta.provider || '—' }}</strong></span>
                <span>model: <strong style="color: #daffd1;">{{ mooSystemPromptMeta.model || '—' }}</strong></span>
                <span>~{{ mooSystemPromptMeta.tokens || 0 }} tokens base</span>
                <span>{{ (mooSystemPrompt || '').length }} caractères base</span>
                <span v-if="mooGraphMeta">graph: <strong style="color: #4ecdc4;">{{ mooGraphMeta.nodes }} blocs · {{ mooGraphMeta.edges }} arêtes · {{ mooGraphMeta.orphans }} hors-flow</strong></span>
              </div>

              <div style="padding: 10px 20px 4px 20px; color: #4ecdc4; font-family: monospace; font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; opacity: 0.8;">
                ── 1. champ `system` envoyé à {{ mooSystemPromptMeta.provider || 'LLM' }} (base SKILLS) ──
              </div>
              <pre class="moo-prompt-view" v-html="mooSystemPromptHighlighted"></pre>

              <div style="padding: 10px 20px 4px 20px; border-top: 1px dashed rgba(255,255,255,0.1); color: #ff6b6b; font-family: monospace; font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; opacity: 0.9;">
                ── 2. préfixe du message utilisateur (contexte dynamique : variables, blocs, graph, etc.) ──
              </div>
              <pre v-if="mooContextString" class="moo-prompt-view moo-prompt-view--context" v-html="mooContextStringHighlighted"></pre>
              <div v-else style="padding: 4px 20px 8px 20px; color: #9ca3af; font-style: italic; font-family: monospace; font-size: 12px;">
                Aucun contexte généré.
              </div>
              <div style="padding: 4px 20px 20px 20px; color: #6b7280; font-family: monospace; font-size: 11px; font-style: italic;">
                ↳ après ce préfixe, l'historique de conversation puis le prompt utilisateur sont concaténés et envoyés comme un seul message user.
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="modal-footer" style="padding: 20px 24px;">
        <button class="cancel-btn" @click="closeDumpModal" style="padding: 10px 20px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: transparent; color: white;">Fermer</button>
        <button class="create-btn" @click="copyDump" style="padding: 10px 20px; border-radius: 8px; border: none; background: #daffd1; color: #0a1a16; font-weight: 600; display: flex; align-items: center; gap: 8px;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          Copier
        </button>
      </div>
    </div>
  </div>
  
  <!-- Composant de notification -->
  <div class="notification-wrapper" v-if="notification.show">
    <div class="notification" :class="notification.type">
      <svg v-if="notification.type === 'success' || notification.type === 'saved'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20 6L9 17l-5-5"></path>
      </svg>
      <svg v-else-if="notification.type === 'error'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <svg v-else-if="notification.type === 'saving'" class="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
      </svg>
      <span>{{ notification.message }}</span>
    </div>
  </div>

  <!-- Mobile Warning Overlay -->
  <!-- Mobile : au lieu de bloquer, on donne une expérience adaptée = Moo (construire)
       + un onglet Tester (essayer l'agent en direct via le widget). -->
  <div v-if="isMobile && mobileTab === 'test'" class="mobile-test-backdrop">
    <div class="mtb-inner">
      <div class="mtb-logo">M</div>
      <h3>Teste ton agent en direct</h3>
      <p>Appuie sur la bulle de discussion pour parler à ton agent, exactement comme le feront tes clients.</p>
    </div>
  </div>

  <div v-if="isMobile" class="mobile-tabbar">
    <button class="mtab" :class="{ active: mobileTab === 'build' }" @click="mobileShowMoo">
      <span class="mtab-ico">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      </span>
      <span>Moo</span>
    </button>
    <button class="mtab" :class="{ active: mobileTab === 'blocks' }" @click="mobileShowBlocks">
      <span class="mtab-ico">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="6" rx="1"></rect><rect x="2" y="16" width="6" height="6" rx="1"></rect><rect x="16" y="16" width="6" height="6" rx="1"></rect><path d="M12 8v3M5 16v-2a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2"></path></svg>
      </span>
      <span>Blocs</span>
    </button>
    <button class="mtab" :class="{ active: mobileTab === 'test' }" @click="mobileShowTest">
      <span class="mtab-ico">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 2v6l-4.5 8.5A2 2 0 0 0 6.3 20h11.4a2 2 0 0 0 1.8-3.5L15 8V2"></path><line x1="8" y1="2" x2="16" y2="2"></line></svg>
      </span>
      <span>Tester</span>
    </button>
  </div>

  <!-- Delete Confirmation Modal -->
  <div class="modal-overlay dark-mode" v-if="showDeleteModal" @click.self="closeDeleteModal">
    <div class="modal-content premium-modal" style="max-width: 500px;">
      <div class="modal-header">
        <h3>Supprimer le bloc ?</h3>
        <button class="close-btn" @click="closeDeleteModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
      <div class="modal-body" style="padding: 24px;">
        <p style="color: #9ca3af; line-height: 1.5; text-align: center;">Êtes-vous sûr de vouloir supprimer le bloc<br><strong style="color: white; font-size: 1.1em; display: inline-block; margin-top: 8px;">{{ blockToDelete?.displayName || blockToDelete?.blockname || blockToDelete?.idblock }}</strong> ?</p>
        <p class="warning-text" style="color: #ef4444; margin-top: 20px; font-size: 0.9em; display: flex; align-items: flex-start; gap: 8px; background: rgba(239, 68, 68, 0.1); padding: 12px; border-radius: 8px;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0; margin-top: 2px;">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>Cette action est irréversible et supprimera toutes les références à ce bloc dans les autres flux.</span>
        </p>
      </div>
      <div class="modal-footer" style="padding: 20px 24px;">
        <button class="cancel-btn" @click="closeDeleteModal" style="padding: 10px 20px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: transparent; color: white;">Annuler</button>
        <button 
          class="delete-confirm-btn" 
          @click="confirmDeleteBlock" 
          :disabled="isDeleting"
          style="padding: 10px 20px; border-radius: 8px; border: none; background: #ef4444; color: white; font-weight: 600; display: flex; align-items: center; gap: 8px;"
        >
          <span v-if="isDeleting" class="spinning">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
          </span>
          <span v-if="!isDeleting">Supprimer définitivement</span>
          <span v-else>Suppression...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Global Tooltip -->
  <div ref="globalTooltip" class="tooltip-text"></div>

  <!-- AI Copilot Sidebar -->
  <AiCopilotSidebar
    :is-open="showAiCopilot"
    :chatbot-id="$route.params.idchatbot"
    :idbot="$route.params.idchatbot"
    :current-blocks="blocks"
    :current-variables="variables"
    :current-block-flow="selectedBlock"
    :selected-element="selectedFlowElement"
    :constitution="constitution"
    @close="showAiCopilot = false"
    @open-constitution="openConstitutionModal"
    @execute-commands="handleAiCommands"
    @restore-snapshot="handleRestoreSnapshot"
    @open-block="handleOpenBlock"
    @deselect-element="selectedFlowElement = null"
    @test-flow="toggleWidgetChat"
    @create-recap-block="createRecapVariablesBlock"
  />
  <!-- System Prompt Preview/Edit Modal -->
  <transition name="fade">
    <!-- CONSTITUTION MODAL -->
    <div v-if="showConstitutionModal" class="modal-overlay dark-mode" style="background: rgba(10, 26, 22, 0.85); backdrop-filter: blur(8px); z-index: 2147483647;" @click.self="closeConstitutionModal">
      <div class="premium-modal constitution-modal" style="max-width: 820px; width: 95vw; max-height: 95vh; display: flex; flex-direction: column; background: #0A1A16; border: 1px solid rgba(218, 255, 209, 0.15); box-shadow: 0 25px 50px -12px rgba(10, 26, 22, 0.5);">
        <div class="modal-header" style="border-bottom: 1px solid rgba(218, 255, 209, 0.1);">
          <div class="modal-icon-wrapper" style="background: rgba(218, 255, 209, 0.1); color: #DAFFD1; border-radius: 8px; padding: 8px;">
             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
          </div>
          <div style="flex: 1;">
            <h3 style="color: #DAFFD1;">Constitution de l'agent</h3>
            <p style="color: #9ca3af; font-size: 12px; margin: 2px 0 0;">Le comportement, le ton et les limites de votre agent — en langage simple.</p>
          </div>
          <button class="close-btn" @click="closeConstitutionModal" style="color: #DAFFD1; opacity: 0.6;" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.6'">×</button>
        </div>

        <div class="modal-body custom-scroll" style="overflow-y: auto; padding: 20px;">
          <div class="const-grid">
            <div class="const-field">
              <label><span>🎯</span> Objectif — il sert à quoi ?</label>
              <p class="const-hint">L'objectif principal d'une conversation réussie.</p>
              <textarea v-model="constitution.objectif" rows="3" class="const-textarea custom-scroll" placeholder="Ex : Accompagner les parents dans l'inscription de leur enfant."></textarea>
            </div>
            <div class="const-field">
              <label><span>👥</span> Public — il parle à qui ?</label>
              <p class="const-hint">Public cible, langue, contexte culturel/géographique.</p>
              <textarea v-model="constitution.audience" rows="3" class="const-textarea custom-scroll" placeholder="Ex : Parents et élèves au Sénégal, en français."></textarea>
            </div>
            <div class="const-field">
              <label><span>🗣️</span> Ton — comment doit-il répondre ?</label>
              <p class="const-hint">Chaleureux/pro/fun, tu/vous, court/détaillé, émojis ou pas.</p>
              <textarea v-model="constitution.ton" rows="3" class="const-textarea custom-scroll" placeholder="Ex : Chaleureux, tutoiement, messages courts, quelques émojis."></textarea>
            </div>
            <div class="const-field">
              <label><span>🚫</span> Interdits — de quoi ne jamais parler ?</label>
              <p class="const-hint">Hors périmètre, et ce qu'il ne doit jamais inventer (prix, délais…).</p>
              <textarea v-model="constitution.interdits" rows="3" class="const-textarea custom-scroll" placeholder="Ex : Ne jamais inventer de tarif. Sujets sensibles : comprendre et orienter."></textarea>
            </div>
            <div class="const-field const-field-full">
              <label><span>💡</span> Valeurs & comportements</label>
              <p class="const-hint">Les principes qui guident l'agent (ex : « comprend et oriente, ne moralise pas »).</p>
              <textarea v-model="constitution.valeurs" rows="3" class="const-textarea custom-scroll" placeholder="Une valeur / un comportement par ligne."></textarea>
            </div>
            <div class="const-field const-field-full">
              <label><span>📝</span> Notes libres (optionnel)</label>
              <p class="const-hint">Tout ce qui n'entre pas dans les champs ci-dessus.</p>
              <textarea v-model="constitution.notes" rows="3" class="const-textarea custom-scroll" placeholder="Notes additionnelles…"></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer" style="border-top: 1px solid rgba(218, 255, 209, 0.1); padding: 16px 20px; display: flex; justify-content: flex-end; gap: 10px;">
          <button class="btn-cancel" @click="closeConstitutionModal" style="background: transparent; color: #DAFFD1; border: 1px solid rgba(218, 255, 209, 0.2); padding: 8px 16px; border-radius: 6px; font-weight: 500; cursor: pointer;">Annuler</button>
          <button @click="saveConstitution" :disabled="isSavingConstitution" style="display: flex; align-items: center; gap: 6px; background: #DAFFD1; color: #143028; padding: 8px 16px; border-radius: 6px; font-weight: 600; border: none; cursor: pointer;">
            {{ isSavingConstitution ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPromptPreviewModal" class="modal-overlay dark-mode" style="background: rgba(10, 26, 22, 0.85); backdrop-filter: blur(8px);" @click.self="closeSystemPromptModal">
      <div class="premium-modal system-prompt-modal" style="max-width: 900px; width: 95vw; max-height: 95vh; display: flex; flex-direction: column; background: #0A1A16; border: 1px solid rgba(218, 255, 209, 0.15); box-shadow: 0 25px 50px -12px rgba(10, 26, 22, 0.5);">
        <div class="modal-header" style="border-bottom: 1px solid rgba(218, 255, 209, 0.1);">
          <div class="modal-icon-wrapper" style="background: rgba(218, 255, 209, 0.1); color: #DAFFD1; border-radius: 8px; padding: 8px;">
             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
          </div>
          <div style="flex: 1;">
            <h3 style="color: #DAFFD1;">Éditeur de Prompt Système</h3>
            <p style="color: rgba(218, 255, 209, 0.6);">Visualisez et modifiez directement les instructions complètes envoyées à l'IA.</p>
          </div>
          <button class="close-btn" @click="closeSystemPromptModal" style="color: #DAFFD1; opacity: 0.6; transition: opacity 0.2s;" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.6'">×</button>
        </div>
        
        <div class="modal-body custom-scroll" style="flex: 1; overflow-y: auto; padding: 20px;">
           <div class="code-window" style="height: 100%; border: 1px solid rgba(218, 255, 209, 0.1); border-radius: 12px; background: #143028; display: flex; flex-direction: column;">
              <div class="window-bar" style="padding: 10px 16px; border-bottom: 1px solid rgba(218, 255, 209, 0.05); display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.2);">
                 <div class="traffic-lights" style="display: flex; gap: 6px;">
                    <div class="light" style="width: 10px; height: 10px; border-radius: 50%; background: #ef4444;"></div>
                    <div class="light" style="width: 10px; height: 10px; border-radius: 50%; background: #eab308;"></div>
                    <div class="light" style="width: 10px; height: 10px; border-radius: 50%; background: #22c55e;"></div>
                 </div>
                 <div style="font-family: monospace; font-size: 0.8rem; color: rgba(218, 255, 209, 0.5);">system_prompt.md (Éditable)</div>
              </div>
              <textarea 
                 v-model="editableSystemPrompt"
                 @input="updatePromptTokens"
                 class="code-editor custom-scroll"
                 style="width: 100%; flex: 1; min-height: 50vh; padding: 16px; background: transparent; color: #DAFFD1; font-family: 'SF Mono', 'JetBrains Mono', monospace; font-size: 0.85rem; border: none; resize: vertical; outline: none; line-height: 1.5;"
              ></textarea>
           </div>
        </div>
        
        <div class="modal-footer" style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(218, 255, 209, 0.1); padding-top: 16px;">
           <div class="token-info" style="display: flex; align-items: center; gap: 8px; color: rgba(218, 255, 209, 0.6); font-size: 0.85rem;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path></svg>
              <span>~{{ systemPromptTokens }} tokens (estimation)</span>
           </div>
           <div style="display: flex; gap: 12px;">
              <button class="btn-cancel" @click="closeSystemPromptModal" style="background: transparent; color: #DAFFD1; border: 1px solid rgba(218, 255, 209, 0.2); padding: 8px 16px; border-radius: 6px; font-weight: 500; cursor: pointer; transition: all 0.2s;" onmouseover="this.style.background='rgba(218, 255, 209, 0.1)'" onmouseout="this.style.background='transparent'">Annuler</button>
              <button @click="saveSystemPrompt" :disabled="isSavingPrompt" style="display: flex; align-items: center; gap: 6px; background: #DAFFD1; color: #143028; padding: 8px 16px; border-radius: 6px; font-weight: 600; border: none; cursor: pointer; box-shadow: 0 4px 12px rgba(218, 255, 209, 0.15); transition: all 0.2s;" onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 16px rgba(218, 255, 209, 0.25)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(218, 255, 209, 0.15)'">
                 <svg v-if="!isSavingPrompt" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                 <svg v-else class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>
                 {{ isSavingPrompt ? 'Enregistrement...' : 'Enregistrer' }}
              </button>
           </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import PlaygroundContextMenu from '@/components/PlaygroundContextMenu.vue';
import { isSuperAdmin } from '@/utils/permissions';
import { axiosPeelo, axiosMoo } from '@/utils/axios';
import { apiBaseUrl } from '@/config';
import { elementTemplate, generateBlockId, slugify, buildElement, chainElement, relinkBlocks, ensureButtonPayloads, enforceButtonLimit, ensureListCaptureVariable, ensureWelcomeRedirect, RECAP_BLOCK_NAME, buildVariableRecapMessage } from '@/services/mooFlowBuilder';
import { consoleLog, consoleError, consoleWarn, consoleInfo } from '@/utils/logger'; // Keep existing import
import { ELEMENT_COMPONENTS } from '@/components/flowelements';
import { playgroundLogger } from '@/utils/logger';
import axios from 'axios';
import { ClaudePromptGenerator } from '@/utils/claudePromptGenerator';
import FlowGraphCanvas from '@/components/FlowGraphCanvas.vue';
import PlaygroundChatBubble from '@/components/PlaygroundChatBubble.vue';
import AiCopilotSidebar from '@/components/AiCopilotSidebar.vue';
import ActionsManager from '@/components/actions/ActionsManager.vue';
import ConsumptionGauges from '@/components/ConsumptionGauges.vue';

export default {
  name: 'PlaygroundView',
  
  components: {
    ...ELEMENT_COMPONENTS,
    PlaygroundContextMenu,
    FlowGraphCanvas,
    PlaygroundChatBubble,
    AiCopilotSidebar,
    ActionsManager,
    ConsumptionGauges
  },
  
  props: {
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      currentMode: 'canvas', // 'canvas' ou 'actions'
      chatbot: {},
      blocks: [],
      primaryBlocks: [],
      secondaryBlocks: [],
      filteredPrimaryBlocks: [],
      filteredSecondaryBlocks: [],
      searchQuery: '',
      loading: true,
      error: null,
      showPromptPreviewModal: false,
      editableSystemPrompt: '',
      isSavingPrompt: false,
      systemPromptTokens: 0,
      // Constitution (couche métier non-technique, partagée avec Moo)
      constitution: {
        objectif: '', audience: '', ton: '', interdits: '', valeurs: '', notes: ''
      },
      showConstitutionModal: false,
      isSavingConstitution: false,
      isSidebarExpanded: true,
      foldersState: {
        welcome: true,
        standard: true
      },
      // Context menu state
      showContextMenu: false,
      contextMenuPosition: { x: 0, y: 0 },
      elementSearchQuery: '',
      availableElementTypes: [
        { type: 'text', title: 'Texte', description: 'Message texte simple', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>' },
        { type: 'text_button', title: 'Texte avec boutons', description: 'Message avec boutons', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><line x1="9" y1="10" x2="15" y2="10"></line></svg>' },
        { type: 'list', title: 'Liste interactive', description: 'Liste déroulante', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>' },
        { type: 'image', title: 'Image', description: 'Envoyer une image', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>' },
        { type: 'audio', title: 'Audio', description: 'Fichier audio', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>' },
        { type: 'audio_wolof', title: 'Audio généré', description: 'Voix fabriquée depuis un texte (wolof ou français)', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>' },
        { type: 'video', title: 'Vidéo', description: 'Fichier vidéo', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>' },
        { type: 'document', title: 'Document', description: 'Fichier PDF', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>' },
        { type: 'delay', title: 'Délai', description: 'Pause dans le flux', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>' },
        { type: 'action', title: 'Action', description: 'Tâche en arrière-plan', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>' },
        { type: 'request', title: 'Requête HTTP', description: 'Appel API', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>' },
        { type: 'answer', title: 'Réponse utilisateur', description: 'Attendre au moins un input', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>' },
        { type: 'redirect', title: 'Redirection', description: 'Changer de bloc', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>' },
        { type: 'notification', title: 'Notification', description: 'Alerter un numéro', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>' },
        { type: 'ask_payment', title: 'Demander un paiement', description: 'Wave/Orange Money', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>' },
        { type: 'variable_insert', title: 'Insertion de variable', description: 'Stocker une valeur', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10"/><path d="M12 21v-8"/><path d="M5 11c6-6 6-6 12 0"/><circle cx="12" cy="7" r="4"/></svg>' },
        { type: 'location_request_message', title: 'Demande de localisation', description: 'Demander les coordonnés', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' },
        { type: 'button_link', title: 'Carte avec bouton', description: 'Action interactive', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="14" rx="2" ry="2"/><path d="M7 17v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/><circle cx="12" cy="9" r="2"/></svg>' },
        { type: 'carousel', title: 'Carrousel', description: 'Cartes défilantes avec boutons', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="7" y="4" width="10" height="16" rx="2"/><path d="M3 8v8"/><path d="M21 8v8"/></svg>' },
        { type: 'template_message', title: 'Template WhatsApp', description: 'Message validé par Meta', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>' },
        { type: 'agent_handoff', title: 'Stop AI / Agent Handoff', description: 'Transférer à un humain', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>' },
        { type: 'condition', title: 'Condition', description: 'Logique si/alors', iconSVG: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 3v12"></path><circle cx="18" cy="6" r="3"></circle><circle cx="6" cy="18" r="3"></circle><path d="M18 9a9 9 0 0 1-9 9"></path></svg>' }
      ],
      scrollPosition: 0,
      selectedBlock: null,
      showCreateBlockModal: false,
      newBlock: {
        name: '',
        idblock: ''
      },
      
      // Transfer Element Modal State
      showTransferModal: false,
      transferSourceIndex: null,
      transferSourceBlockId: null,
      transferTargetBlock: null,
      isTransferring: false,
      
      creatingBlock: false,
      notification: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      },
      showDumpModal: false,
      dumpContent: '',
      variables: [], 
      showVariablesList: false,
      showCreateVariableModal: false,
      newVariableName: '',
      isCreatingVariable: false,
      showDeleteVariableModal: false,
      variableToDelete: null,
      isDeletingVariable: false,
      showAiCopilot: true,
      selectedFlowElement: null,
      showWidgetChat: false,
      ELEMENT_COMPONENTS, // Exposition de ELEMENT_COMPONENTS pour le template
      // Templates d'éléments : extraits dans @/services/mooFlowBuilder (module partagé
      // avec les harnais de test Node). Référence directe — toujours cloné avant mutation.
      element_template: elementTemplate,
      elementInfo: {
        show: false,
        title: '',
        description: ''
      },
      isEditingBlockName: false,
      editedBlockName: '',
      isEditingDescription: false,
      editedDescription: '',
      saveTimeout: null,
      inboundLinks: [],
      outboundLinks: [],
      loadingInboundLinks: false,
      isLinksExpanded: false,
      totalLinksCount: 0,
      showFilter: false,
      filterQuery: '',
      filteredInboundLinks: [],
      filteredOutboundLinks: [],
      isOnline: navigator.onLine,
      activeDumpTab: 'json', // 'json' | 'prompt' | 'moo'
      mooSystemPrompt: '',
      mooContextString: '',
      mooGraphMeta: null,
      mooSystemPromptMeta: { provider: '', model: '', tokens: 0 },
      mooSystemPromptLoading: false,
      mooSystemPromptError: null,
      mooSystemPromptLoaded: false,
      currentSystemPrompt: '',
      isTraining: false,
      hasUnsavedTrainingChanges: false, // To track if block was modified since load
      isTraining: false,
      hasUnsavedTrainingChanges: false, // To track if block was modified since load
      isMobile: false,
      mobileTab: 'build', // 'build' (Moo) | 'test' (widget) — navigation mobile du playground

      // Delete Block Logic
      showDeleteModal: false,
      blockToDelete: null,
      isDeleting: false
    };
  },
  
  computed: {
    isSuperAdmin() { return isSuperAdmin() },
    mooSystemPromptHighlighted() {
      return this._colorizeMooPrompt(this.mooSystemPrompt || '');
    },
    mooContextStringHighlighted() {
      return this._colorizeMooPrompt(this.mooContextString || '');
    },
    formattedVariableName() {
      return slugify(this.newVariableName);
    },
    isLocal() {
      return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    },

    filteredElementTypes() {
      const query = this.elementSearchQuery.toLowerCase().trim();
      if (!query) return this.availableElementTypes;
      
      return this.availableElementTypes.filter(item => 
        item.title.toLowerCase().includes(query) || 
        item.description.toLowerCase().includes(query) ||
        item.type.toLowerCase().includes(query)
      );
    },
     isBlockTrained() {
        // If we have unsaved training changes, it's NOT trained (needs update)
        if (this.hasUnsavedTrainingChanges) return false;
        
        if (!this.selectedBlock || !this.currentSystemPrompt || typeof this.currentSystemPrompt !== 'string') return false;
        
        // Case-insensitive check for the block ID in the prompt
        const promptUpper = this.currentSystemPrompt.toUpperCase();
        const blockIdUpper = String(this.selectedBlock.idblock).toUpperCase();
        
        return promptUpper.includes(blockIdUpper);
     },
     isEnglish() {
        return navigator.language && navigator.language.startsWith('en');
      },
     
     highlightedSystemPrompt() {
        if (!this.currentSystemPrompt) return '';
        if (!this.selectedBlock) return this.currentSystemPrompt;

        let content = this.currentSystemPrompt
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");

        const idToHighlight = this.selectedBlock.idblock;
        const nameToHighlight = this.selectedBlock.blockname;
        
        consoleLog('DEBUG Highlight:', { id: idToHighlight, name: nameToHighlight });

        // Highlight Function - more aggressive
        // Highlight logic
        const highlight = (text) => `<span class="highlight-prompt-red">${text}</span>`;
        
        // 1. Highlight the specific Header line "BLOC "my_id""
        if (idToHighlight) {
            // Match exactly: BLOC "current_id"
            const headerRegex = new RegExp(`BLOC "${idToHighlight}"`, 'gi');
            content = content.replace(headerRegex, (match) => `<span class="highlight-prompt-red-heavy">${match}</span>`);

            // Also match just the ID elsewhere
            const idRegex = new RegExp(idToHighlight, 'gi'); 
            content = content.replace(idRegex, (match) => {
                // Avoid double highlighting if already wrapped
                if (match.includes('span')) return match; 
                return highlight(match);
            });
        }

        // 2. Highlight Name
        if (nameToHighlight && nameToHighlight.length > 1) {
           const escapedName = nameToHighlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
           const regexName = new RegExp(escapedName, 'gi');
           content = content.replace(regexName, (match) => {
                // Basic check to see if we are inside a tag (imperfect but better than nothing)
                // Actually regex replacement happens sequentially.
                return highlight(match); 
           });
        }
        
        return content;
      },
      
      defaultBlock() {
         if (!this.primaryBlocks) return null;
         return this.primaryBlocks.find(block => block.type === 'default_block');
      }
  },
  
  watch: {
    selectedBlock: {
       handler(newBlock) {
          if (newBlock) {
            this.hasUnsavedTrainingChanges = false;
            this.fetchSystemPrompt();
            this.selectedFlowElement = null;
          }
       },
       immediate: true
    },
    // Watcher optimisé pour détecter les changements de route
    '$route': {
      handler(to, from) {
        if (to.params.idblock !== from.params.idblock) {
          if (to.params.idblock) {
            if (to.params.idblock === 'actions') {
              this.selectedBlock = null;
              this.currentMode = 'actions';
            } else {
              this.currentMode = 'canvas';
              this.selectBlockFromRoute();
            }
          } else {
            this.selectedBlock = null;
            this.currentMode = 'canvas';
          }
        }
      },
      immediate: false
    }
  },

  async created() {
    await this.initializeData();
    
    // Vérifier si un idblock est présent dans l'URL et le sélectionner
    if (this.$route.params.idblock) {
      await this.selectBlockFromRoute();
    }
  },

  mounted() {
    // Ajouter les event listeners pour surveiller la connexion
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Check mobile on mount and listen for resize
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    // Sur mobile : on démarre sur Moo plein écran (construire l'agent).
    if (this.isMobile) {
      this.showAiCopilot = true;
      this.mobileTab = 'build';
    }
  },

  beforeUnmount() {
    // Nettoyer les event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('resize', this.checkMobile);
    
    // Widget Cleanup
    this.removeWidgetScript();

    // Click outside handler logic
    document.removeEventListener('click', this.closeContextMenu);

    // Nettoyer le timeout de notification
    if (this.notification.timeout) {
      clearTimeout(this.notification.timeout);
    }
  },

  methods: {
    // Résout le bloc cible des actions Moo en PRIVILÉGIANT this.selectedBlock.
    // selectedBlock porte le flow complet réellement chargé (et envoyé à Moo via
    // currentBlockFlow, avec les vrais id_element). L'entrée correspondante dans
    // this.blocks vient d'un endpoint léger et peut avoir un flow périmé/absent —
    // chercher dedans fait échouer delete_element ("élément introuvable").
    resolveMooTargetBlock(blockname) {
      const sb = this.selectedBlock;
      if (sb && (sb.blockname === blockname || sb.displayName === blockname || sb.idblock === blockname)) {
        return sb;
      }
      return this.blocks.find(b =>
        b.blockname === blockname ||
        b.displayName === blockname ||
        b.idblock === blockname
      ) || null;
    },

    // Recharge le flow RÉEL d'un bloc depuis le serveur (la liste des blocs est chargée
    // "légère" : pour un bloc non ouvert, sa copie en mémoire peut avoir un flow périmé/vide
    // → delete/move ne trouveraient pas l'élément, insert écraserait le contenu). À appeler
    // AVANT toute édition d'un bloc non ouvert.
    async ensureBlockFlowLoaded(block) {
      if (!block || !block._id) return block;
      try {
        const userData = localStorage.getItem('user');
        const token = userData ? JSON.parse(userData).data.token : '';
        const response = await axiosPeelo.get(
          `/strong/get/flow/${this.$route.params.idchatbot}/${block._id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const arr = response.data && response.data.block;
        if (response.data && response.data.success && Array.isArray(arr) && arr.length && Array.isArray(arr[0].flow)) {
          block.flow = arr[0].flow; // flow frais (source de vérité)
        }
      } catch (e) {
        consoleWarn('[MooAI] ensureBlockFlowLoaded échec pour', block && block.blockname, ':', e.message);
      }
      return block;
    },

    // Résout le bloc cible (par blockname), RECHARGE son flow frais, l'OUVRE (force-open :
    // l'utilisateur voit le bloc s'ouvrir), puis localise l'élément par id_element.
    // Fallback : recherche l'id_element parmi les blocs déjà chargés. Renvoie { block, idx }.
    async loadTargetForElement(blockname, id_element) {
      let block = this.resolveMooTargetBlock(blockname);
      if (block) {
        await this.ensureBlockFlowLoaded(block);
        this.selectedBlock = block; // force-open : rend le bloc visible pendant l'édition
        if (!id_element) return { block, idx: -1 };
        const idx = (block.flow || []).findIndex(el => el.id_element === id_element);
        if (idx !== -1) return { block, idx };
      }
      // Fallback : l'id_element est peut-être dans un autre bloc déjà chargé
      if (id_element) {
        for (const b of [this.selectedBlock, ...this.blocks].filter(Boolean)) {
          if ((b.flow || []).some(el => el.id_element === id_element)) {
            await this.ensureBlockFlowLoaded(b);
            this.selectedBlock = b;
            const idx = (b.flow || []).findIndex(el => el.id_element === id_element);
            return { block: b, idx };
          }
        }
      }
      return { block: block || null, idx: -1 };
    },
    toggleActionsMode() {
      if (this.currentMode === 'actions') {
        this.currentMode = 'canvas';
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
      } else {
        this.currentMode = 'actions';
        this.selectedBlock = null; // Quitte l'éditeur de bloc s'il était ouvert
        
        // Fermer Moo
        if (this.showAiCopilot) {
          this.showAiCopilot = false;
        }

        // Naviguer vers l'URL des actions
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground/actions`);
      }
    },
    getCommandLabel(cmd) {
      const name = cmd.function || cmd.name;
      const args = cmd.args || cmd.arguments || cmd.input || {};
      if (name === 'create_variable') return `Création de la variable "${args.variable_name}"`;
      if (name === 'create_block') return `Création du bloc "${args.blockname}"`;
      if (name === 'insert_element') return `Insertion d'un "${args.element_type}" dans "${args.blockname}"`;
      if (name === 'delete_element') return `Suppression d'un élément dans "${args.blockname}"`;
      if (name === 'move_element') return `Déplacement d'un élément dans "${args.blockname}"`;
      if (name === 'delete_block') return `Suppression du bloc "${args.blockname || args.idblock}"`;
      if (name === 'delete_variable') return `Suppression de la variable "${args.slug}"`;
      if (name === 'update_constitution') return `Mise à jour de la constitution du bot`;
      return 'Action en cours...';
    },

    async handleAiCommands(commands, executionCallback, controller) {
      consoleLog('%c[MooAI] handleAiCommands appelé', 'color:#DAFFD1;font-weight:bold', commands?.length, 'commandes');
      if (!Array.isArray(commands)) { consoleWarn('[MooAI] commands n\'est pas un tableau', commands); return; }

      const warnings = [];
      // Résultats réels d'exécution — permet au résumé de Moo de ne jamais
      // annoncer un succès pour une action qui a échoué ou été ignorée
      const results = { done: [], failed: [] };

      // Remplacement EN PLACE : un delete_element libère un index ; l'insert_element suivant
      // dans le MÊME bloc réinsère à cet index (au lieu d'ajouter en fin) → l'ordre est
      // préservé quand Moo modifie un élément au milieu d'un bloc (delete→insert = remplacement).
      const replaceAt = new Map(); // idblock → index libéré

      // Snapshot des blocs existants qui seront modifiés — pour le undo
      const snapshot = { blocksSnapshot: [], createdBlockIds: [], createdVariableSlugs: [] };
      const insertTargetNames = new Set(
        commands
          .filter(c => ['insert_element', 'delete_element', 'move_element'].includes(c.name || c.function))
          .map(c => ((c.args || c.arguments || c.input) || {}).blockname)
          .filter(Boolean)
      );
      for (const bname of insertTargetNames) {
        const block = this.blocks.find(b => b.blockname === bname || b.displayName === bname || b.idblock === bname);
        if (block) {
          snapshot.blocksSnapshot.push({
            idblock: block.idblock,
            flow: JSON.parse(JSON.stringify(block.flow || []))
          });
        }
      }

      for (const cmd of commands) {
        if (controller?.cancelled) break;

        // Afficher l'action en cours et attendre 2s avant d'exécuter
        controller?.onProgress(this.getCommandLabel(cmd));
        await new Promise(resolve => setTimeout(resolve, 2000));

        if (controller?.cancelled) break;

        const cmdName = cmd.name || cmd.function;
        const cmdArgs = cmd.arguments || cmd.args || cmd.input || {};
        const failedBefore = results.failed.length;
        const fail = (reason) => {
          warnings.push(reason);
          results.failed.push({ name: cmdName, reason });
        };

        if (cmdName === 'create_variable') {
          const varName = cmdArgs.variable_name || cmdArgs.slug;
          if (!varName) { consoleWarn('[MooAI] create_variable: varName manquant'); fail('create_variable sans nom de variable — ignoré.'); continue; }

          const formattedName = slugify(varName);

          consoleLog('[MooAI] create_variable — varName:', varName, '| formattedName:', formattedName);

          if (this.variables.some(x => x.slug === formattedName || x.id === formattedName)) {
            consoleWarn('[MooAI] create_variable: variable déjà existante, skip');
            fail(`La variable "${varName}" existe déjà.`);
            continue;
          }

          this.newVariableName = varName;
          this._aiVariableType = cmdArgs.type || 'text';
          consoleLog('[MooAI] create_variable — appel saveNewVariable, newVariableName:', this.newVariableName);
          await this.saveNewVariable();
          snapshot.createdVariableSlugs.push(formattedName);
          consoleLog('[MooAI] create_variable — terminé, variables count:', this.variables.length);
          this.newVariableName = '';
          this._aiVariableType = null;
        }

        else if (cmdName === 'create_block') {
          const blockName = cmdArgs.blockname;
          if (!blockName) { consoleWarn('[MooAI] create_block: blockname manquant'); fail('create_block sans nom de bloc — ignoré.'); continue; }
          if (this.blocks.some(x => x.blockname === blockName || x.displayName === blockName)) {
            consoleWarn('[MooAI] create_block: bloc déjà existant, skip:', blockName);
            fail(`Le flux "${blockName}" existe déjà.`);
            continue;
          }
          const blockDescription = cmdArgs.description || '';
          consoleLog('[MooAI] create_block — création de:', blockName, '| description:', blockDescription);
          this.newBlock = { name: blockName, idblock: '', description: blockDescription };
          this.generateIdBlock();
          const capturedIdblock = this.newBlock.idblock;
          await this.createNewBlock();
          snapshot.createdBlockIds.push(capturedIdblock);
          consoleLog('[MooAI] create_block — terminé, blocs count:', this.blocks.length);
        }

        else if (cmdName === 'insert_element') {
          const targetBlock = this.resolveMooTargetBlock(cmdArgs.blockname);
          if (!targetBlock) {
             fail(`Impossible d'insérer l'élément, le flux cible "${cmdArgs.blockname}" est introuvable.`);
             continue;
          }
          // Force-open + flow frais AVANT d'insérer (anti-écrasement d'un bloc non ouvert).
          await this.ensureBlockFlowLoaded(targetBlock);
          this.selectedBlock = targetBlock;

          // Construction de l'élément déléguée à @/services/mooFlowBuilder (template + résolution
          // des références blockname→idblock et nom de variable→slug). Partagé avec les tests Node.
          const newElement = buildElement({
            elementType: cmdArgs.element_type,
            properties: cmdArgs.properties,
            blocks: this.blocks,
            variables: this.variables
          });
          if (!newElement) {
            consoleWarn('[MooAI] insert_element: type inconnu:', cmdArgs.element_type);
            fail(`Type d'élément inconnu "${cmdArgs.element_type}" — insertion ignorée dans "${cmdArgs.blockname}".`);
            continue;
          }

          this.selectedBlock = targetBlock;
          if (!this.selectedBlock.flow) this.selectedBlock.flow = [];
          // Remplacement en place : si un delete vient de libérer un index dans ce bloc,
          // on réinsère à cet index (ordre préservé) ; sinon on ajoute en fin.
          if (replaceAt.has(targetBlock.idblock)) {
            const at = replaceAt.get(targetBlock.idblock);
            replaceAt.delete(targetBlock.idblock);
            this.selectedBlock.flow.splice(Math.min(at, this.selectedBlock.flow.length), 0, newElement);
          } else {
            this.selectedBlock.flow.push(newElement);
          }
          // refreshChain génère les id_element / id_previous avant de sauvegarder
          this.refreshChain(targetBlock, false);
          await this.saveFlowToServer();
        }

        else if (cmdName === 'delete_element') {
          // Force-open + flow frais par blockname, puis localisation par id_element (unique) ;
          // fallback : recherche l'id_element parmi les blocs déjà chargés.
          const id = cmdArgs.id_element;
          const { block: targetBlock, idx: elemIndex } = await this.loadTargetForElement(cmdArgs.blockname, id);
          if (!targetBlock || elemIndex === -1) {
            fail(`Élément "${id}" introuvable (bloc indiqué : "${cmdArgs.blockname}").`);
            continue;
          }
          targetBlock.flow.splice(elemIndex, 1);
          // Mémorise l'index libéré : si un insert_element suit dans ce bloc, ce sera un
          // remplacement EN PLACE (préserve l'ordre quand on modifie un élément du milieu).
          replaceAt.set(targetBlock.idblock, elemIndex);
          this.selectedBlock = targetBlock;
          this.refreshChain(targetBlock, false);
          await this.saveFlowToServer();
          consoleLog(`[MooAI] delete_element — supprimé "${id}" dans "${targetBlock.blockname}" (idblock: ${targetBlock.idblock})`);
        }

        else if (cmdName === 'move_element') {
          // Réordonne un élément DANS son bloc (ex: un 'answer' placé avant sa question 'text').
          // Force-open + flow frais par blockname, puis localisation par id_element.
          const id = cmdArgs.id_element;
          const { block: targetBlock, idx: fromIndex } = await this.loadTargetForElement(cmdArgs.blockname, id);
          if (!targetBlock || fromIndex === -1) { fail(`Élément "${id}" introuvable pour move_element (bloc "${cmdArgs.blockname}").`); continue; }
          const flow = targetBlock.flow;
          let toIndex;
          if (cmdArgs.after_id_element) {
            if (cmdArgs.after_id_element === 'START') {
              toIndex = 0;
            } else {
              const yIndex = flow.findIndex(el => el.id_element === cmdArgs.after_id_element);
              if (yIndex === -1) { fail(`move_element : after_id_element "${cmdArgs.after_id_element}" introuvable dans "${targetBlock.blockname}".`); continue; }
              toIndex = (fromIndex < yIndex) ? yIndex : yIndex + 1; // position finale = juste après Y
            }
          } else if (cmdArgs.direction === 'up') {
            toIndex = Math.max(0, fromIndex - 1);
          } else if (cmdArgs.direction === 'down') {
            toIndex = Math.min(flow.length - 1, fromIndex + 1);
          } else {
            fail('move_element : précise after_id_element ou direction ("up"/"down").'); continue;
          }
          if (toIndex === fromIndex) { results.done.push({ name: cmdName }); continue; }
          const [moved] = flow.splice(fromIndex, 1);
          flow.splice(toIndex, 0, moved);
          this.selectedBlock = targetBlock;
          this.refreshChain(targetBlock, false);
          await this.saveFlowToServer();
          consoleLog(`[MooAI] move_element — "${id}" déplacé dans "${targetBlock.blockname}" → index ${toIndex}`);
        }

        else if (cmdName === 'delete_block') {
          const ref = cmdArgs.idblock || cmdArgs.blockname;
          const targetBlock = this.blocks.find(b =>
            b.idblock === cmdArgs.idblock || b.idblock === ref ||
            b.blockname === cmdArgs.blockname || b.displayName === cmdArgs.blockname ||
            b.blockname === ref || b.displayName === ref
          );
          if (!targetBlock) {
            fail(`Bloc "${cmdArgs.blockname || ref}" introuvable pour delete_block.`);
            continue;
          }
          // Garde-fou : ne jamais supprimer un bloc système
          const SYSTEM_TYPES = ['starting_point', 'default_block', 'commerce_point', 'appel_point'];
          if (SYSTEM_TYPES.includes(targetBlock.type)) {
            fail(`Le bloc système "${targetBlock.blockname}" ne peut pas être supprimé.`);
            continue;
          }
          try {
            const userData = localStorage.getItem('user');
            const token = userData ? JSON.parse(userData).data.token : '';
            await axiosPeelo.delete(`/strong/delete/block/${targetBlock.idblock}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            this.blocks = this.blocks.filter(b => b.idblock !== targetBlock.idblock);
            if (this.secondaryBlocks) {
              this.secondaryBlocks = this.secondaryBlocks.filter(b => b.idblock !== targetBlock.idblock);
            }
            if (typeof this.filterBlocks === 'function') this.filterBlocks();
            if (this.selectedBlock && this.selectedBlock.idblock === targetBlock.idblock) {
              this.selectedBlock = null;
            }
            // Le nom réel sert au résumé affiché par Moo (les args ne portent parfois que l'idblock)
            cmdArgs.blockname = cmdArgs.blockname || targetBlock.blockname;
            consoleLog(`[MooAI] delete_block — supprimé "${targetBlock.blockname}" (${targetBlock.idblock})`);
          } catch (e) {
            consoleError('[MooAI] delete_block erreur:', e);
            fail(`Erreur lors de la suppression du bloc "${targetBlock.blockname}" : ${e.response?.data?.message || e.message}`);
          }
        }

        else if (cmdName === 'delete_variable') {
          const slug = cmdArgs.slug || cmdArgs.variable_name;
          const targetVar = this.variables.find(v => v.slug === slug || v.id === slug);
          if (!targetVar) {
            fail(`Variable "${slug}" introuvable pour delete_variable.`);
            continue;
          }
          // Garde-fou : ne jamais supprimer une variable système (globale)
          if (targetVar.type === 'global') {
            fail(`La variable système "${slug}" ne peut pas être supprimée.`);
            continue;
          }
          try {
            const customVariables = this.variables.filter(v =>
              v.type !== 'global' && v.slug !== targetVar.slug && v.id !== targetVar.id
            );
            await axiosPeelo.post(`/update/variable/declaration/${this.$route.params.idchatbot}`, {
              variables: { idbot: this.$route.params.idchatbot, variables: customVariables }
            });
            this.variables = this.variables.filter(v => v.slug !== targetVar.slug && v.id !== targetVar.id);
            consoleLog(`[MooAI] delete_variable — supprimé "${slug}"`);
          } catch (e) {
            consoleError('[MooAI] delete_variable erreur:', e);
            fail(`Erreur lors de la suppression de la variable "${slug}" : ${e.response?.data?.message || e.message}`);
          }
        }

        else if (cmdName === 'update_constitution') {
          const champs = ['objectif', 'audience', 'ton', 'interdits', 'valeurs', 'notes'];
          const patch = {};
          champs.forEach((c) => {
            if (cmdArgs[c] !== undefined && cmdArgs[c] !== null) patch[c] = String(cmdArgs[c]);
          });
          if (Object.keys(patch).length === 0) {
            fail('update_constitution sans champ — ignoré.');
            continue;
          }
          try {
            const res = await axiosPeelo.post(
              `/knowledge/jarvis/${this.$route.params.idchatbot}/constitution`,
              { constitution: patch }
            );
            // Refléter localement (merge) la constitution mise à jour
            const saved = res.data?.constitution || { ...this.constitution, ...patch };
            this.constitution = {
              objectif: saved.objectif || '',
              audience: saved.audience || '',
              ton: saved.ton || '',
              interdits: saved.interdits || '',
              valeurs: saved.valeurs || '',
              notes: saved.notes || ''
            };
            consoleLog('[MooAI] update_constitution — champs mis à jour:', Object.keys(patch));
          } catch (e) {
            consoleError('[MooAI] update_constitution erreur:', e);
            fail(`Erreur lors de la mise à jour de la constitution : ${e.response?.data?.message || e.message}`);
          }
        }

        else {
          fail(`Action inconnue "${cmdName}" — ignorée.`);
        }

        // Aucune erreur enregistrée pour cette commande → réellement exécutée
        if (results.failed.length === failedBefore) {
          results.done.push({ name: cmdName, args: cmdArgs });
        }
      }

      // Re-liaison finale : tous les blocs créés existent désormais → on re-résout les
      // cibles de navigation (boutons / redirections / lignes de liste / conditions).
      // Corrige les références "en avant" (cible créée APRÈS l'élément qui la pointe) et
      // les slugs devinés par Moo (ex. bouton id="infos" → bloc "Infos"). Logique partagée
      // avec les harnais de test via @/services/mooFlowBuilder (zéro dérive).
      try {
        // 0) WhatsApp : un text_button > 3 boutons ne s'affiche pas → conversion en list
        const listified = enforceButtonLimit(this.blocks);
        // 1) garantit la capture d'une sélection de list (options.variable sur la liste, sinon valeur perdue)
        const listcap = ensureListCaptureVariable(this.blocks);
        // 2) injecte les payloads manquants sur les boutons de choix (text_button → answer)
        const payloaded = ensureButtonPayloads(this.blocks);
        // 3) re-résout les cibles de navigation maintenant que tous les blocs existent
        const relinked = relinkBlocks(this.blocks);
        // 4) garantit que le Welcome Flow redirige vers l'accueil (sinon le bot ne démarre pas)
        const welcomed = ensureWelcomeRedirect(this.blocks);
        const touched = [...new Set([...listified, ...listcap, ...payloaded, ...relinked, ...welcomed])];
        for (const b of touched) {
          await this.saveSpecificBlockToServer(b);
        }
        if (listified.length) consoleLog('[MooAI] text_button >3 → list:', listified.map(b => b.blockname || b.idblock));
        if (listcap.length) consoleLog('[MooAI] list → options.variable (capture):', listcap.map(b => b.blockname || b.idblock));
        if (payloaded.length) consoleLog('[MooAI] payloads ajoutés:', payloaded.map(b => b.blockname || b.idblock));
        if (relinked.length) consoleLog('[MooAI] relink — blocs re-liés:', relinked.map(b => b.blockname || b.idblock));
        if (welcomed.length) consoleLog('[MooAI] Welcome Flow → redirection accueil ajoutée');
      } catch (e) {
        consoleWarn('[MooAI] post-traitement flow — erreur:', e.message);
      }

      const failCount = results.failed.length;
      this.showNotification(failCount > 0
        ? { message: `Moo IA a terminé : ${results.done.length} action(s) réussie(s), ${failCount} échec(s).`, type: 'error' }
        : { message: 'Moo IA a terminé. Le flow a été sauvegardé.', type: 'success' });
      if (typeof executionCallback === 'function') {
        executionCallback(warnings, snapshot, results);
      }
    },

    handleOpenBlock(blockName) {
      const block = this.blocks.find(b =>
        b.blockname === blockName ||
        b.displayName === blockName ||
        b.idblock === blockName
      );
      if (block) {
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground/${block.idblock}`);
      }
    },

    async handleRestoreSnapshot(snapshot) {
      consoleLog('%c[MooAI UNDO] handleRestoreSnapshot appelé', 'color:#DAFFD1;font-weight:bold', snapshot);
      if (!snapshot) return;
      const userData = localStorage.getItem('user');
      const token = userData ? JSON.parse(userData).data.token : '';
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      consoleLog('[MooAI UNDO] blocksSnapshot:', snapshot.blocksSnapshot.length, 'entrées');
      consoleLog('[MooAI UNDO] createdBlockIds:', snapshot.createdBlockIds);
      consoleLog('[MooAI UNDO] createdVariableSlugs:', snapshot.createdVariableSlugs);

      // Restaurer les flows des blocs modifiés — réinsérer le flow snapshottés tel quel
      for (const entry of snapshot.blocksSnapshot) {
        const block = this.blocks.find(b => b.idblock === entry.idblock);
        consoleLog(`[MooAI UNDO] Restauration bloc "${entry.idblock}" —`, block ? 'trouvé' : 'INTROUVABLE', '— flow à restaurer:', entry.flow.length, 'éléments');
        if (block) {
          block.flow = JSON.parse(JSON.stringify(entry.flow));
          await this.saveSpecificBlockToServer(block);
          consoleLog(`[MooAI UNDO] Sauvegarde bloc "${entry.idblock}" envoyée`);
          if (this.selectedBlock && this.selectedBlock.idblock === block.idblock) {
            this.selectedBlock = block;
          }
        }
      }

      // Supprimer les blocs créés par l'IA
      for (const blockId of snapshot.createdBlockIds) {
        try {
          await axiosPeelo.delete(`/strong/delete/block/${blockId}`, config);
          this.blocks = this.blocks.filter(b => b.idblock !== blockId);
          this.secondaryBlocks = (this.secondaryBlocks || []).filter(b => b.idblock !== blockId);
          this.filterBlocks?.();
          if (this.selectedBlock?.idblock === blockId) this.selectedBlock = null;
        } catch (e) {
          consoleError('[MooAI] Erreur suppression bloc undo:', blockId, e);
        }
      }

      // Supprimer les variables créées par l'IA
      for (const slug of snapshot.createdVariableSlugs) {
        const varObj = this.variables.find(v => v.slug === slug || v.id === slug);
        if (varObj) {
          try {
            const customVariables = this.variables.filter(v => v.type !== 'global' && v.id !== varObj.id);
            await axiosPeelo.post(`/update/variable/declaration/${this.$route.params.idchatbot}`, {
              variables: { idbot: this.$route.params.idchatbot, variables: customVariables }
            }, config);
            this.variables = this.variables.filter(v => v.id !== varObj.id);
          } catch (e) {
            consoleError('[MooAI] Erreur suppression variable undo:', slug, e);
          }
        }
      }

      this.showNotification({ message: 'Actions annulées avec succès', type: 'success' });
    },

    selectElementForMoo(element) {
      if (this.selectedFlowElement && this.selectedFlowElement.id_element === element.id_element) {
        this.selectedFlowElement = null;
        return;
      }
      this.selectedFlowElement = element;
      if (!this.showAiCopilot) this.showAiCopilot = true;
    },
    toggleMoo() {
      this.showAiCopilot = !this.showAiCopilot;
      if (this.showAiCopilot && this.showWidgetChat) {
        this.showWidgetChat = false;
        this.removeWidgetScript();
      }
      if (!this.showAiCopilot) this.selectedFlowElement = null;
    },
    toggleWidgetChat() {
      this.showWidgetChat = !this.showWidgetChat;
      if (this.showWidgetChat) {
        if (this.showAiCopilot) this.showAiCopilot = false;
        this.injectWidgetScript();
      } else {
        this.removeWidgetScript();
      }
    },
    injectWidgetScript() {
       if (!this.chatbot || !this.chatbot._id) {
           setTimeout(() => this.injectWidgetScript(), 1000);
           return;
       }
       const existingScript = document.getElementById('peelo-jssdk');
       if (existingScript) existingScript.remove();
       const existingIframe = document.getElementById('peelo-widget-iframe');
       if (existingIframe) existingIframe.remove();

       let adminUser = { name: "Admin Peelo", email: "admin@peelo.io", phone: "221770000000" };
       const userData = localStorage.getItem('user');
       if (userData) {
           try {
               const parsed = JSON.parse(userData);
               if (parsed.data && parsed.data.user) {
                   const u = parsed.data.user;
                   adminUser.name = u.name || adminUser.name;
                   adminUser.email = u.email || adminUser.email;
                   adminUser.phone = u.phone || adminUser.phone;
               }
           } catch(e) {}
       }
       const script = document.createElement('script');
       script.id = 'peelo-jssdk';
       // Sur sandbox.peelo.io, le widget de test est une copie d'agenttalk branchée sur le
       // moteur sandbox (/agents + /simwa) : on teste les nouveautés du moteur sans toucher la prod.
       const baseUrl = window.location.hostname === 'sandbox.peelo.io'
         ? "https://sandbox.peelo.io/agents/embed.js"
         : "https://agenttalk.peelo.io/embed.js";
       const params = new URLSearchParams();
       params.append('id', this.chatbot._id);
       params.append('email', adminUser.email);
       params.append('name', adminUser.name);
       params.append('phone', adminUser.phone);
       params.append('pgdebug', '1'); // contexte Playground → active la commande /debug du widget
       // 🔄 Cache-bust : un jeton unique à CHAQUE ouverture. embed.js relit son origine et
       // RE-TRANSMET tous ses params (sauf id) à l'URL de l'iframe (embed.js L57-61) → ce `v`
       // force le navigateur à re-télécharger embed.js ET le contenu de l'iframe (index.html
       // + bundles). Conséquence : fermer puis rouvrir la bulle recharge toujours la DERNIÈRE
       // version déployée d'agenttalk (plus de version en cache après un redeploy).
       params.append('v', String(Date.now()));
       script.src = `${baseUrl}?${params.toString()}`;
       document.body.appendChild(script);
    },
    removeWidgetScript() {
       const existingScript = document.getElementById('peelo-jssdk');
       if (existingScript) existingScript.remove();
       const existingIframe = document.getElementById('peelo-widget-iframe');
       if (existingIframe) existingIframe.remove();
    },

    closeCreateVariableModal() {
      this.showCreateVariableModal = false;
      this.newVariableName = '';
    },
    async saveNewVariable() {
      if (!this.newVariableName || this.isCreatingVariable) return;
      
      const newVarName = this.formattedVariableName;
      
      // Check for duplicates
      if (this.variables.some(v => v.slug === newVarName || v.id === newVarName)) {
        this.showNotification({ message: 'Une variable avec ce nom existe déjà.', type: 'error' });
        return;
      }
      
      try {
        this.isCreatingVariable = true;
        
        const newVariable = {
          id: newVarName,
          slug: newVarName,
          type: this._aiVariableType || 'text'
        };
        
        // Ensure we only send the custom variables back to the API, excluding global/system ones
        const customVariables = (this.variables || []).filter(v => v.type !== 'global');
        const updatedCustomVariablesList = [newVariable, ...customVariables];
        
        const response = await axiosPeelo.post(`/update/variable/declaration/${this.$route.params.idchatbot}`, {
          variables: {
            idbot: this.$route.params.idchatbot, 
            variables: updatedCustomVariablesList
          }
        });
        
        // Similar to VariablesView.vue, if no error is thrown by axios, it is considered a success 
        // because the backend often omits 'success: true' and just returns a message or an object.
        const isSuccess = response.data && (response.data.success || response.data.message || typeof response.data === 'object');
        
        if (isSuccess) {
          // Recombine the global variables with the newly updated custom ones for the UI
          const globalVariables = (this.variables || []).filter(v => v.type === 'global');
          this.variables = [...updatedCustomVariablesList, ...globalVariables];
          
          this.closeCreateVariableModal();
          this.showNotification({ message: response.data.message || 'Variable créée avec succès', type: 'success' });
        } else {
          this.showNotification({ message: 'Erreur inattendue lors de la création de la variable', type: 'error' });
        }
      } catch (error) {
        consoleError('Erreur saveNewVariable:', error);
        this.showNotification({ message: 'Erreur réseau lors de la création', type: 'error' });
      } finally {
        this.isCreatingVariable = false;
      }
    },
    promptDeleteVariable(variable) {
      if (variable.type === 'global') return;
      this.variableToDelete = variable;
      this.showDeleteVariableModal = true;
    },
    closeDeleteVariableModal() {
      this.showDeleteVariableModal = false;
      this.variableToDelete = null;
    },
    async confirmDeleteVariable() {
      if (!this.variableToDelete || this.isDeletingVariable) return;
      
      try {
        this.isDeletingVariable = true;
        const customVariables = this.variables.filter(v => v.type !== 'global' && v.id !== this.variableToDelete.id);
        
        const response = await axiosPeelo.post(`/update/variable/declaration/${this.$route.params.idchatbot}`, {
          variables: {
            idbot: this.$route.params.idchatbot, 
            variables: customVariables
          }
        });
        
        const isSuccess = response.data && (response.data.success || response.data.message || typeof response.data === 'object');
        
        if (isSuccess) {
          this.variables = this.variables.filter(v => v.id !== this.variableToDelete.id);
          this.closeDeleteVariableModal();
          this.showNotification({ message: 'Variable supprimée avec succès', type: 'success' });
        } else {
          this.showNotification({ message: 'Erreur lors de la suppression de la variable', type: 'error' });
        }
      } catch (error) {
        consoleError('Erreur confirmDeleteVariable:', error);
        this.showNotification({ message: 'Erreur réseau lors de la suppression', type: 'error' });
      } finally {
        this.isDeletingVariable = false;
      }
    },
    // Context Menu Logic
    handleContextMenu(event) {
      // Only show context menu if a block is selected (playground is visible)
      if (!this.selectedBlock) return;
      
      this.showContextMenu = true;
      this.elementSearchQuery = '';
      
      // Calculate position relative to container
      const container = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - container.left;
      const y = event.clientY - container.top;
      
      // Prevent menu from overflowing off-screen
      const menuWidth = 260; // Estimated max width
      const menuHeight = 300; // Estimated max height
      
      this.contextMenuPosition = {
        x: x + menuWidth > container.width ? container.width - menuWidth - 10 : x,
        y: y + menuHeight > container.height ? container.height - menuHeight - 10 : y
      };
      
      // Focus search input on next tick
      this.$nextTick(() => {
        if (this.$refs.contextMenuSearch) {
          this.$refs.contextMenuSearch.focus();
        }
      });
      
      // Add global click listener to close menu
      document.addEventListener('click', this.closeContextMenu);
    },
    
    closeContextMenu() {
      this.showContextMenu = false;
      this.elementSearchQuery = '';
      document.removeEventListener('click', this.closeContextMenu);
    },
    
    addContextMenuElement(type) {
      this.insertElement(type);
      this.closeContextMenu();
    },
    
    handleCreateBlockFromMenu() {
      this.closeContextMenu();
      this.openCreateBlockModal();
    },

    handleCreateVariableFromMenu() {
      this.closeContextMenu();
      this.showCreateVariableModal = true;
    },

    getChatbotAvatar(chatbot) {
      if (!chatbot) return null;
      
      // Handle legacy default avatar path
      if (chatbot.avatar === '/avatars/bot1.png') {
        // Use a nice default bot avatar (e.g. DiceBear)
        // Using bottts-neutral which is clean and professional
        return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${chatbot._id || chatbot.bot_name}`;
      }
      
      // Standard avatar
      if (chatbot.avatar) {
        // Check if it's already a full URL (in case of legacy data)
        if (chatbot.avatar.startsWith('http')) {
          return chatbot.avatar;
        }
        return `https://api.peelo.io/public/${chatbot.avatar}`;
      }
      
      // Return null to fallback or handle in template
      return null;
    },

    openDeleteModal(block) {
      this.blockToDelete = block;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.blockToDelete = null;
    },

    async confirmDeleteBlock() {
      if (!this.blockToDelete) return;

      this.isDeleting = true;

      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const config = {
          headers: { 'Authorization': `Bearer ${token}` }
        };

        const response = await axiosPeelo.delete(
          `/strong/delete/block/${this.blockToDelete.idblock}`,
          config
        );

        if (response.data && response.data.success) {
          this.showNotification({
            message: 'Bloc supprimé avec succès',
            type: 'success'
          });

          // Vide le system prompt pour que le prochain apprentissage ne mette pas les blocs supprimés
          // Utilisation d'un espace ' ' au lieu de '' pour contourner la validation (!systemPrompt) du backend
          this.editableSystemPrompt = ' ';
          this.currentSystemPrompt = ' ';
          this.mooSystemPrompt = ' ';
          this.updatePromptTokens();

          try {
            const chatbotId = this.$route.params.idchatbot;
            await axios.post(
              `https://jarvis.aipeelo.xyz/knowledge/jarvis/${chatbotId}/scrape-blocks`,
              {
                systemPrompt: ' ',
                idbot: chatbotId
              },
              config
            );
          } catch (error) {
            consoleError('Erreur lors du nettoyage du system prompt:', error);
          }

          // Remove from list locally
          this.blocks = this.blocks.filter(b => b.idblock !== this.blockToDelete.idblock);
          this.secondaryBlocks = this.secondaryBlocks.filter(b => b.idblock !== this.blockToDelete.idblock);
          this.filterBlocks(); // Re-apply search filter

          // If the deleted block was selected, deselect it
          if (this.selectedBlock && this.selectedBlock.idblock === this.blockToDelete.idblock) {
            this.selectedBlock = null;
            this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
          }

          this.closeDeleteModal();
        } else {
          throw new Error(response.data.error || 'Erreur lors de la suppression');
        }

      } catch (error) {
        consoleError('Error deleting block:', error);
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de supprimer le bloc'}`,
          type: 'error'
        });
      } finally {
        this.isDeleting = false;
      }
    },
    // Méthodes pour les tooltips - copié de Sidebar.vue
    // Méthodes pour les tooltips
    showTooltip(event) {
      const tooltip = this.$refs.globalTooltip;
      const text = event.currentTarget.getAttribute('data-tooltip');
      
      if (tooltip && text) {
        tooltip.innerHTML = text;
        const rect = event.currentTarget.getBoundingClientRect();
        
        tooltip.style.position = 'fixed';
        tooltip.style.visibility = 'hidden'; 
        tooltip.style.display = 'block'; 
        
        const isToolsSidebar = event.currentTarget.closest('.tools-sidebar');
        const isActionBar = event.currentTarget.closest('.playground-action-bar');
        
        let left, top;
        
        if (isActionBar) {
          // Position en dessous pour la barre d'action
          left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
          top = rect.bottom + 8;
        } else if (isToolsSidebar) {
          // Position à gauche pour la sidebar de droite
          left = rect.left - tooltip.offsetWidth - 10;
          top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
        } else {
          // Position à droite par défaut
          left = rect.right + 10;
          top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
        }
        
        // Empêcher de sortir de l'écran horizontalement
        const margin = 15;
        if (left < margin) left = margin;
        if (left + tooltip.offsetWidth > window.innerWidth - margin) {
          left = window.innerWidth - tooltip.offsetWidth - margin;
        }
        
        // Empêcher de sortir de l'écran verticalement
        if (top < margin) top = margin;
        if (top + tooltip.offsetHeight > window.innerHeight - margin) {
          top = window.innerHeight - tooltip.offsetHeight - margin;
        }
        
        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.display = ''; 
        tooltip.style.visibility = ''; 
        tooltip.classList.add('visible');
      }
    },

    hideTooltip(event) {
      const tooltip = this.$refs.globalTooltip;
      if (tooltip) {
        tooltip.classList.remove('visible');
      }
    },

    async initializeData() {
      this.loading = true;
      this.error = null;
      
      try {
        await this.getChatbotInfo();
        await this.fetchVariables();
      } catch (error) {
        //consoleError('Error initializing data:', error);
        this.error = 'Une erreur est survenue lors du chargement des données';
      } finally {
        this.loading = false;
      }
    },

    async getChatbotInfo() {
      try {
        // Récupérer le token depuis l'objet user dans localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        // Faire la requête avec le token
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.$route.params.idchatbot}`,
          config
        );
        
        if (response.data && response.data.success) {
          this.chatbot = response.data.chatbot;
          window.sessionStorage.setItem('Chatbot', JSON.stringify(this.chatbot));
          await this.loadBlocks();
          
          // Récupérer le statut de Jarvis via l'endpoint dédié /quick/get/jarvis/status/:idbot
          try {
            const jarvisRes = await axiosPeelo.get(`/quick/get/jarvis/status/${this.$route.params.idchatbot}`);
            if (jarvisRes.data && jarvisRes.data.success && jarvisRes.data.data?.jarvis) {
              const isJarvisActive = jarvisRes.data.data.jarvis.enabled;
              consoleLog('🔌 STATUT JARVIS RÉCUPÉRÉ VIA /quick/get/jarvis/status:', isJarvisActive);
              if (this.defaultBlock) {
                this.defaultBlock.jarvis = isJarvisActive;
              }
            }
          } catch (err) {
            consoleError('Erreur lors de la récupération du statut Jarvis:', err);
          }
        } else {
          throw new Error(response.data.error || 'Erreur lors de la récupération du chatbot');
        }
      } catch (error) {
        //consoleError('Error fetching chatbot info:', error);
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de récupérer les informations du chatbot'}`,
          type: "error"
        });
        throw error;
      }
    },

    handleGraphNodeClick(nodeData) {
      // Le nodeData est l'objet node cliqué
      // On doit trouver le bloc correspondant dans nos listes
      // nodeData.data.blockId ou nodeData.data.idblock
      
      const blockId = nodeData.data.blockId;
      const idBlock = nodeData.data.idblock;
      
      // Essayer de trouver par _id (blockId) ou idblock
      let foundBlock = null;
      
      if (blockId) {
          foundBlock = this.blocks.find(b => b._id === blockId);
      }
      
      if (!foundBlock && idBlock) {
          foundBlock = this.blocks.find(b => b.idblock === idBlock);
      }
      
      if (foundBlock) {
          this.handleBlockClick(foundBlock);
      } else {
          consoleWarn('Bloc non trouvé pour le noeud:', nodeData);
          // Fallback on creating a fake block object if needed or fetch
      }
    },

    checkMobile() {
       this.isMobile = window.innerWidth < 768;
    },

    // --- Navigation mobile : Moo (construire) <-> Tester (widget en direct) ---
    mobileShowMoo() {
      this.mobileTab = 'build';
      this.showAiCopilot = true;
      if (this.showWidgetChat) { this.showWidgetChat = false; this.removeWidgetScript(); }
    },
    mobileShowTest() {
      this.mobileTab = 'test';
      this.showAiCopilot = false;
      if (!this.showWidgetChat) { this.showWidgetChat = true; this.injectWidgetScript(); }
    },
    mobileShowBlocks() {
      // Vue graphe : voir les blocs du flux et en ouvrir un pour le modifier.
      // Si un bloc est déjà ouvert, re-taper l'onglet ramène au graphe.
      if (this.mobileTab === 'blocks' && this.selectedBlock) { this.goBack(); return; }
      this.mobileTab = 'blocks';
      this.showAiCopilot = false;
      if (this.showWidgetChat) { this.showWidgetChat = false; this.removeWidgetScript(); }
    },

    async loadBlocks() {
      try {
        // Récupérer le token depuis l'objet user dans localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.$route.params.idchatbot}/flow`,
          config
        );
        
        if (response.data && response.data.success) {
          const flowData = response.data.flow;
          consoleLog('🤖 DUMP FLOW DATA - mainblocks:', JSON.stringify(flowData.mainblocks, null, 2));
          
          // Traiter les blocs principaux
          this.primaryBlocks = [];
          if (flowData.mainblocks) {
            // Welcome blocks
            if (flowData.mainblocks.welcome) {
              this.primaryBlocks.push(...flowData.mainblocks.welcome.map(block => ({
                ...block,
                category: 'welcome',
                displayName: 'Welcome Flow'
              })));
            }
            
            // Default blocks
            if (flowData.mainblocks.default) {
              this.primaryBlocks.push(...flowData.mainblocks.default.map(block => ({
                ...block,
                category: 'default',
                displayName: 'Default Flow'
              })));
            }
            
            // Commerce blocks
            if (flowData.mainblocks.commerce) {
              this.primaryBlocks.push(...flowData.mainblocks.commerce.map(block => ({
                ...block,
                category: 'commerce',
                displayName: 'Commerce Flow'
              })));
            }

            // Bloc Appel : l'accueil parlé des appels vocaux WhatsApp (ce que la voix dit au décroché)
            if (flowData.mainblocks.appel) {
              this.primaryBlocks.push(...flowData.mainblocks.appel.map(block => ({
                ...block,
                category: 'appel',
                displayName: 'Appel vocal'
              })));
            }
          }
          
          // Traiter les blocs secondaires
          this.secondaryBlocks = flowData.blocks ? flowData.blocks.map(block => ({
            ...block,
            category: 'standard',
            displayName: block.blockname && block.blockname !== null ? block.blockname : block.idblock
          })) : [];
          
          // Combiner tous les blocs
          this.blocks = [...this.primaryBlocks, ...this.secondaryBlocks];
          
          // Initialiser les blocs filtrés avec tous les blocs
          this.filteredPrimaryBlocks = [...this.primaryBlocks];
          this.filteredSecondaryBlocks = [...this.secondaryBlocks];
        } else {
          this.error = response.data.error || 'Erreur lors de la récupération des flows';
        }
      } catch (error) {
        this.error = 'Erreur lors de la récupération des flows';
        //consoleError('Error fetching blocks:', error);
      }
    },

    // Méthode pour filtrer les blocs en fonction de la recherche
    filterBlocks() {
      const query = this.searchQuery.toLowerCase().trim();
      
      if (!query) {
        // Si la recherche est vide, afficher tous les blocs
        this.filteredPrimaryBlocks = [...this.primaryBlocks];
        this.filteredSecondaryBlocks = [...this.secondaryBlocks];
        return;
      }
      
      // Filtrer les blocs principaux
      this.filteredPrimaryBlocks = this.primaryBlocks.filter(block => {
        return (
          block.name?.toLowerCase().includes(query) ||
          block.idblock?.toLowerCase().includes(query) ||
          block.displayName?.toLowerCase().includes(query)
        );
      });
      
      // Filtrer les blocs secondaires
      this.filteredSecondaryBlocks = this.secondaryBlocks.filter(block => {
        return (
          block.name?.toLowerCase().includes(query) ||
          block.idblock?.toLowerCase().includes(query) ||
          block.displayName?.toLowerCase().includes(query)
        );
      });
    },

    toggleFolder(folderName) {
      this.foldersState[folderName] = !this.foldersState[folderName];
    },

    async handleBlockClick(block) {
      // Si c'est le bloc déjà sélectionné, déselectionner en naviguant sans idblock
      if (this.selectedBlock && this.selectedBlock.idblock === block.idblock) {
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
      } else {
        // Naviguer vers l'URL avec l'idblock
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground/${block.idblock}`);
      }
    },

    async selectBlockFromRoute() {
      const idblock = this.$route.params.idblock;
      if (!idblock) {
        this.selectedBlock = null;
        this.currentMode = 'canvas';
        return;
      }
      
      if (idblock === 'actions') {
        this.selectedBlock = null;
        this.currentMode = 'actions';
        return;
      }

      // Chercher le bloc dans les listes existantes
      let targetBlock = this.blocks.find(block => block.idblock === idblock);
      
      if (!targetBlock) {
        // Si le bloc n'est pas trouvé dans les listes actuelles, attendre que les données soient chargées
        let retries = 0;
        while (!targetBlock && retries < 5) {
          await new Promise(resolve => setTimeout(resolve, 100));
          targetBlock = this.blocks.find(block => block.idblock === idblock);
          retries++;
        }
      }

      if (!targetBlock) {
        this.showNotification({
          message: "Bloc non trouvé",
          type: "error"
        });
        // Rediriger vers la page playground sans idblock
        this.$router.replace(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
        return;
      }

      // Mettre à jour le bloc sélectionné immédiatement pour l'UI
      this.selectedBlock = targetBlock;
      
      // Récupérer les détails complets du bloc depuis l'API
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        const response = await axiosPeelo.get(
          `/strong/get/flow/${this.$route.params.idchatbot}/${targetBlock._id}`, 
          config
        );
        
        if (response.data && response.data.success) {
          // Traiter la structure de réponse de l'API
          if (response.data.block && Array.isArray(response.data.block) && response.data.block.length > 0) {
            // Récupérer le premier élément du tableau block
            const blockData = response.data.block[0];
            
            // Conserver les propriétés displayName et category du bloc original
            blockData.displayName = targetBlock.displayName;
            blockData.category = targetBlock.category;
            
            // Mettre à jour le bloc sélectionné avec les données complètes
            this.selectedBlock = blockData;

            
            // Charger les liens après avoir récupéré les détails du bloc
            await this.loadInboundLinks();
            this.analyzeOutboundLinks();
            this.updateTotalLinksCount();
          } else {
            throw new Error('Structure de données inattendue dans la réponse');
          }
        } else {
          throw new Error(response.data.error || 'Erreur lors de la récupération des détails du bloc');
        }
      } catch (error) {
        //consoleError('Erreur lors de la récupération des détails du bloc:', error);
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de récupérer les détails du bloc'}`,
          type: "error"
        });
      }
    },

    goBack() {
      if (this.selectedBlock) {
         // Return to canvas view
         this.selectedBlock = null;
         this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
      } else {
         // Return to home
         this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/home`);
      }
    },
    
    // Méthode pour afficher les notifications
    showNotification({ message, type = 'success', duration = 5000 }) {

      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 3000)
      };
    },
    
    // Méthodes pour la Constitution
    async openConstitutionModal() {
       await this.fetchSystemPrompt(); // recharge aussi la constitution
       this.showConstitutionModal = true;
    },
    closeConstitutionModal() {
       this.showConstitutionModal = false;
    },
    async saveConstitution() {
       if (this.isSavingConstitution) return;
       this.isSavingConstitution = true;
       try {
          const res = await axiosPeelo.post(
             `/knowledge/jarvis/${this.$route.params.idchatbot}/constitution`,
             { constitution: { ...this.constitution } }
          );
          if (res.data?.success) {
             this.showNotification({ message: 'Constitution enregistrée', type: 'success' });
             this.showConstitutionModal = false;
          } else {
             this.showNotification({ message: res.data?.message || 'Échec de l\'enregistrement', type: 'error' });
          }
       } catch (e) {
          consoleError('saveConstitution error', e);
          this.showNotification({ message: 'Erreur lors de l\'enregistrement de la constitution', type: 'error' });
       } finally {
          this.isSavingConstitution = false;
       }
    },

    // Méthodes pour le Prompt
    async openSystemPromptModal() {
       await this.fetchSystemPrompt();
       this.editableSystemPrompt = this.currentSystemPrompt || '';
       this.updatePromptTokens();
       this.showPromptPreviewModal = true;
    },
    closeSystemPromptModal() {
       this.showPromptPreviewModal = false;
    },
    updatePromptTokens() {
       if (this.editableSystemPrompt && typeof this.editableSystemPrompt === 'string') {
          this.systemPromptTokens = ClaudePromptGenerator.estimateTokenCount(this.editableSystemPrompt);
       } else {
          this.systemPromptTokens = 0;
       }
    },
    async saveSystemPrompt() {
      if (!this.$route.params.idchatbot) return;
      this.isSavingPrompt = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        const token = JSON.parse(userData).data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        const chatbotId = this.$route.params.idchatbot;
        
        // Utiliser le même endpoint que JarvisKnowledgeView (jarvis.aipeelo.xyz)
        const response = await axios.post(
          `https://jarvis.aipeelo.xyz/knowledge/jarvis/${chatbotId}/scrape-blocks`,
          {
            systemPrompt: this.editableSystemPrompt,
            idbot: chatbotId
          },
          config
        );
        
        if (response.data && response.data.success) {
          this.currentSystemPrompt = this.editableSystemPrompt;
          this.showNotification({ message: response.data.message || 'Prompt système enregistré avec succès', type: 'success' });
          this.closeSystemPromptModal();
        } else {
          throw new Error(response.data?.message || 'Erreur lors de la sauvegarde');
        }
      } catch (error) {
        consoleError('Error saving system prompt:', error);
        this.showNotification({ message: 'Erreur lors de la sauvegarde du prompt', type: 'error' });
      } finally {
        this.isSavingPrompt = false;
      }
    },
    copySystemPrompt() {
      if (!this.currentSystemPrompt) return;
      navigator.clipboard.writeText(this.currentSystemPrompt)
        .then(() => {
          this.showNotification({ message: 'Prompt copié dans le presse-papiers', type: 'success' });
        })
        .catch(err => {
          this.showNotification({ message: 'Erreur lors de la copie', type: 'error' });
        });
    },

    // Méthodes pour l'entraînement IA
    async fetchSystemPrompt() {
      if (!this.$route.params.idchatbot) return;

      try {
        const userData = localStorage.getItem('user');
        if (!userData) return;
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };

        consoleLog('Playground: Calling fetchSystemPrompt for training status check...');

        const response = await axiosPeelo.get(
          `/knowledge/jarvis/${this.$route.params.idchatbot}/system-prompt`,
          config
        );

        // Dump de la réponse serveur exacte
        consoleLog('🔍 DUMP SERVEUR - /system-prompt:', JSON.stringify(response.data, null, 2));

        // Charger la constitution (couche métier non-technique, utilisée par Moo)
        if (response.data && response.data.constitution && typeof response.data.constitution === 'object') {
          const c = response.data.constitution;
          this.constitution = {
            objectif: c.objectif || '',
            audience: c.audience || '',
            ton: c.ton || '',
            interdits: c.interdits || '',
            valeurs: c.valeurs || '',
            notes: c.notes || ''
          };
        }

        if (response.data && response.data.systemPrompt) {
          let sp = response.data.systemPrompt;
          if (typeof sp === 'object') {
             // Extract text based on backend schema
             sp = sp.promptblock || sp.prompt || '';
          }
          this.currentSystemPrompt = sp;
          this.updatePromptTokens();
          
          // Debug log for user verification
          consoleLog('DEBUG Check Training:', {
             blockId: this.selectedBlock ? this.selectedBlock.idblock : null,
             promptLength: this.currentSystemPrompt.length,
             isTrained: this.isBlockTrained // Computed property
          });
        }
      } catch (error) {
        consoleError('Error fetching system prompt:', error);
      }
    },

    async trainCurrentBlock() {
      if (!this.selectedBlock) return;
      this.isTraining = true;
      try {
        // Format the block using the generator
        const formattedBlock = ClaudePromptGenerator.formatBlockForPrompt(this.selectedBlock);
        
        const userData = localStorage.getItem('user');
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // Add a small artificial delay so the user can actually see the "Griot apprend..." state
        const minDelay = new Promise(resolve => setTimeout(resolve, 2000));
        
        const payload = { 
          textToAppend: formattedBlock,
          blockId: this.selectedBlock.idblock,
          blockName: this.selectedBlock.blockname
        };

        consoleLog('📤 ENTRAÎNEMENT BLOC - PAYLOAD:', JSON.stringify(payload, null, 2));

        const response = await axiosPeelo.post(
          `/knowledge/jarvis/${this.$route.params.idchatbot}/train-block`,
          payload,
          config
        );

        consoleLog('🔍 ENTRAÎNEMENT BLOC - RÉPONSE SERVEUR:', JSON.stringify(response.data, null, 2));
        
        // Wait for both the API call and the minimum delay
        await minDelay;
        
        // Refresh prompt to update status (to "Trained")
        await this.fetchSystemPrompt();
        this.hasUnsavedTrainingChanges = false; // Reset dirty state
        
      } catch (error) {
        consoleError('Training error:', error);
        this.showNotification({ message: 'Erreur lors de l\'entraînement', type: 'error' });
      } finally {
        this.isTraining = false;
      }
    },

    // Navigate to Griot Builder page
    goToGriotBuilder() {
      if (this.$route.params.idchatbot) {
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/griot-builder`);
      }
    },

    // Auto-train with debounce to avoid spamming during rapid saves
    autoTrainBlock() {
      if (this._autoTrainTimer) clearTimeout(this._autoTrainTimer);
      this._autoTrainTimer = setTimeout(() => {
        this.trainCurrentBlock();
      }, 500); // Wait 500ms after last save before training
    },

    // Méthodes pour la création de bloc
    openCreateBlockModal() {
      this.showCreateBlockModal = true;
      this.newBlock = {
        name: '',
        idblock: '',
        description: ''
      };
    },
    
    closeCreateBlockModal() {
      this.showCreateBlockModal = false;
    },
    
    generateIdBlock() {
      // Logique extraite dans @/services/mooFlowBuilder (partagée avec les harnais de test Node).
      this.newBlock.idblock = generateBlockId(this.newBlock.name);
    },

    // Crée (ou met à jour) le bloc « 🔍 Récap variables (test) » : un bloc isolé (hors parcours)
    // qui liste TOUTES les variables du bot et leurs valeurs ({{slug}}). Déclenché par le bouton
    // de la sidebar Moo. Sert à vérifier d'un clic que la capture a bien pris.
    async createRecapVariablesBlock() {
      try {
        const message = buildVariableRecapMessage(this.variables);
        let block = this.blocks.find(b => b.blockname === RECAP_BLOCK_NAME || b.displayName === RECAP_BLOCK_NAME);
        if (!block) {
          // création via le même chemin que la création manuelle (API + rechargement des blocs)
          this.newBlock = { name: RECAP_BLOCK_NAME, idblock: '', description: 'Bloc de test : affiche toutes les variables et leurs valeurs (l\'utilisateur final ne le voit pas).' };
          this.generateIdBlock();
          const idblock = this.newBlock.idblock;
          await this.createNewBlock();
          block = this.blocks.find(b => b.idblock === idblock) || this.blocks.find(b => b.blockname === RECAP_BLOCK_NAME);
        }
        if (!block) {
          this.showNotification({ message: "Impossible de créer le bloc Récap variables.", type: "error" });
          return;
        }
        // (Re)met le contenu : un seul élément texte listant toutes les variables, à jour
        const textEl = buildElement({ elementType: 'text', properties: { message, preview_url: false }, blocks: this.blocks, variables: this.variables });
        block.flow = [textEl];
        this.refreshChain(block, false);
        await this.saveSpecificBlockToServer(block);
        this.selectedBlock = block;
        this.showNotification({ message: "🔍 Bloc « Récap variables » prêt — active-le pour voir les valeurs capturées.", type: "success" });
      } catch (e) {
        consoleWarn('[Recap] création bloc récap variables — erreur:', e.message);
        this.showNotification({ message: "Erreur lors de la création du bloc Récap variables.", type: "error" });
      }
    },

    async createNewBlock() {
      if (!this.newBlock.name) return;
      
      this.creatingBlock = true;
      
      try {
        // Récupérer le token
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        // Préparer les données du bloc
        const blockData = {
          name: this.newBlock.name,
          idblock: this.newBlock.idblock,
          idchatbot: this.$route.params.idchatbot,
          type: 'standard',
          description: this.newBlock.description || ''
        };
        
        // Envoyer la requête
        const response = await axiosPeelo.post(
          `/strong/create/flow/${this.$route.params.idchatbot}`,
          blockData,
          config
        );
        
        // Vérifier si la création a réussi
        if (response.data && response.data.message === "Block créé avec succès") {
          // Afficher une notification de succès
          this.showNotification({
            message: "Le flux a été créé avec succès",
            type: "success"
          });
          
          // Sauvegarder l'ID du nouveau bloc pour le sélectionner après rechargement
          const newBlockId = response.data.block.idblock;
          
          // Fermer le modal et rafraîchir les blocs
          this.closeCreateBlockModal();
          await this.loadBlocks();
          
          // Sélectionner automatiquement le nouveau bloc
          const newlyCreatedBlock = this.blocks.find(block => block.idblock === newBlockId);
          if (newlyCreatedBlock) {
            this.selectedBlock = newlyCreatedBlock;
          }

          // 🔗 Relink "à la création" — maintenant que CE bloc existe, on ré-résout
          // immédiatement toutes les références qui le pointaient par NOM (redirection,
          // bouton, ligne de liste, condition — en entrée comme en sortie) vers son idblock
          // RÉEL, puis on sauvegarde les blocs touchés. Corrige les redirections « en avant »
          // (créées AVANT leur bloc cible, donc restées en nom car non résolvables au moment
          // de l'insertion) qui cassent la navigation au runtime. Fait à chaque création (et
          // pas seulement en fin de batch) → robuste même si le build est interrompu/scindé.
          // (demande user 2026-06-22 : « une fois qu'un bloc est créé, chequer tous les blocs
          //  qui redirigent vers lui pour mettre le vrai id »).
          try {
            const relinkedOnCreate = relinkBlocks(this.blocks);
            for (const b of relinkedOnCreate) {
              await this.saveSpecificBlockToServer(b);
            }
            if (relinkedOnCreate.length) {
              consoleLog(`[MooAI] relink à la création de "${newBlockId}" — ${relinkedOnCreate.length} bloc(s) re-lié(s) vers leur idblock réel:`, relinkedOnCreate.map(b => b.blockname || b.idblock));
            }
          } catch (e) {
            consoleWarn('[MooAI] relink à la création — erreur:', e && e.message);
          }
        } else if (response.data && response.data.error === "Un block avec cet ID existe déjà") {
          // Afficher une notification d'erreur pour ID existant
          this.showNotification({
            message: "Un flux avec cet identifiant existe déjà",
            type: "error"
          });
        } else {
          // Gérer d'autres erreurs potentielles
          throw new Error(response.data.error || 'Erreur lors de la création du flux');
        }
      } catch (error) {
        //consoleError('Error creating block:', error);
        
        // Afficher une notification d'erreur
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de créer le flux'}`,
          type: "error"
        });
      } finally {
        this.creatingBlock = false;
      }
    },

    async duplicateBlock() {
      if (!this.selectedBlock) return;
      
      this.showNotification({
        message: 'Duplication en cours...',
        type: 'saving',
        duration: 0
      });
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Session expirée, veuillez vous reconnecter');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // 1. Generate new ID
        let baseId = (this.selectedBlock.displayName || this.selectedBlock.blockname || 'copie')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '');
          
        if (baseId.length > 13) baseId = baseId.substring(0, 13);
        const randomChars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let randomString = '';
        for (let i = 0; i < 6; i++) {
          randomString += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
        }
        const newIdblock = `${baseId}-${randomString}`;
        
        // 2. Create the wrapper block
        const blockName = this.selectedBlock.displayName || this.selectedBlock.blockname || 'Bloc';
        const blockData = {
          name: `${blockName} (Copie)`,
          idblock: newIdblock,
          idchatbot: this.$route.params.idchatbot,
          type: 'standard'
        };
        
        const createResponse = await axios.post(
          `${apiBaseUrl}/strong/create/flow/${this.$route.params.idchatbot}`,
          blockData,
          config
        );
        
        if (!createResponse.data || createResponse.data.error || createResponse.data.message !== "Block créé avec succès") {
          throw new Error(createResponse.data?.error || 'Erreur lors de la création du bloc');
        }
        
        const newBlockInternalId = createResponse.data.block._id;
        
        // Use the current flow on screen
        let flowToClone = this.selectedBlock.flow || [];
        
        // 3. Clone elements with new id_element and map previous ids
        const idMapping = {};
        
        // First pass: generate new IDs and build mapping
        const clonedFlow = flowToClone.map(element => {
          const newId = 'element-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
          if (element.id_element) {
            idMapping[element.id_element] = newId;
          }
          return {
            ...element,
            id_element: newId
          };
        });
        
        // Second pass: update id_previous using the mapping
        clonedFlow.forEach(element => {
          if (element.id_previous && idMapping[element.id_previous]) {
            element.id_previous = idMapping[element.id_previous];
          }
        });
        
        // 4. Save the flow for the new block
        await axios.post(
          `${apiBaseUrl}/update/flow/${this.$route.params.idchatbot}/${newBlockInternalId}/standard`,
          { flow: clonedFlow },
          config
        );
        
        this.showNotification({ message: 'Bloc dupliqué avec succès', type: 'success' });
        
        // 5. Refresh blocks
        await this.loadBlocks();
        
        // 6. Select the new block
        const newlyCreatedBlock = this.blocks.find(b => b.idblock === newIdblock);
        if (newlyCreatedBlock) {
          this.handleBlockClick(newlyCreatedBlock);
        }
        
      } catch (error) {
        //consoleError('Erreur lors de la duplication', error);
        this.showNotification({ 
          message: error.message || 'Erreur de duplication', 
          type: 'error' 
        });
      }
    },

    openDumpModal() {
      if (!this.selectedBlock) return;
      this.dumpContent = JSON.stringify(this.selectedBlock, null, 2);
      this.showDumpModal = true;
    },

    closeDumpModal() {
      this.showDumpModal = false;
      this.dumpContent = '';
    },

    async switchToMooTab() {
      this.activeDumpTab = 'moo';
      if (!this.mooSystemPromptLoaded && !this.mooSystemPromptLoading) {
        await this.fetchMooSystemPrompt();
      }
    },

    _colorizeMooPrompt(text) {
      if (!text) return '';
      const escapeHtml = (s) => s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      const applyInline = (line) => {
        let l = line;
        // Inline code `xxx`
        l = l.replace(/`([^`\n]+)`/g, '<span class="moo-inlinecode">$1</span>');
        // Tool names (avec ou sans backticks déjà appliqués)
        l = l.replace(/\b(create_block|create_variable|insert_element|delete_element|update_constitution|respond_to_user|ask_clarification)\b/g, '<span class="moo-tool">$1</span>');
        // idblock: xxx
        l = l.replace(/(idblock:\s*)([a-zA-Z0-9_-]+)/g, '$1<span class="moo-id">$2</span>');
        // id_element: "xxx"
        l = l.replace(/(id_element:\s*&quot;)([^&]+)(&quot;)/g, '$1<span class="moo-id">$2</span>$3');
        // Flèches du graph
        l = l.replace(/(─▶|→|↳|←|↦)/g, '<span class="moo-arrow">$1</span>');
        // Bold markdown **xxx**
        l = l.replace(/\*\*([^*]+)\*\*/g, '<strong class="moo-bold">$1</strong>');
        // Citation > xxx (en début de ligne)
        return l;
      };

      const lines = text.split('\n');
      const out = [];
      let inCodeBlock = false;

      for (const raw of lines) {
        const escaped = escapeHtml(raw);
        const trimmed = escaped.trim();

        // Toggle code block ```
        if (trimmed.startsWith('```')) {
          if (!inCodeBlock) {
            inCodeBlock = true;
            out.push('<span class="moo-codeblock-start">```</span>');
          } else {
            inCodeBlock = false;
            out.push('<span class="moo-codeblock-end">```</span>');
          }
          continue;
        }

        if (inCodeBlock) {
          out.push(`<span class="moo-codeblock">${applyInline(escaped)}</span>`);
          continue;
        }

        // Titres markdown
        const h = escaped.match(/^(#{1,4})\s+(.+)$/);
        if (h) {
          const level = h[1].length;
          out.push(`<span class="moo-h${level}">${applyInline(escaped)}</span>`);
          continue;
        }

        // Marqueurs de sections
        if (/^\s*⚠️/.test(escaped)) {
          out.push(`<span class="moo-warn">${applyInline(escaped)}</span>`);
          continue;
        }
        if (/^\s*❌/.test(escaped)) {
          out.push(`<span class="moo-bad">${applyInline(escaped)}</span>`);
          continue;
        }
        if (/^\s*✅/.test(escaped)) {
          out.push(`<span class="moo-good">${applyInline(escaped)}</span>`);
          continue;
        }
        if (/^\s*(📊|📍|🔥|💡)/.test(escaped)) {
          out.push(`<span class="moo-section">${applyInline(escaped)}</span>`);
          continue;
        }
        // Citation
        if (/^\s*&gt;/.test(escaped)) {
          out.push(`<span class="moo-quote">${applyInline(escaped)}</span>`);
          continue;
        }
        // Règle X —
        if (/^\s*(Règle|Rule)\s+\d/.test(escaped)) {
          out.push(`<span class="moo-rule">${applyInline(escaped)}</span>`);
          continue;
        }
        // Séparateur horizontal
        if (/^-{3,}$/.test(trimmed)) {
          out.push(`<span class="moo-hr">${escaped}</span>`);
          continue;
        }

        out.push(applyInline(escaped));
      }

      return out.join('\n');
    },

    async fetchMooSystemPrompt() {
      this.mooSystemPromptLoading = true;
      this.mooSystemPromptError = null;
      try {
        const idbot = this.$route.params.idchatbot;
        // On envoie le même payload que /build pour que le contextString preview
        // soit EXACTEMENT celui que recevra DeepSeek.
        const payload = {
          idbot,
          blocks: (this.blocks || []).map(b => ({
            idblock: b.idblock,
            blockname: b.blockname || b.displayName,
            type: b.type,
            description: b.description || ''
          })),
          variables: (this.variables || []).map(v => ({ slug: v.slug, type: v.type })),
          currentBlockFlow: this.selectedBlock || null,
          mentionedBlocks: [],
          selectedElement: null
        };
        const { data } = await axiosMoo.post('/ai/system-prompt', payload);
        if (data && data.success) {
          this.mooSystemPrompt = data.systemPrompt || '';
          this.mooContextString = data.contextString || '';
          this.mooGraphMeta = data.graphMeta || null;
          this.mooSystemPromptMeta = {
            provider: data.provider || '',
            model: data.model || '',
            tokens: data.tokens || 0
          };
          this.mooSystemPromptLoaded = true;
        } else {
          this.mooSystemPromptError = (data && data.message) || 'Réponse invalide';
        }
      } catch (err) {
        consoleError('fetchMooSystemPrompt error:', err);
        this.mooSystemPromptError = err.response?.data?.message || err.message || 'Erreur réseau';
      } finally {
        this.mooSystemPromptLoading = false;
      }
    },

    copyDump() {
      let payload = this.dumpContent;
      let label = 'JSON';
      if (this.activeDumpTab === 'prompt') {
        payload = this.currentSystemPrompt || '';
        label = 'System Prompt Griot';
      } else if (this.activeDumpTab === 'moo') {
        payload = [
          '=== SYSTEM ===',
          this.mooSystemPrompt || '',
          '',
          '=== USER MESSAGE PREFIX (contexte injecté) ===',
          this.mooContextString || ''
        ].join('\n');
        label = 'System Prompt Moo (system + contexte)';
      }
      if (!payload) {
        this.showNotification('Rien à copier', 'error');
        return;
      }
      navigator.clipboard.writeText(payload).then(() => {
        this.showNotification(`${label} copié dans le presse-papier !`, 'success');
      }).catch(err => {
        consoleError('Erreur de copie:', err);
        this.showNotification('Erreur lors de la copie', 'error');
      });
    },

    insertElement(elementType) {
      if (!this.selectedBlock) {
        this.showNotification({
          message: "Veuillez d'abord sélectionner un flux pour ajouter des éléments",
          type: "error"
        });
        return;
      }

      // Cloner le template d'élément
      const elementTemplate = JSON.parse(JSON.stringify(this.element_template[elementType]));
      
      // Injection dynamique de properties spécifiques
      if (elementType === 'agent_handoff') {
          elementTemplate.idbot = this.$route.params.idchatbot; // Inject actual chatbot ID
      } else if (elementType === 'ask_payment') {
          elementTemplate.provider = 'wave';
          elementTemplate.amount = 100;
          elementTemplate.client_reference = 'Paiement';
      }
      
      // Initialiser le flow s'il n'existe pas
      if (!this.selectedBlock.flow) {
        this.selectedBlock.flow = [];
      }
      
      // Ajouter l'élément au flow
      this.selectedBlock.flow.push(elementTemplate);
      
      // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
      this.refreshChain();
      
      // Notification de succès
      this.showNotification({
        message: `Élément "${elementType}" ajouté au flux avec succès`,
        type: "success"
      });
      
     },

    showElementInfo(elementType, event) {
      const elementDescriptions = {
        text: {
          title: 'Texte',
          description: 'Envoie un message texte simple à l\'utilisateur. Peut inclure des liens avec aperçu.'
        },
        text_button: {
          title: 'Texte avec boutons', 
          description: 'Message texte accompagné de boutons cliquables. Permet de créer des menus d\'options.'
        },
        list: {
          title: 'Liste interactive',
          description: 'Affiche une liste déroulante avec sections et options. Idéal pour les menus complexes.'
        },
        image: {
          title: 'Image',
          description: 'Envoie une image à l\'utilisateur. L\'image doit être préalablement uploadée.'
        },
        document: {
          title: 'Document',
          description: 'Envoie un fichier PDF à l\'utilisateur. Le document doit être préalablement uploadé.'
        },
        audio: {
          title: 'Audio',
          description: 'Envoie un fichier audio (message vocal, musique, etc.) à l\'utilisateur.'
        },
        audio_wolof: {
          title: 'Audio généré',
          description: 'Tu écris un texte, la voix (wolof par défaut, ou français) est fabriquée une seule fois puis renvoyée telle quelle. Les anciens audios restent disponibles pour être repris. Peut dépendre d\'une variable (ex. langage = wolof).'
        },
        video: {
          title: 'Vidéo',
          description: 'Envoie un fichier vidéo MP4 à l\'utilisateur. Lecteur intégré avec contrôles.'
        },
        delay: {
          title: 'Délai',
          description: 'Ajoute une pause dans le flux avant de continuer vers l\'élément suivant.'
        },
        request: {
          title: 'Requête HTTP',
          description: 'Effectue un appel API vers un service externe pour récupérer ou envoyer des données.'
        },
        answer: {
          title: 'Réponse utilisateur',
          description: 'Attend une réponse de l\'utilisateur et la stocke dans une variable.'
        },
        redirect: {
          title: 'Redirection',
          description: 'Redirige le flux vers un autre bloc de conversation.'
        },
        notification: {
          title: 'Notification',
          description: 'Envoie une notification à un numéro spécifique.'
        },
        variable_insert: {
          title: 'Insertion de variable',
          description: 'Insère ou modifie une variable dans le contexte de la conversation.'
        },
        location_request_message: {
          title: 'Demande de localisation',
          description: 'Demande à l\'utilisateur de partager sa position géographique via un bouton de localisation.'
        },
        button_link: {
          title: 'Carte avec bouton',
          description: 'Affiche une carte interactive avec image header, texte de corps, texte de pied et bouton d\'action cliquable.'
        },
        carousel: {
          title: 'Carrousel',
          description: 'Envoie 2 à 10 cartes défilantes (image ou vidéo, texte, boutons). Les boutons de réponse redirigent vers un bloc ; le bouton lien ouvre une URL.'
        },
        template_message: {
          title: 'Template WhatsApp',
          description: 'Envoie un message basé sur un template WhatsApp pré-approuvé. Nécessaire pour initier des conversations (fenêtre de 24h).'
        },
        agent_handoff: {
          title: 'Stop AI / Agent Handoff',
          description: 'Désactive l\'IA pour cet utilisateur, signalant un transfert vers un agent humain.'
        },
        condition: {
          title: 'Condition',
          description: 'Crée des branchements logiques (Si/Alors) basés sur des variables pour rediriger le flux.'
        },
        ask_payment: {
          title: 'Demander un paiement',
          description: 'Initie une demande de paiement (Wave) et redirige vers un bloc de succès ou d\'échec.'
        }
      };

      const info = elementDescriptions[elementType];
      if (info) {
        this.elementInfo = {
          show: true,
          title: info.title,
          description: info.description
        };
        
        // Positionner le panneau au niveau du bouton
        this.$nextTick(() => {
          const panel = document.querySelector('.element-info-panel');
          if (panel && event) {
            const rect = event.currentTarget.getBoundingClientRect();
            panel.style.top = `${rect.top + (rect.height / 2)}px`;
            panel.style.transform = 'translateY(-50%)';
          }
        });
      }
    },

    hideElementInfo() {
      this.elementInfo = {
        show: false,
        title: '',
        description: ''
      };
    },
    
    // Méthodes pour la gestion des éléments du flux
    getElementTypeLabel(type) {
      const labels = {
        text: 'Texte',
        text_button: 'Texte avec boutons',
        carousel: 'Carrousel',
        list: 'Liste interactive',
        image: 'Image',
        document: 'Document',
        audio: 'Audio',
        audio_wolof: 'Audio généré',
        delay: 'Délai',
        action: 'Action',
        request: 'Requête HTTP',
        answer: 'Réponse utilisateur',
        redirect: 'Redirection',
        notification: 'Notification',
        variable_insert: 'Variable',
        ask_payment: 'Paiement'
      };
      return labels[type] || type;
    },
    
    // Gestion du déplacement des éléments
    moveElementUp(index) {
      if (index > 0) {
        this.moveElement(index, index - 1);
      }
    },
    
    moveElementDown(index) {
      if (this.selectedBlock && this.selectedBlock.flow && index < this.selectedBlock.flow.length - 1) {
        this.moveElement(index, index + 1);
      }
    },
    
    moveElement(fromIndex, toIndex) {
      if (!this.selectedBlock || !this.selectedBlock.flow) return;
      
      // Créer une copie du tableau pour la manipulation
      const updatedFlow = [...this.selectedBlock.flow];
      const [movedElement] = updatedFlow.splice(fromIndex, 1);
      updatedFlow.splice(toIndex, 0, movedElement);
      
      // Mettre à jour le flux
      this.selectedBlock.flow = updatedFlow;
      
      // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
      this.refreshChain();
      
      // Notification de succès
      this.showNotification({
        message: `Élément déplacé avec succès`,
        type: 'success'
      });
    },
    
    // --- Nouveautés Drag & Drop inter-blocs ---
    dragOverBlock(event) {
      event.preventDefault();
      const draggingElement = document.querySelector('.dragging');
      // On autorise le drop uniquement si on "drag" un élément (et non du texte basique)
      if (draggingElement) {
        event.currentTarget.classList.add('drag-over-block');
      }
    },
    
    dragLeaveBlock(event) {
      event.currentTarget.classList.remove('drag-over-block');
    },
    
    dropOnBlock(event, targetBlock) {
      event.preventDefault();
      event.currentTarget.classList.remove('drag-over-block');
      
      // Nettoyer l'état "dragging" global
      document.querySelectorAll('.flow-element-wrapper').forEach(el => {
        el.classList.remove('dragging');
        el.classList.remove('drag-over');
      });
      
      const sourceIndexRaw = event.dataTransfer.getData('text/plain');
      if (!sourceIndexRaw) return;
      
      const sourceIndex = parseInt(sourceIndexRaw);
      
      // Si la cible est le même bloc que celui actuellement ouvert, on annule (ce n'est pas un transfert cross-block)
      if (this.selectedBlock && targetBlock.idblock === this.selectedBlock.idblock) {
        return;
      }
      
      // Préparer les données pour le modal
      this.transferSourceIndex = sourceIndex;
      this.transferSourceBlockId = this.selectedBlock._id;
      this.transferTargetBlock = targetBlock;
      this.showTransferModal = true;
    },
    
    cancelTransfer() {
      this.showTransferModal = false;
      this.transferSourceIndex = null;
      this.transferSourceBlockId = null;
      this.transferTargetBlock = null;
      this.isTransferring = false;
    },
    
    async confirmTransfer() {
      if (this.transferSourceIndex === null || !this.transferTargetBlock || !this.selectedBlock) {
        this.cancelTransfer();
        return;
      }
      
      this.isTransferring = true;
      const sourceIndex = this.transferSourceIndex;
      const targetBlock = this.transferTargetBlock;
      
      try {
        // 1. Couper l'élément du bloc source
        const sourceFlow = [...this.selectedBlock.flow];
        const [movedElement] = sourceFlow.splice(sourceIndex, 1);
        this.selectedBlock.flow = sourceFlow;
        
        // 2. Coller l'élément dans le bloc cible
        if (!targetBlock.flow) {
          targetBlock.flow = []; // Si jamais le target n'a pas encore de flux du tout
        }
        
        // On effectue une copie profonde pour éviter de muter des objets liés
        const elementToAdd = JSON.parse(JSON.stringify(movedElement));
        delete elementToAdd.id_element;
        delete elementToAdd.id_previous;
        targetBlock.flow.push(elementToAdd);
        
        // 3. Rafraîchir les deux chaînes 
        // a) Le selectBlock (source)
        this.refreshChain(this.selectedBlock, true);
        
        // b) Le targetBlock (cible) 
        // Attention refreshChain est asynchrone pour la sauvegarde, on l'appelle en parallèle
        this.refreshChain(targetBlock, true);
        
        this.showNotification({
          message: `Élément déplacé avec succès vers ${targetBlock.displayName}`,
          type: 'success'
        });
        
      } catch (error) {
        //consoleError('❌ [PlaygroundView] Erreur lors du déplacement cross-block', error);
        this.showNotification({ message: 'Erreur lors du déplacement', type: 'error' });
      } finally {
        this.isTransferring = false;
        this.showTransferModal = false;
        this.transferTargetBlock = null;
        this.transferSourceIndex = null;
      }
    },
    
    updateElementReferences(flowArray) {
      // Méthode obsolète - remplacée par refreshChain()
      //consoleWarn('updateElementReferences est obsolète, utilisez refreshChain() à la place');
      return flowArray;
    },
    
    editElement(index) {
      // À implémenter: ouvrir un modal d'édition pour l'élément
      this.showNotification({
        message: `Fonctionnalité d'édition à implémenter pour l'élément ${index + 1}`,
        type: 'info'
      });
    },
    
    deleteElement(index) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
        this.selectedBlock.flow.splice(index, 1);
        // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
        this.refreshChain();
      }
    },
    
    addFirstElement() {
      if (!this.selectedBlock) return;
      
      // Créer un premier élément texte par défaut
      this.insertElement('text');
    },

    // Nouvelle méthode pour l'édition directe en cliquant sur le message
    editMessageDirectly(index, event) {
      event.stopPropagation(); // Empêcher la propagation de l'événement
      const element = this.selectedBlock.flow[index];
      let currentText = '';
      
      // Détecter le type de message et récupérer le texte actuel
      if (element.type === 'text') {
        currentText = element.text || '';
      } else if (element.type === 'text_button') {
        currentText = element.text || element.message || '';
      }
      
      // Créer un input temporaire pour l'édition
      const tempInput = document.createElement('textarea');
      tempInput.value = currentText;
      tempInput.style.cssText = `
        width: 100%;
        padding: 0.75rem;
        border: 2px solid #667eea;
        border-radius: 12px;
        font-family: inherit;
        font-size: 1rem;
        color: #333;
        background: white;
        resize: vertical;
        min-height: 60px;
        outline: none;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      `;
      
      // Trouver l'élément de contenu du message
      const messageContent = event.target.closest('.message-content');
      if (messageContent) {
        const originalContent = messageContent.innerHTML;
        messageContent.innerHTML = '';
        messageContent.appendChild(tempInput);
        tempInput.focus();
        tempInput.select();
        
        // Sauvegarder en appuyant sur Entrée ou en perdant le focus
        const saveEdit = () => {
          const newText = tempInput.value.trim();
          if (newText !== '') {
            if (element.type === 'text') {
              element.text = newText;
            } else if (element.type === 'text_button') {
              if ('text' in element) {
                element.text = newText;
              } else {
                element.message = newText;
              }
            }
            this.saveBlock();
            this.showNotification({
              message: 'Message modifié avec succès',
              type: 'success'
            });
          }
          messageContent.innerHTML = originalContent;
        };
        
        // Annuler en appuyant sur Échap
        const cancelEdit = () => {
          messageContent.innerHTML = originalContent;
        };
        
        tempInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            saveEdit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
          }
        });
        
        tempInput.addEventListener('blur', saveEdit);
      }
    },

    // Méthode pour sauvegarder le bloc
    async saveBlock() {
      try {
        // Sauvegarder la position de scroll avant la sauvegarde
        this.saveScrollPosition();
        
        // Récupérer le token depuis localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
          this.$router.push('/login');
          return;
        }

        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };

         const response = await axiosPeelo.post(
          `/strong/update/blockname/${this.$route.params.idchatbot}/${this.selectedBlock._id}/${this.selectedBlock.type}`,
          {
            blockname: this.selectedBlock.blockname
          },
          config
        );

        
        if (response.data.success) {
          // Mettre à jour le nom dans les listes de flux
          if (this.selectedBlock.category === 'primary') {
            const blockToUpdate = this.primaryBlocks.find(block => block._id === this.selectedBlock._id);
            if (blockToUpdate) {
              blockToUpdate.blockname = this.selectedBlock.blockname;
              blockToUpdate.displayName = this.selectedBlock.blockname;
            }
          } else {
            const blockToUpdate = this.secondaryBlocks.find(block => block._id === this.selectedBlock._id);
            if (blockToUpdate) {
              blockToUpdate.blockname = this.selectedBlock.blockname;
              blockToUpdate.displayName = this.selectedBlock.blockname;
            }
          }

          // Mettre à jour les listes filtrées
          this.filterBlocks();
          
          this.showNotification({ message: 'Sauvegardé', type: 'success' });
          this.isEditingBlockName = false;
          this.hasUnsavedTrainingChanges = true; // Mark as needing training
          this.saveStatus = {
            show: true,
            type: 'saved',
            message: 'Sauvegardé'
          };
          
          // Restaurer la position de scroll après la sauvegarde réussie
          this.restoreScrollPosition();
        }
      } catch (error) {
        //consoleError('Erreur saveBlock:', error);
        
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
          localStorage.removeItem('user');
          this.$router.push('/login');
          return;
        }

        this.showNotification({ message: 'Erreur lors de la sauvegarde', type: 'error' });
        this.saveStatus = {
          show: true,
          type: 'error',
          message: 'Erreur lors de la sauvegarde'
        };
        
        // Restaurer la position même en cas d'erreur
        this.restoreScrollPosition();
      }
    },
    
    // Méthode pour déclencher l'upload d'image
    uploadImage(index) {
      // Créer un élément input file dynamique
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.style.display = 'none';
      
      // Ajouter l'événement de changement
      fileInput.addEventListener('change', (event) => {
        this.handleImageUpload(event, index);
      });
      
      // Ajouter au DOM, déclencher le clic, puis supprimer
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        document.body.removeChild(fileInput);
      }, 1000);
    },
    
    // Méthode pour gérer l'upload d'image
    handleImageUpload(event, index) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Vérifier que c'est bien une image
      if (!file.type.match('image.*')) {
        this.showNotification({
          message: "Veuillez sélectionner une image valide",
          type: "error"
        });
        return;
      }
      
      // Créer un URL pour l'image
      const imageUrl = URL.createObjectURL(file);
      
      // Mettre à jour l'élément avec le lien de l'image
      const element = this.selectedBlock.flow[index];
      element.link = imageUrl;
      
      // Dans une application réelle, vous voudriez probablement télécharger
      // l'image sur un serveur et stocker l'URL retournée
      
      this.saveBlock();
      
      this.showNotification({
        message: "Image ajoutée avec succès",
        type: "success"
      });
    },
    
    // Méthode pour afficher une notification
    showNotification({ message, type = 'success', duration = 5000 }) {
       if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 3000)
      };
    },

    startEditingBlockName() {
      // Vérifier si l'utilisateur est connecté avant de permettre l'édition
      const userData = localStorage.getItem('user');
      if (!userData) {
        this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
        this.$router.push('/login');
        return;
      }

      this.isEditingBlockName = true;
      this.editedBlockName = this.selectedBlock.blockname || '';
      this.$nextTick(() => {
        if (this.$refs.blockNameInput) {
          this.$refs.blockNameInput.focus();
        }
      });
    },

    saveBlockName() {
      this.isEditingBlockName = false;
      this.selectedBlock.blockname = this.editedBlockName;
      this.saveBlock();
    },

    cancelBlockNameEdit() {
      this.isEditingBlockName = false;
      this.editedBlockName = this.selectedBlock.blockname || '';
    },

    validateBlockName(event) {
      if (this.editedBlockName.length > 20) {
        this.editedBlockName = this.editedBlockName.slice(0, 20);
        this.showNotification({ message: 'Le nom ne peut pas dépasser 20 caractères', type: 'error' });
        return;
      }
    },

    async saveBlockName() {
      if (!this.editedBlockName.trim()) {
        this.showNotification({ message: 'Le nom du bloc ne peut pas être vide', type: 'error' });
        return;
      }
      
      if (this.editedBlockName.length > 20) {
        this.showNotification({ message: 'Le nom ne peut pas dépasser 20 caractères', type: 'error' });
        return;
      }

      this.selectedBlock.blockname = this.editedBlockName.trim();
      await this.saveBlock();
    },

    // Méthodes pour l'édition de la description
    startEditingDescription() {
      const userData = localStorage.getItem('user');
      if (!userData) {
        this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
        this.$router.push('/login');
        return;
      }

      this.isEditingDescription = true;
      this.editedDescription = this.selectedBlock.description || '';
      this.$nextTick(() => {
        if (this.$refs.descriptionInput) {
          this.$refs.descriptionInput.focus();
          this.adjustTextareaHeight();
        }
      });
    },

    async saveDescription() {
      this.isEditingDescription = false;
      
      // Permettre une description vide
      this.selectedBlock.description = this.editedDescription.trim();
      
      try {
        // Sauvegarder la position de scroll avant la sauvegarde
        this.saveScrollPosition();
        
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
          this.$router.push('/login');
          return;
        }

        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };

        // Utiliser la nouvelle route spécifique pour la description
        const response = await axiosPeelo.post(
          `/strong/update/description/${this.$route.params.idchatbot}/${this.selectedBlock._id}/${this.selectedBlock.type}`,
          {
            description: this.selectedBlock.description
          }, 
          config 
        );

        if (response.data.success) {
          // Mettre à jour la description dans les listes de flux
          if (this.selectedBlock.category === 'primary') {
            const blockToUpdate = this.primaryBlocks.find(block => block._id === this.selectedBlock._id);
            if (blockToUpdate) {
              blockToUpdate.description = this.selectedBlock.description;
            }
          } else {
            const blockToUpdate = this.secondaryBlocks.find(block => block._id === this.selectedBlock._id);
            if (blockToUpdate) {
              blockToUpdate.description = this.selectedBlock.description;
            }
          }

          this.showNotification({ message: 'Description sauvegardée', type: 'success' });
          this.restoreScrollPosition();
        }
      } catch (error) {
        //consoleError('Erreur saveDescription:', error);
        
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
          localStorage.removeItem('user');
          this.$router.push('/login');
          return;
        }

        this.showNotification({ message: 'Erreur lors de la sauvegarde de la description', type: 'error' });
        this.restoreScrollPosition();
      }
    },

    cancelDescriptionEdit() {
      this.isEditingDescription = false;
      this.editedDescription = this.selectedBlock.description || '';
    },

    // Méthode pour ajuster automatiquement la hauteur du textarea
    adjustTextareaHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.descriptionInput;
        if (textarea) {
          textarea.style.height = 'auto';
          const newHeight = Math.min(Math.max(textarea.scrollHeight, 18), 36);
          textarea.style.height = newHeight + 'px';
        }
      });
    },

    // Méthode pour obtenir le bon composant selon le type d'élément
    getElementComponent(type) {
      if (ELEMENT_COMPONENTS[type]) {
        return ELEMENT_COMPONENTS[type];
      }
      
      //consoleWarn(`Type de composant non trouvé: ${type}. Types disponibles: ${Object.keys(ELEMENT_COMPONENTS).join(', ')}`);
      
      // Essayer de trouver un type similaire
      const normalizedType = type.toLowerCase().replace(/s$/, '');
      
      for (const [key, component] of Object.entries(ELEMENT_COMPONENTS)) {
        if (key.includes(normalizedType) || normalizedType.includes(key)) {
          //consoleInfo(`Utilisation du composant ${key} pour le type ${type}`);
          return component;
        }
      }
      
      return null;
    },

    // Méthode pour mettre à jour un élément depuis le composant
    updateElement(updatedElement, index) {
      //consoleLog('🔄 [PlaygroundView] updateElement appelé:', updatedElement, index);
      //consoleLog('📊 [PlaygroundView] État actuel du flow[index]:', this.selectedBlock.flow[index]);
      //consoleLog('🎯 [PlaygroundView] Type d\'élément:', updatedElement.type);
      
      if (this.selectedBlock && this.selectedBlock.flow && index >= 0 && index < this.selectedBlock.flow.length) {
        this.selectedBlock.flow[index] = { ...updatedElement };
        //consoleLog('✅ [PlaygroundView] Élément mis à jour, déclenchement de refreshChain...');
        // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
        this.refreshChain();
      } else {
        //consoleError('❌ [PlaygroundView] Conditions non respectées pour updateElement');
      }
    },

    // Méthode pour dupliquer un élément
    duplicateElement(index) {
      if (this.selectedBlock && this.selectedBlock.flow && index >= 0 && index < this.selectedBlock.flow.length) {
        const elementToDuplicate = { ...this.selectedBlock.flow[index] };
        
        // Supprimer les identifiants pour forcer la génération de nouveaux dans refreshChain
        delete elementToDuplicate.id_element;
        delete elementToDuplicate.id_previous;
        
        // Insérer après l'élément original
        this.selectedBlock.flow.splice(index + 1, 0, elementToDuplicate);
        // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
        this.refreshChain();
        this.showNotification({
          message: 'Élément dupliqué avec succès',
          type: 'success'
        });
      }
    },

    // Méthode pour récupérer les variables du chatbot
    async fetchVariables() {
      try {
        // Variables globales par défaut
        const globalVariables = [
          { id: 'first_name', type: 'global', slug: 'first_name' },
          { id: 'last_name', type: 'global', slug: 'last_name' },
          { id: 'reply_phone', type: 'global', slug: 'reply_phone' },
          { id: 'full_name', type: 'global', slug: 'full_name' },
          { id: 'phone_number', type: 'global', slug: 'phone_number' },
          { id: 'last_input', type: 'global', slug: 'last_input' },
          { id: 'id_user', type: 'global', slug: 'id_user' },
          { id: 'phone_number_id', type: 'global', slug: 'phone_number_id' },
          { id: 'bot_number', type: 'global', slug: 'bot_number' },
          { id: 'idbot', type: 'global', slug: 'idbot' },
          { id: 'order_amount', type: 'global', slug: 'order_amount' },


        ];
        
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.variables = globalVariables;
          return;
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        let chatbotVariables = [];
        
        try {
          const response = await axiosPeelo.get(
            `/get/strong/variables/${this.$route.params.idchatbot}`,
            config
          );
          
          if (response.data) {
            let responseVariables = null;
            
            if (response.data.success && response.data.variables) {
              responseVariables = response.data.variables;
            } else if (response.data.variables) {
              responseVariables = response.data.variables;
            } else if (Array.isArray(response.data)) {
              responseVariables = response.data;
            }
            
            if (responseVariables && Array.isArray(responseVariables)) {
              chatbotVariables = responseVariables;
            }
          }
        } catch (e) {
          //consoleError('Erreur lors de la récupération des variables:', e);
          chatbotVariables = [];
        }
        
        this.variables = [...chatbotVariables, ...globalVariables];
        
      } catch (e) {
        //consoleError('Erreur globale lors de la récupération des variables:', e);
        this.variables = [
          { id: 'first_name', type: 'global', slug: 'first_name' },
          { id: 'last_name', type: 'global', slug: 'last_name' },
          { id: 'reply_phone', type: 'global', slug: 'reply_phone' },
          { id: 'full_name', type: 'global', slug: 'full_name' },
          { id: 'phone_number', type: 'global', slug: 'phone_number' },
          { id: 'last_input', type: 'global', slug: 'last_input' },
          { id: 'id_user', type: 'global', slug: 'id_user' },
          { id: 'phone_number_id', type: 'global', slug: 'phone_number_id' },
          { id: 'bot_number', type: 'global', slug: 'bot_number' }
        ];
      }
    },

    // Navigation vers un bloc spécifique
    navigateToBlock(idblock) {
      this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground/${idblock}`);
    },

    // Obtenir le label du type de bloc
    getBlockTypeLabel(type) {
      const labels = {
        welcome: 'Flux Principal',
        standard: 'Flux Standard',
        commerce: 'Commerce',
        default: 'Flux par défaut'
      };
      return labels[type] || type;
    },

    // Charger les liens entrants vers le bloc actuel
    async loadInboundLinks() {

      if (!this.selectedBlock) return;
      
      this.loadingInboundLinks = true;
      this.inboundLinks = [];
      
       
      try { 
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // Charger différents types de liens entrants
        const linkTypes = [ 'starter', 'commerce_point', 'default_block', 'appel_point', 'blocks'];
        const allInboundLinks = [];
        
        for (const linkType of linkTypes) {
          try {
             
            const response = await axiosPeelo.get(
              `/get/flow/${this.$route.params.idchatbot}/${this.selectedBlock.idblock}/${linkType}`,
              config
            );
            
             
            if (response.data && response.data.match_previous && response.data.match_previous.length > 0) {
              // Ajouter le type de lien aux résultats pour identification
              const linksWithType = response.data.match_previous.map(link => ({
                ...link,
                linkType: linkType,
                sourceType: this.getLinkTypeLabel(linkType)
              }));
              allInboundLinks.push(...linksWithType);
            }
          } catch (error) {
            //consoleWarn(`Erreur lors du chargement des liens ${linkType}:`, error);
            // Continue avec les autres types même si un échoue
          }
        }
        
        // Supprimer les doublons basés sur l'idblock
        const uniqueLinks = allInboundLinks.filter((link, index, self) => 
          index === self.findIndex(l => l.idblock === link.idblock)
        );
        
        this.inboundLinks = uniqueLinks;

        
        this.resetFilteredLinks();
      } catch (error) {
        //consoleError('Erreur lors du chargement des liens entrants:', error);
        this.showNotification({ 
          message: 'Erreur lors du chargement des liens entrants', 
          type: 'error' 
        });
      } finally {
        this.loadingInboundLinks = false;
      }
    },

    // Obtenir le label du type de lien
    getLinkTypeLabel(linkType) {
      const labels = {
        'previous': 'Bloc Standard',
        'starter': 'Flux de Démarrage',
        'commerce_point': 'Flux E-commerce',
        'default_block': 'Flux par Défaut',
        'appel_point': 'Appel vocal'
      };
      return labels[linkType] || linkType;
    },

    // Analyser les liens sortants du bloc actuel
    analyzeOutboundLinks() {
      if (!this.selectedBlock || !this.selectedBlock.flow) {
        this.outboundLinks = [];
        return;
      }

      const outbound = [];
      
      this.selectedBlock.flow.forEach((element, index) => {
        // Vérifier les éléments de redirection
        if (element.type === 'redirect' || element.type === 'redirection') {
          if (element.redirection_block) {
            const targetBlock = this.blocks.find(block => block.idblock === element.redirection_block);
            if (targetBlock) {
              outbound.push({
                id: `redirect_${index}`,
                targetBlock: element.redirection_block,
                targetName: this.getBlockDisplayName(element.redirection_block),
                sourceType: 'Redirection'
              });
            }
          }
        }
        
        // Carrousel : chaque bouton de réponse d'une carte peut pointer vers un bloc
        if (element.type === 'carousel' && Array.isArray(element.cards)) {
          element.cards.forEach((card, cardIndex) => {
            (card.buttons || []).forEach((button, buttonIndex) => {
              if (!button || !button.id) return;
              const targetBlock = this.blocks.find(block => block.idblock === button.id);
              if (targetBlock) {
                outbound.push({
                  id: `carousel_${index}_${cardIndex}_${buttonIndex}`,
                  targetBlock: button.id,
                  targetName: this.getBlockDisplayName(button.id),
                  sourceType: `Carte ${cardIndex + 1} — bouton "${button.title}"`
                });
              }
            });
          });
        }

        // Vérifier les boutons dans text_button en comparant l'id avec les idblock
        if (element.type === 'text_button' && element.buttons) {
          element.buttons.forEach((button, buttonIndex) => {
            if (button.id) {
              // Chercher si l'id du bouton correspond à un idblock d'un bloc existant
              const targetBlock = this.blocks.find(block => block.idblock === button.id);
              if (targetBlock) {
                outbound.push({
                  id: `button_${index}_${buttonIndex}`,
                  targetBlock: button.id,
                  targetName: this.getBlockDisplayName(button.id),
                  sourceType: `Bouton "${button.title}"`
                });
              }
            }
            
            // Fallback : vérifier aussi dans le payload pour les redirections explicites
            if (button.payload && button.payload.includes('redirect_to=')) {
              const match = button.payload.match(/redirect_to=([^&]+)/);
              if (match) {
                const targetBlock = this.blocks.find(block => block.idblock === match[1]);
                if (targetBlock) {
                  outbound.push({
                    id: `button_payload_${index}_${buttonIndex}`,
                    targetBlock: match[1],
                    targetName: this.getBlockDisplayName(match[1]),
                    sourceType: `Bouton "${button.title}" (payload)`
                  });
                }
              }
            }
          });
        }
        
        // Vérifier les listes interactives avec redirection
        if (element.type === 'list' && element.interactive && element.interactive.action && element.interactive.action.sections) {
          element.interactive.action.sections.forEach((section, sectionIndex) => {
            if (section.rows) {
              section.rows.forEach((row, rowIndex) => {
                // Vérifier si l'id de la row correspond à un idblock
                if (row.id) {
                  const targetBlock = this.blocks.find(block => block.idblock === row.id);
                  if (targetBlock) {
                    outbound.push({
                      id: `list_row_${index}_${sectionIndex}_${rowIndex}`,
                      targetBlock: row.id,
                      targetName: this.getBlockDisplayName(row.id),
                      sourceType: `Liste "${row.title}"`
                    });
                  }
                }
                
                // Fallback : vérifier aussi dans le payload
                if (row.payload && row.payload.includes('redirect_to=')) {
                  const match = row.payload.match(/redirect_to=([^&]+)/);
                  if (match) {
                    const targetBlock = this.blocks.find(block => block.idblock === match[1]);
                    if (targetBlock) {
                      outbound.push({
                        id: `list_payload_${index}_${sectionIndex}_${rowIndex}`,
                        targetBlock: match[1],
                        targetName: this.getBlockDisplayName(match[1]),
                        sourceType: `Liste "${row.title}" (payload)`
                      });
                    }
                  }
                }
              });
            }
          });
        }
      });
      
      // Supprimer les doublons basés sur targetBlock et sourceType
      const uniqueOutbound = outbound.filter((item, index, self) => 
        index === self.findIndex(t => t.targetBlock === item.targetBlock && t.sourceType === item.sourceType)
      );
      
      this.outboundLinks = uniqueOutbound;
      

      this.resetFilteredLinks();
    },

    // Obtenir le nom d'affichage d'un bloc par son ID
    getBlockDisplayName(idblock) {
      const block = this.blocks.find(b => b.idblock === idblock);
      return block ? (block.blockname || block.displayName || block.idblock) : idblock;
    },

    toggleLinksExpanded() {
      this.isLinksExpanded = !this.isLinksExpanded;
    },


    updateTotalLinksCount() {
      this.totalLinksCount = this.inboundLinks.length + this.outboundLinks.length;
    },

    getLinksStatus() {
      if (this.loadingInboundLinks) return 'loading';
      if (this.totalLinksCount === 0) return 'empty';
      if (this.totalLinksCount > 0) return 'active';
      return '';
    },

    getLinksStatusText() {
      const status = this.getLinksStatus();
      switch (status) {
        case 'loading':
          return 'Analyse...';
        case 'empty':
          return 'Isolé';
        case 'active':
          return this.totalLinksCount > 5 ? 'Connecté+' : 'Connecté';
        default:
          return '';
      }
    },

    toggleFilter() {
      this.showFilter = !this.showFilter;
      if (this.showFilter) {
        this.$nextTick(() => {
          if (this.$refs.filterInput) {
            this.$refs.filterInput.focus();
          }
        });
      } else {
        this.clearFilter();
      }
    },

    filterLinks() {
      const query = this.filterQuery.toLowerCase();
      
      this.filteredInboundLinks = this.inboundLinks.filter(link => 
        (link.blockname || link.idblock || '').toLowerCase().includes(query)
      );
      
      this.filteredOutboundLinks = this.outboundLinks.filter(link => 
        (link.targetName || link.targetBlock || '').toLowerCase().includes(query) ||
        (link.sourceType || '').toLowerCase().includes(query)
      );
    },

    clearFilter() {
      this.filterQuery = '';
      this.resetFilteredLinks();
    },

    resetFilteredLinks() {
      this.filteredInboundLinks = [...this.inboundLinks];
      this.filteredOutboundLinks = [...this.outboundLinks];
    },

    // Générer une chaîne alphanumérique aléatoire
    generateRandomId(length = 8) {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    // Rafraîchir la chaîne de traçabilité du flow
    refreshChain(blockToUpdate = this.selectedBlock, shouldSave = true) {
      
      if (!blockToUpdate || !blockToUpdate.flow) {
        return;
      }
      
      if (blockToUpdate === this.selectedBlock) {
        this.loading = true;
        this.hasUnsavedTrainingChanges = true; // Mark as needing training immediately when modification starts
      }
      
      try {
        const updatedFlow = blockToUpdate.flow.map((element, index) => {
          return this.updateElementChain(element, index, blockToUpdate);
        });
        
        blockToUpdate.flow = updatedFlow;
        
        // Sauvegarder automatiquement après la mise à jour
        if (shouldSave) {
          if (blockToUpdate === this.selectedBlock) {
            this.saveFlowToServer();
          } else {
            this.saveSpecificBlockToServer(blockToUpdate);
          }
        }
      } catch (error) {
        //consoleError('❌ [PlaygroundView] Erreur lors de la mise à jour de la chaîne:', error);
      } finally {
        if (blockToUpdate === this.selectedBlock) this.loading = false;
      }
    },

    // Vérifier la connexion internet
    checkConnection() {
      return navigator.onLine;
    },

    // Sauvegarder un bloc spécifique silencieusement (pour les transferts)
    async saveSpecificBlockToServer(block) {
      if (!block || !block.flow || !this.checkConnection()) return;
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Session expirée');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        const blockType = block.type || 'blocks';
        const saveUrl = `/update/flow/${this.$route.params.idchatbot}/${block._id}/${blockType}`;
        
        const response = await axiosPeelo.post(saveUrl, { flow: block.flow }, config);
        
        if (response.data.success) {
           this.hasUnsavedTrainingChanges = true;
        }
      } catch (error) {
        // Silencieux en cas d'échec pour le bloc secondaire ou logging mineur
        //consoleError('❌ Error saving target block: ', error);
      }
    },

    // Sauvegarder le flow sur le serveur
    async saveFlowToServer() {
      //consoleLog('💾 [PlaygroundView] saveFlowToServer appelé');
      
      if (!this.selectedBlock || !this.selectedBlock.flow) {
        //consoleLog('❌ [PlaygroundView] Pas de bloc sélectionné ou pas de flow pour saveFlowToServer');
        return;
      }
      
      //consoleLog('📊 [PlaygroundView] Sauvegarde du flow avec', this.selectedBlock.flow.length, 'éléments');
      
      // Sauvegarder la position de scroll avant la sauvegarde
      this.saveScrollPosition();
      
      // Vérifier la connexion
      if (!this.checkConnection()) {
        //consoleLog('📡 [PlaygroundView] Pas de connexion internet');
        this.showNotification({
          message: 'Pas de connexion, vos changements ne seront pas effectués',
          type: 'error',
          duration: 5000
        });
        this.restoreScrollPosition();
        return;
      }
      
      // Afficher le statut "sauvegarde en cours"
      this.showNotification({
        message: 'Sauvegarde...',
        type: 'saving',
        duration: 0 // Pas d'auto-hide
      });
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          //consoleLog('❌ [PlaygroundView] Aucune donnée utilisateur trouvée');
          throw new Error('Session expirée');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // Déterminer le type de bloc
        const blockType = this.selectedBlock.type || 'blocks';
        
        const saveUrl = `/update/flow/${this.$route.params.idchatbot}/${this.selectedBlock._id}/${blockType}`;
        
        
        const response = await axiosPeelo.post(
          saveUrl,
          { flow: this.selectedBlock.flow },
          config
        );
        
        
        if (response.data.success) {
          //consoleLog('🎉 [PlaygroundView] Sauvegarde réussie !');
          this.showNotification({
            message: 'Saved',
            type: 'saved',
            duration: 2000
          });
          
          this.hasUnsavedTrainingChanges = true; // Mark as needing training after save
          
          // Auto-train Griot after successful save
          this.autoTrainBlock();
          
          // Restaurer la position de scroll après la sauvegarde réussie
          this.restoreScrollPosition();
        } else {
          //consoleLog('❌ [PlaygroundView] Réponse d\'échec du serveur:', response.data);
          throw new Error(response.data.error || 'Erreur lors de la sauvegarde');
        }
      } catch (error) {
        //consoleError('❌ [PlaygroundView] Erreur lors de la sauvegarde du flow:', error);
       
        
        this.showNotification({
          message: 'Erreur de sauvegarde',
          type: 'error',
          duration: 4000
        });
        
        // Restaurer la position même en cas d'erreur
        this.restoreScrollPosition();
      }
    },

    // Méthode pour afficher les notifications unifiée
    showNotification({ message, type = 'success', duration = 3000 }) {
       if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      
      this.notification = {
        show: true,
        message,
        type,
        timeout: duration > 0 ? setTimeout(() => {
          this.notification.show = false;
        }, duration) : null
      };
    },

    // Mettre à jour la chaîne d'un élément spécifique
    updateElementChain(element, index, targetBlock = this.selectedBlock) {
      if (!element || !targetBlock) return element;
      // Logique extraite dans @/services/mooFlowBuilder (partagée avec les harnais de test Node).
      return chainElement(element, index, targetBlock.flow, this.$route.params.idchatbot, targetBlock._id);
    },

    handleOnline() {
      this.isOnline = true;
      this.showNotification({ message: 'Connexion rétablie', type: 'success' });
    },

    handleOffline() {
      this.isOnline = false;
      this.showNotification({ message: 'Connexion perdue', type: 'error' });
    },

    copyIdBlock() {
      const textToCopy = this.selectedBlock.idblock;
      const tempInput = document.createElement('input');
      tempInput.value = textToCopy;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      this.showNotification({ message: 'ID copié avec succès', type: 'success' });
    },

    openWhatsAppTest() {
      if (!this.chatbot || !this.chatbot.display_phone_number) {
        this.showNotification({ 
          message: 'Numéro WhatsApp du chatbot non configuré', 
          type: 'error' 
        });
        return;
      }
      
      if (!this.selectedBlock || !this.selectedBlock.idblock) {
         this.showNotification({ 
          message: 'Aucun bloc sélectionné', 
          type: 'error' 
        });
        return;
      }

      // Nettoyer le numéro de téléphone (garder seulement les chiffres)
      const rawNumber = this.chatbot.display_phone_number.replace(/\D/g, '');
      
      if (!rawNumber) {
        this.showNotification({ 
          message: 'Format du numéro de téléphone invalide', 
          type: 'error' 
        });
        return;
      }

      const blockId = this.selectedBlock.idblock;
      // Créer le message avec le format <goto:ID_DU_BLOC>
      const message = encodeURIComponent(`<goto:${blockId}>`);
      const url = `https://wa.me/${rawNumber}?text=${message}`;
      
      // Ouvrir dans un nouvel onglet
      window.open(url, '_blank');
    },

    // Méthodes pour gérer la position de scroll
    saveScrollPosition() {
      const workspace = document.querySelector('.workspace');
      if (workspace) {
        this.scrollPosition = workspace.scrollTop;
      }
    },

    restoreScrollPosition() {
      this.$nextTick(() => {
        const workspace = document.querySelector('.workspace');
        if (workspace && this.scrollPosition !== undefined) {
          workspace.scrollTop = this.scrollPosition;
        }
      });
    },

    async toggleGlobalJarvis(event) {
      if (!this.defaultBlock) return;
      this.defaultBlock.jarvis = event.target.checked;
      await this.updateJarvisState(this.defaultBlock);
    },

    async updateJarvisState(block = null) {
      const targetBlock = block || this.selectedBlock;
      if (!targetBlock) return;

      try {
        // Sauvegarder la position de scroll avant la sauvegarde
        this.saveScrollPosition();
        
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Session expirée');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const response = await axiosPeelo.post(`/strong/update/jarvis/${targetBlock.idblock}`, {
          status: targetBlock.jarvis
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          this.showNotification({
            message: response.data.message,
            type: 'success'
          });
        } else {
          this.showNotification({
            message: response.data.message || 'Erreur lors de la mise à jour de Jarvis',
            type: 'error'
          });
        }
        
        // Restaurer la position de scroll après la sauvegarde
        this.restoreScrollPosition();
      } catch (error) {
        this.showNotification({
          message: `Erreur: ${error.response?.data?.error || 'Erreur lors de la mise à jour de Jarvis'}`,
          type: 'error'
        });
        //consoleError('Erreur lors de la mise à jour de Jarvis:', error);
        
        // Restaurer la position même en cas d'erreur
        this.restoreScrollPosition();
      }
    },

  },
};
</script>

<style scoped>
/* Active icon state in header */
.btn-pill-dark.is-active-icon {
  background: rgba(16, 185, 129, 0.15) !important;
  color: #10b981 !important;
  border-color: #10b981 !important;
}

.griot-ia-link-wrapper {
  margin-right: 12px;
}

.griot-ia-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #143028;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid rgba(20, 48, 40, 0.05);
}

.griot-ia-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.playground-action-bar {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(20px) saturate(180%);
  padding: 4px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  gap: 2px;
  margin-left: auto;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: #143028; /* Peelo Dark */
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}

.action-btn:hover {
  background: rgba(20, 48, 40, 0.12);
}

.action-btn.is-active {
  background: #143028;
  color: #DAFFD1; /* Peelo Accent */
}

.action-btn svg {
  opacity: 0.9;
}

.action-btn:hover svg {
  opacity: 1;
}

.action-divider {
  width: 1px;
  height: 16px;
  background: rgba(20, 48, 40, 0.15);
  margin: 0 4px;
}

.action-item-wrapper {
  display: flex;
  align-items: center;
  padding: 0 8px;
}

/* Hide default browser tooltip */
.playground-action-bar button[title] {
  /* On désactive les titles par défaut pour utiliser les nôtres */
}

/* Tooltip Custom Logic */
[data-tooltip] {
  position: relative;
}

/* On désactive les pseudos-éléments CSS car on utilise maintenant le globalTooltip JS */
[data-tooltip]::before,
[data-tooltip]::after {
  display: none !important;
}

/* Mini AI Switch for Action Bar */
.mini-ai-switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
}

.mini-ai-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.mini-ai-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e5e7eb;
  transition: .4s;
  border-radius: 34px;
}

.mini-ai-slider:before {
  position: absolute;
  content: "";
  height: 12px;
  width: 12px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .mini-ai-slider {
  background-color: #16a34a;
}

input:checked + .mini-ai-slider:before {
  transform: translateX(14px);
}

/* Mobile Warning Styles */
/* ===== Playground mobile : barre d'onglets Moo / Tester + fond de test ===== */
.mobile-tabbar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  display: flex;
  gap: 6px;
  padding: 6px 10px calc(6px + env(safe-area-inset-bottom, 0px));
  background: #0A1A16;
  border-top: 1px solid rgba(218, 255, 209, 0.14);
  z-index: 2147483647;
}
.mobile-tabbar .mtab {
  flex: 1;
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  background: transparent; border: none; cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px; font-weight: 600;
  padding: 8px 4px; border-radius: 12px;
  transition: color 0.15s, background 0.15s;
}
.mobile-tabbar .mtab.active { color: #DAFFD1; background: rgba(218, 255, 209, 0.10); }
.mobile-tabbar .mtab-ico { display: flex; }

.mobile-test-backdrop {
  position: fixed; inset: 0;
  z-index: 500000;
  background: linear-gradient(160deg, #143028 0%, #0A1A16 100%);
  display: flex; align-items: center; justify-content: center;
  text-align: center; padding: 24px;
}
.mobile-test-backdrop .mtb-inner { max-width: 300px; color: #fff; }
.mobile-test-backdrop .mtb-logo {
  width: 56px; height: 56px; margin: 0 auto 18px;
  border-radius: 16px; background: #DAFFD1; color: #143028;
  font-weight: 800; font-size: 28px;
  display: flex; align-items: center; justify-content: center;
}
.mobile-test-backdrop h3 { font-size: 1.25rem; margin: 0 0 10px; font-weight: 700; }
.mobile-test-backdrop p { color: rgba(255, 255, 255, 0.72); font-size: 0.95rem; line-height: 1.55; margin: 0; }

/* Onglet « Blocs » mobile : le graphe du flux prend tout l'écran.
   Le panneau latéral desktop (340px) est masqué — trop large pour un téléphone. */
.playground-container.mobile-mode { height: 100dvh; }
.playground-container.mobile-mode .sidebar { display: none; }
.playground-container.mobile-mode .workspace {
  margin: 0;
  border-radius: 0;
  padding: 1rem 0.75rem calc(90px + env(safe-area-inset-bottom, 0px)); /* place pour la barre d'onglets */
}

/* NB : la réserve de hauteur pour la barre d'onglets est dans AiCopilotSidebar.vue
   (la sidebar Moo n'est PAS un descendant de .playground-container, :deep ne matche pas). */

.playground-container {
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.sidebar {
  width: 340px;
  background: var(--dark-bg, #143028);
  border-right: 1px solid var(--dark-border, rgba(255, 255, 255, 0.05));
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow-y: auto;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--dark-text, white);
}

.sidebar.expanded {
  width: 350px;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--dark-border-light, rgba(255, 255, 255, 0.1));
}

.chatbot-info {
  padding: 0 0 1rem 0;
  border-bottom: 1px solid var(--dark-border-light, rgba(255, 255, 255, 0.1));
  margin-bottom: 1rem;
  text-align: left;
  margin-left: 0;
  padding-left: 0;
}

.chatbot-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  text-decoration: none;
  display: block;
  margin-bottom: 0.5rem;
  transition: opacity 0.2s ease;
}

.chatbot-name:hover {
  opacity: 0.8;
}

.chatbot-description {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chatbot-identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chatbot-details {
  flex: 1;
  min-width: 0; /* Ensures truncation works */
}

.chatbot-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--dark-hover-bg, rgba(255, 255, 255, 0.08));
  border: 1px solid var(--dark-border-light, rgba(255, 255, 255, 0.1));
  flex-shrink: 0;
}

.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
}

.sidebar-back-btn {
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;
}

.sidebar-back-btn:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.logo {
  height: 24px;
  display: flex;
  align-items: center;
}

.logo img {
  height: 100%;
  object-fit: contain;
  width: auto;
  opacity: 0.9;
}

.add-btn {
  background: var(--dark-hover-bg, rgba(255, 255, 255, 0.08));
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  position: relative;
}

.add-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.add-btn .tooltip-text {
  visibility: hidden;
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background-color: #1F2937;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 10000;
}

.add-btn .tooltip-text::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent #1F2937 transparent transparent;
}

.add-btn:hover .tooltip-text,
.add-btn .tooltip-text.visible {
  visibility: visible;
  opacity: 1;
}

.add-btn-subtle {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  margin-right: -4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(4px);
  position: relative; /* Ensure tooltip can position relative if needed, though we use fixed in JS */
}

/* Tooltip Styles */
.tooltip-text {
  visibility: hidden;
  position: fixed; /* JS handles positioning */
  background-color: #1F2937;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 999999;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.tooltip-text.visible {
  visibility: visible;
  opacity: 1;
}

.add-btn-subtle:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.search-container {
  position: relative;
  width: 100%;
  margin-bottom: 0.75rem;
}

.search-input {
  display: flex;
  align-items: center;
  background: var(--dark-hover-bg, rgba(255, 255, 255, 0.08));
  border-radius: 8px;
  padding: 0.5rem;
}

.search-input input {
  flex: 1;
  background: none;
  border: none;
  color: white;
  padding: 0.5rem;
  font-size: 0.9rem;
}

.search-input input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.search-input svg {
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0.5rem;
}

.folder-section {
  margin: 1.5rem 0;
}

.folder-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.folder-header:hover {
  background: rgba(255, 255, 255, 0.1);
}

.folder-header svg {
  transition: transform 0.2s ease;
}

.folder-header svg.rotated {
  transform: rotate(-90deg);
}

.folder-content {
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.flow-button {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  height: 42px;
  white-space: nowrap;
  flex: 0 0 auto;
  width: fit-content;
}

.flow-button:not(.active):hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.flow-button.active {
  background: #DAFFD1;
  color: #143028;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.flow-button.add-flow {
  border: 1px dashed rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  margin-left: auto;
}

.flow-button.add-flow:hover {
  border-color: white;
  color: white;
}

.new-folder-button {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #64748b;
  cursor: pointer;
  margin-top: 1rem;
}

.new-folder-button svg {
  margin-right: 0.5rem;
  color: #94a3b8;
}

.new-folder-button:hover {
  color: #334155;
  background-color: #f8fafc;
}

/* Override the global main-content style specifically for this component */
.playground-container .main-content {
  flex: 1;
  padding: 0;
  overflow-y: auto;
  margin-left: 0 !important; /* Remove default left margin with !important to override global styles */
  display: flex;
  flex-direction: column;
}

.notification-banner {
  background-color: #fff3e0;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #e65100;
  font-size: 0.9rem;
}

.connect-btn {
  background-color: #ff9800;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  cursor: pointer;
}

.workspace {
  flex: 1;
  position: relative;
  padding: 3rem 2rem;
  background: white;
  border-radius: 12px;
  margin: 1rem;
   
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: visible;
}

.workspace.full-canvas {
  padding: 0 !important;
  overflow: hidden !important;
}

.block-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 0;
  background: transparent;
}

.block-header svg {
  margin-top: 6px;
  flex-shrink: 0;
}

.block-name-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
}

.block-name {
  font-size: 24px;
  font-weight: 500;
  color: #2c3e50;
  cursor: text;
}

.block-name.no-name {
  color: #999;
  font-style: italic;
}

.block-name-input {
  font-size: 24px;
  font-weight: 500;
  color: #2c3e50;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  width: 100%;
  font-family: inherit;
  -webkit-appearance: none;
  appearance: none;
  outline: none !important;
}

.block-name-input:focus,
.block-name-input:active,
.block-name-input:hover,
.block-name-input:-moz-focusring,
.block-name-input:focus-visible,
.block-name-input:focus-within,
.block-name-input:visited {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

/* Supprimer la bordure de focus sur Firefox */
.block-name-input::-moz-focus-inner {
  border: 0 !important;
  outline: none !important;
}

/* Supprimer la bordure de focus sur Chrome */
.block-name-input:focus-visible {
  outline: none !important;
  outline-offset: 0 !important;
}

/* Supprimer la bordure de focus sur Safari */
.block-name-input:focus {
  outline: none !important;
  -webkit-focus-ring-color: transparent !important;
}

/* Supprimer la bordure de focus sur IE/Edge */
.block-name-input::-ms-clear,
.block-name-input::-ms-reveal {
  display: none !important;
}

.block-name-input:-ms-input-placeholder {
  color: transparent !important;
}

/* Supprimer l'effet de focus lors du tab */
.block-name-input:not(:focus-visible) {
  outline: none !important;
}

.char-count {
  position: absolute;
  right: -45px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #999;
  opacity: 0.7;
}

.char-count-limit {
  color: #ff4444;
}

.block-header svg {
  color: #666;
  opacity: 0.7;
}

.block-info {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.info-row {
  display: flex;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.info-label {
  font-weight: 600;
  width: 120px;
  color: #667eea;
}

.info-value {
  color: #333;
  flex: 1;
  font-weight: 500;
}

.flow-title {
  text-align: center;
  margin: 3rem 0 2rem;
  color: #333;
  font-size: 1.3rem;
  font-weight: 700;
  position: relative;
}

.flow-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.flow-empty {
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  color: #666;
  font-style: italic;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 2px dashed #e0e0e0;
}

.add-first-element {
  margin-top: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.add-first-element:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.start-node {
  top: 250px;
  left: 100px;
}

.node-header {
  padding: 0.75rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8f9fa;
}
 
.element-header {
  display: none; /* Masquer l'en-tête pour un look plus propre */
}

.element-type {
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
  display: none;
}

.element-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.flow-element:hover .element-actions {
  opacity: 1;
}

.element-actions .action-btn {
  background: white;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
}

.element-actions .action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.element-actions .edit-btn svg {
  color: #667eea;
}

.element-actions .delete-btn svg {
  color: #ff6b6b;
}

.element-content {
  padding: 0;
  display: block;
  position: static;
}

.element-footer {
  display: none; /* Masquer le footer pour un look plus propre */
}

/* Styles spécifiques par type d'élément - couleurs plus subtiles */
.element-type-text {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  border: 1px solid #e0e7ff;
}

.element-type-text_button {
  background: linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%);
  border: 1px solid #e9d5ff;
}

.element-type-list {
  background: linear-gradient(135deg, #fff8f0 0%, #fef3e2 100%);
  border: 1px solid #fed7aa;
}

.element-type-image {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
}

.element-type-audio {
  background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);
  border: 1px solid #a5f3fc;
}

.element-type-delay {
  background: linear-gradient(135deg, #fafafa 0%, #f4f4f5 100%);
  border: 1px solid #e4e4e7;
}

.element-type-request {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
}

.element-type-answer {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
  overflow: visible !important;
  z-index: 100 !important;
}

.element-type-redirect {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  border: 1px solid #fca5a5;
}

.element-type-notification {
  background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
  border: 1px solid #f9a8d4;
}

.element-type-variable_insert {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
}

.element-type-document {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
}

/* Styles pour le nouveau wrapper d'éléments */
.flow-element-wrapper {
  margin: 4rem auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: none;
  overflow: visible;
  transition: all 0.3s ease;
  cursor: grab;
  width: 90%;
  max-width: 600px;
  padding: 0;
  position: relative;
  z-index: 1;
}

.flow-element-wrapper:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.flow-element-wrapper:has(.multiselect--active) {
  z-index: 9998 !important;
  overflow: visible !important;
}

.flow-element-wrapper .multiselect--active {
  z-index: 9999 !important;
}

/* Ensure wrappers with open internal dropdowns (like ConditionElement) are on top */
.flow-element-wrapper:has(.dropdown-open) {
  z-index: 9998 !important;
  overflow: visible !important;
}

.flow-element-wrapper:active {
  cursor: grabbing;
}

.flow-element-wrapper.dragging {
  opacity: 0.8;
  transform: scale(1.05) translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.flow-element-wrapper.drag-over {
  border: 3px dashed #667eea;
  transform: scale(1.02);
}

.flow-button.drag-over-block {
  border: 2px dashed #1EA355 !important;
  background-color: #DAFFD1 !important;
  color: #143028 !important; /* Forces dark green text on the light green background */
  transform: scale(1.02);
}

/* Modal text color overrides for Peelo Green branding */

/* Premium Modal Base */
.transfer-modal-content.premium-modal {
  max-width: 480px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 0;
  overflow: visible;
  background: #FFFFFF;
  transition: all 0.3s ease;
}

.modal-title-transfer {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.transfer-modal-content.premium-modal .modal-header {
  padding: 24px 24px 16px;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.transfer-modal-content.premium-modal .modal-body {
  padding: 0 24px 24px;
}

/* Premium Buttons */
.cancel-transfer-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-transfer-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
  color: #111827;
}

.confirm-transfer-btn {
  padding: 8px 16px;
  background: #DAFFD1;
  border: 1px solid #1EA355; /* keeping a border allows it to pop */
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #143028;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 12px;
}

.confirm-transfer-btn:hover:not(:disabled) {
  background: #C4F5BA;
  border-color: #167F42;
}

.confirm-transfer-btn:disabled {
  background: #e6ffdf;
  border-color: #a0d8b8;
  color: #8da69c;
  cursor: not-allowed;
}

/* Dark Mode Premium Modal */
html.dark .transfer-modal-content.premium-modal {
  background: #0A1A16 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

html.dark .transfer-modal-content.premium-modal .modal-header {
  border-bottom: none;
}

html.dark .transfer-modal-content.premium-modal .modal-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
}

html.dark .transfer-modal-content.premium-modal .modal-body p {
  color: #E2E8F0 !important;
}

html.dark .transfer-modal-content.premium-modal .modal-body p[style*="color: #666"] {
  color: #9CA3AF !important; 
}

html.dark .modal-title-transfer {
  color: #FFFFFF;
}

html.dark .cancel-transfer-btn {
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.2);
  color: #FFFFFF;
}

html.dark .cancel-transfer-btn:hover {
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.3);
}

html.dark .close-btn {
  color: #4B5563;
  opacity: 0.8;
}

html.dark .close-btn:hover {
  color: #FFFFFF;
  opacity: 1;
}

/* Dark mode handling for drag over indicator */
html.dark .flow-button.drag-over-block {
  border: 2px dashed #10B981 !important;
  background-color: #DAFFD1 !important; 
  color: #143028 !important;
}

.flow-element-wrapper .element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.flow-element-wrapper .element-type {
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
}

.flow-element-wrapper .element-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.flow-element-wrapper:hover .element-actions {
  opacity: 1;
}

.flow-element-wrapper .element-actions .action-btn {
  background: white;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
}

.flow-element-wrapper .element-actions .action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.flow-element-wrapper .element-actions .duplicate-btn svg {
  color: #667eea;
}

.flow-element-wrapper .element-actions .delete-btn svg {
  color: #ff6b6b;
}

.flow-element-wrapper .element-actions .delete-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #ef4444;
}

/* Styles pour le composant dynamique */
.dynamic-element {
  padding: 0;
  margin: 0;
  position: relative;
  z-index: auto;
}

/* Styles pour l'élément fallback */
.fallback-element {
  padding: 2rem;
  text-align: center;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 2px dashed #fca5a5;
  border-radius: 12px;
  margin: 1rem;
  color: #dc2626;
}

.fallback-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 1.1rem;
}

.fallback-element p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
}

.element-debug-details {
  margin-top: 1rem;
  text-align: left;
}

.element-debug-details summary {
  cursor: pointer;
  font-weight: 500;
  padding: 0.5rem;
  color: #333;
}

.block-name.no-name {
  color: #999;
}

.block-name-input {
  width: 100%;
  padding: 0;
  border: 0px solid #e0e0e0;
  font-weight: 500;
  border-radius: 4px;
  font-size: 24px;
}

.block-name-input:focus {
  outline: none;
  
}

.element-previous {
  font-style: italic;
}

/* Animation d'apparition pour les éléments */
@keyframes elementAppear {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.flow-element-wrapper {
  animation: elementAppear 0.4s ease-out;
  position: relative;
}
.flow-element-wrapper.is-moo-selected {
  outline: 2px solid rgba(218, 255, 209, 0.6);
  border-radius: 10px;
  box-shadow: 0 0 0 4px rgba(218, 255, 209, 0.1);
}
.moo-select-btn {
  position: absolute;
  top: 19px;
  right: 104px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(218, 255, 209, 0.15);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(218, 255, 209, 0.5);
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  padding: 0;
  z-index: 10;
}
body:has(.ai-sidebar.is-open) .moo-select-btn {
  display: flex;
}
.moo-select-btn:hover,
.moo-select-btn.is-active {
  color: #DAFFD1;
  background: rgba(218, 255, 209, 0.1);
  border-color: rgba(218, 255, 209, 0.4);
}

/* Contrôles de déplacement (Monter/Descendre) */
.element-move-controls {
  position: absolute;
  top: 50%;
  left: -40px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.flow-element-wrapper:hover .element-move-controls {
  opacity: 1;
}

.move-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(218, 255, 209, 0.2);
  background: #143028; /* Peelo Dark Green */
  color: #daffd1; /* Peelo Mint */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.move-btn:hover:not(:disabled) {
  background: #0f241d;
  transform: scale(1.1);
}

.move-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: rgba(20, 48, 40, 0.5);
}

/* Amélioration de la scrollbar pour tout le playground */
.playground-container *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

/* Premium Action Card (Compact) */
.premium-action-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(145deg, rgba(20, 48, 40, 0.15) 0%, rgba(20, 48, 40, 0.3) 100%);
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: none;
  position: relative;
  overflow: hidden;
}

.premium-action-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(145deg, rgba(218, 255, 209, 0.05) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.premium-action-card:hover {
  transform: translateY(-1px);
  border-color: rgba(218, 255, 209, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.premium-action-card:hover::before {
  opacity: 1;
}

.premium-action-card.is-active {
  background: #DAFFD1;
  border-color: #DAFFD1;
  box-shadow: 0 4px 15px rgba(218, 255, 209, 0.2);
}

.premium-action-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(218, 255, 209, 0.1);
  color: #DAFFD1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.premium-action-card.is-active .premium-action-icon {
  background: rgba(20, 48, 40, 0.1);
  color: #143028;
}

.premium-action-text {
  flex: 1;
  z-index: 1;
}

.premium-action-text h4 {
  margin: 0;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.premium-action-card.is-active .premium-action-text h4 {
  color: #143028;
  font-weight: 600;
}

.premium-action-text p {
  display: none;
}

.premium-action-arrow {
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.2s ease;
  transform: translateX(-2px);
  opacity: 0;
}

.premium-action-card:hover .premium-action-arrow {
  transform: translateX(0);
  opacity: 1;
  color: #DAFFD1;
}

/* ===== Carte Constitution (variante distinctive) ===== */
.constitution-action-card {
  align-items: center;
  gap: 11px;
  padding: 11px 13px;
  background: linear-gradient(135deg, rgba(218, 255, 209, 0.06) 0%, rgba(20, 48, 40, 0.35) 100%);
  border: 1px solid rgba(218, 255, 209, 0.16);
  border-left: 2px solid #DAFFD1;
}
.constitution-action-card::before {
  background: radial-gradient(120px 60px at 12% 0%, rgba(218, 255, 209, 0.14) 0%, transparent 70%);
  opacity: 1;
}
.constitution-action-card:hover {
  border-color: rgba(218, 255, 209, 0.4);
  border-left-color: #DAFFD1;
  box-shadow: 0 6px 20px rgba(218, 255, 209, 0.12);
}
.constitution-icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(218, 255, 209, 0.12);
  color: #DAFFD1;
  box-shadow: inset 0 0 0 1px rgba(218, 255, 209, 0.18);
}
.constitution-action-card:hover .constitution-icon {
  box-shadow: inset 0 0 0 1px rgba(218, 255, 209, 0.35), 0 0 14px rgba(218, 255, 209, 0.25);
}
.constitution-text { display: flex; flex-direction: column; gap: 1px; }
.constitution-subtitle {
  font-size: 10.5px;
  color: rgba(218, 255, 209, 0.55);
  letter-spacing: 0.02em;
}
.constitution-action-card .premium-action-arrow { opacity: 0.5; transform: translateX(0); color: rgba(218,255,209,0.5); }
.constitution-action-card:hover .premium-action-arrow { opacity: 1; }

.premium-action-card.is-active .premium-action-arrow {
  transform: translateX(0);
  opacity: 1;
  color: #143028;
}

.playground-container *::-webkit-scrollbar-track {
  background: transparent;
}

.playground-container *::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.15);
  border-radius: 10px;
}

.playground-container *::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.4);
}

.tools-sidebar {
  position: fixed;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: white;
  padding: 1rem 0.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 80%;
  overflow-y: auto;
  z-index: 1000;
}

.tool-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;
}

.tool-btn:hover {
  background: #f5f5f5;
  color: #1EA355;
  border-color: #1EA355;
  transform: translateX(-2px);
}

.test-flow-btn {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: #1EA355;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-weight: 500;
}

.test-flow-btn:hover {
  background: #167F42;
  transform: translateY(-1px);
}

.btn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
}

/* Loading and Error States with green theme */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
}

.loading-spinner svg {
  color: white;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  text-align: center;
  padding: 2rem;
}

.empty-state svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  font-size: 0.9rem;
  max-width: 300px;
  line-height: 1.4;
}

/* Header buttons container */
.header-buttons {
  position: absolute;
  top: 1rem;
  left: 410px; /* Position moved to 410px as requested */
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 10;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-buttons.ai-sidebar-open {
  right: 470px; /* 450px sidebar width + 20px padding */
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #1EA355;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 44px;
  box-sizing: border-box;
}

.back-button:hover {
  background: #f5f5f5;
  transform: translateY(-1px);
}

.back-button svg {
  width: 16px;
  height: 16px;
}

/* Styles des notifications */
.notification-wrapper {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  animation: slideIn 0.3s ease forwards;
}

@keyframes slideIn {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 25px;
  border-radius: 8px;
  z-index: 9999999;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.notification.success {
  background-color: #4CAF50;
  color: white;
}

.notification.error {
  background-color: #f44336;
  color: white;
}

.notification span {
  font-size: 14px;
  font-weight: 500;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  position: relative;
  z-index: 1;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
  position: relative;
}

.char-count {
  position: absolute;
  right: -30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #666;
  opacity: 0.7;
}

.char-count-limit {
  color: #f44336;
  font-weight: bold;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: #1EA355;
  box-shadow: 0 0 0 2px rgba(30, 163, 85, 0.1);
}

.form-group input:disabled {
  background: #f9f9f9;
  color: #666;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #666;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f5f5f5;
}

.create-btn {
  padding: 0.75rem 1.5rem;
  background: #1EA355;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-btn:hover:not(:disabled) {
  background: #167F42;
}

.create-btn:disabled {
  background: #a0d8b8;
  cursor: not-allowed;
}

/* Styles du tooltip */
.add-btn, .tool-btn {
  position: relative;
}

.tooltip-text {
  visibility: hidden;
  position: fixed;
  left: 110%;
  top: 50%;
  transform: translateY(-50%);
  background-color: #1F2937;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 40000000000;
}

.tooltip-text::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent #1F2937 transparent transparent;
}

.tooltip-text.visible {
  position: fixed !important;
  z-index: 99999 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Styles du panneau d'information dynamique */
.element-info-panel {
  position: fixed;
  right: 100px;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  min-width: 280px;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  z-index: 10000;
  animation: slideInRight 0.2s ease-out;
  pointer-events: none;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
}

.element-info-panel h4 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #1EA355;
  font-weight: 600;
}

.element-info-panel p {
  margin: 0;
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
}

.block-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.block-name {
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.block-name.no-name {
  color: #999;
}

.block-name-input {
  width: 100%;
  padding: 0;
  border: 0px solid #e0e0e0;
  font-weight: 500;
  border-radius: 4px;
  font-size: 24px;
}

.block-name-input:focus {
  outline: none;
}

.links-card {
  background: transparent;
  border: none;
  margin-bottom: 2rem;
  overflow: visible;
}

.links-card .links-header {
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  width: fit-content;
  margin-bottom: 0.75rem;
}

.links-card .links-header:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.links-card .links-header .links-header-content {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.links-card .links-header .links-header-content svg {
  color: #667eea;
  width: 16px;
  height: 16px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.links-card .links-header .links-title-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
}

.links-card .links-header .links-summary {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.8), rgba(118, 75, 162, 0.8));
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  backdrop-filter: blur(5px);
}

.links-card .links-header .collapse-icon {
  color: #64748b;
  transition: all 0.3s ease;
  width: 16px;
  height: 16px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.links-card .links-header .collapse-icon.expanded {
  transform: rotate(180deg);
  color: #667eea;
}

.links-card .links-content {
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.links-card .links-group {
  margin-bottom: 1rem;
  padding: 1rem;
}

.links-card .links-group:last-child {
  margin-bottom: 0;
}

.links-card .links-group .group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.links-card .links-group .group-header svg {
  width: 12px;
  height: 12px;
}

.links-card .loading-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #64748b;
  font-size: 0.85rem;
  justify-content: center;
}

.links-card .loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.links-card .empty-state {
  padding: 0.75rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.8rem;
  font-style: italic;
}

.links-card .links-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.links-card .link-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  max-width: 150px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.links-card .link-badge:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.links-card .link-badge.inbound {
  border-left: 4px solid #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.08));
  border: 1px solid rgba(16, 185, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.links-card .link-badge.inbound::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.02), rgba(5, 150, 105, 0.02));
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.links-card .link-badge.inbound:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.12));
  border-color: rgba(16, 185, 129, 0.5);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.2);
  border-left-color: #059669;
}

.links-card .link-badge.inbound:hover::before {
  opacity: 1;
}

.links-card .link-badge.inbound .badge-text {
  color: #047857;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.8);
  position: relative;
  z-index: 1;
}

.links-card .link-badge.inbound .badge-source {
  color: #10b981;
  font-weight: 600;
  font-size: 0.65rem;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(16, 185, 129, 0.2);
  margin-top: 0.4rem;
  position: relative;
  z-index: 1;
  text-align: center;
  display: block;
  width: fit-content;
  max-width: 100%;
}

.links-card .link-badge.outbound {
  border-left: 4px solid #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  border: 1px solid rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
}

/* Premium Train Button Styles */
/* Premium Dark Pill Style - Shared */
.btn-pill-dark {
  background-color: #1F2937; /* Dark almost black */
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 9999px; /* Pill shape */
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  height: 42px; /* Fixed height for consistency */
}

.btn-pill-dark:hover {
  background-color: #374151;
  transform: translateY(-1px);
}

.btn-pill-dark:active {
  transform: translateY(0);
}

.btn-pill-dark:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-pill-dark svg {
  width: 16px;
  height: 16px;
}

.train-btn-premium:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.2), 0 4px 6px -2px rgba(16, 185, 129, 0.1);
  background: linear-gradient(135deg, #34d399 0%, #059669 100%);
}

.train-btn-premium:active {
  transform: translateY(0);
}

.train-btn-premium:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trained-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.6rem 1.5rem;
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 9999px; /* Pill shape */
  color: #1F2937;
  font-weight: 500;
  font-size: 0.9rem;
}

.links-card .link-badge.outbound {
  border-left: 4px solid #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  border: 1px solid rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.links-card .link-badge.outbound::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.02), rgba(118, 75, 162, 0.02));
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.links-card .link-badge.outbound:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.12));
  border-color: rgba(102, 126, 234, 0.5);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
  border-left-color: #5a67d8;
}

.links-card .link-badge.outbound:hover::before {
  opacity: 1;
}

.links-card .link-badge.outbound .badge-text {
  color: #4c51bf;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.8);
  position: relative;
  z-index: 1;
}

.links-card .link-badge.outbound .badge-source {
  color: #667eea;
  font-weight: 600;
  font-size: 0.65rem;
  background: rgba(102, 126, 234, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(102, 126, 234, 0.2);
  margin-top: 0.4rem;
  position: relative;
  z-index: 1;
  text-align: center;
  display: block;
  width: fit-content;
  max-width: 100%;
}

.links-card .link-badge .badge-text {
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
}

.links-card .link-badge .badge-source {
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.links-card .no-links-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  margin: 1rem;
}

.links-card .no-links-state svg {
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

.links-card .no-links-state span {
  font-size: 0.85rem;
  font-style: italic;
}

.save-status {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 12px;
  z-index: 10000;
  animation: slideInFromRight 0.3s ease-out;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-family: system-ui, -apple-system, sans-serif;
}

.save-status.saving {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.95), rgba(255, 193, 7, 0.95));
  color: white;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.save-status.saved {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.95), rgba(67, 160, 71, 0.95));
  color: white;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.save-status.error {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.95), rgba(211, 47, 47, 0.95));
  color: white;
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.save-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.save-icon.spinning {
  animation: spin 1s linear infinite;
}

.save-text {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.notification.success {
  background-color: #4CAF50;
  color: white;
}

.notification.saved {
  background-color: #4CAF50;
  color: white;
}

.notification.saving {
  background-color: #ff9800;
  color: white;
}

.notification.error {
  background-color: #f44336;
  color: white;
}

.notification svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.block-name-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.block-id-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.block-id {
  font-size: 10px;
  color: #1d1c1c;
  font-weight: 500;
  font-family: 'Courier New', monospace;
  background: #f1f3f4;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  letter-spacing: 0.5px;
  cursor: pointer;
}

.copy-id-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.8;
}

.copy-id-btn:hover {
  background: #e5f3ff;
  color: #1EA355;
  opacity: 1;
  transform: scale(1.15);
}

.copy-id-btn svg {
  width: 13px;
  height: 13px;
}

.test-wa-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.8;
  margin-left: 0.2rem;
}

.test-wa-btn:hover {
  background: #dcfce7;
  color: #16a34a; /* WhatsApp green */
  opacity: 1;
  transform: scale(1.15);
}

.test-wa-btn svg {
  width: 14px;
  height: 14px;
}

.test-wa-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.8;
  margin-left: 0.2rem;
}

.test-wa-btn:hover {
  background: #dcfce7;
  color: #16a34a; /* WhatsApp green */
  opacity: 1;
  transform: scale(1.15);
}

.test-wa-btn svg {
  width: 14px;
  height: 14px;
}

/* Styles pour la description du bloc */
.block-description-container {
  margin-top: 1.5rem;
  width: 100%;
  max-width: 800px;
}

.description-edit-mode {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  position: relative;
}

.description-display {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.description-display:hover {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.description-prefix {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  flex-shrink: 0;
  margin-top: 2px;
}

.description-text {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
  flex: 1;
  font-style: italic;
  word-wrap: break-word;
}

.description-text.no-description {
  color: #94a3b8;
  opacity: 0.8;
}

.description-input {
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #64748b;
  background: transparent;
  border: none;
  padding: 0;
  resize: none;
  min-height: 18px;
  max-height: 200px;
  line-height: 1.3;
  outline: none;
  overflow: hidden;
  width: 100%;
}

.description-input:focus {
  outline: none;
  background: transparent;
  border: none;
}

.description-input::placeholder {
  color: #94a3b8;
  font-style: italic;
}

.description-char-count {
  position: absolute;
  right: -50px;
  top: 0;
  font-size: 11px;
  color: #4b5563;
  font-family: 'Courier New', monospace;
  opacity: 1;
  font-weight: 600;
}

.description-char-count.char-limit {
  color: #ef4444;
  font-weight: 600;
  opacity: 1;
}

/* AI Toggle Styles */
.ai-toggle-container {
  display: flex;
  align-items: center;
}

.ai-toggle-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  min-width: 140px;
  height: 44px;
  box-sizing: border-box;
}

.ai-toggle-wrapper:hover {
  background: #f5f5f5;
  transform: translateY(-1px);
}

.ai-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #1EA355;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ai-label:hover {
  color: #16a34a;
  text-decoration: none;
}

/* Custom Switch Premium Design - Enlarged */
.ai-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 22px;
  margin-left: 0;
}

.ai-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.ai-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.ai-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background: #9ca3af;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

input:checked + .ai-slider {
  background: rgba(218, 255, 209, 0.15);
  border-color: rgba(218, 255, 209, 0.4);
  box-shadow: 0 0 15px rgba(218, 255, 209, 0.1);
}

input:checked + .ai-slider:before {
  transform: translateX(20px);
  background: #DAFFD1;
  box-shadow: 0 0 10px rgba(218, 255, 209, 0.8);
}

.ai-switch:hover .ai-slider {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

.ai-switch:hover input:checked + .ai-slider {
  background: rgba(218, 255, 209, 0.25);
  border-color: rgba(218, 255, 209, 0.5);
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

/* Dump Modal & Button Styles */
.dump-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #3b82f6; /* Blueish */
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 10px;
}

.dump-btn:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
}

/* Dump Modal Styles - CORRECTED */
.dump-modal {
  width: 90vw !important;
  max-width: 1200px !important;
  height: 85vh !important;
  display: flex !important;
  flex-direction: column !important;
}

/* ───── Coloration du System Prompt Moo ───── */
.moo-prompt-view {
  margin: 0;
  padding: 6px 20px 20px 20px;
  color: #e5e7eb;
  font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.moo-prompt-view--context {
  background: rgba(255, 233, 194, 0.03);
  border-left: 2px solid rgba(255, 107, 107, 0.4);
}
.moo-prompt-view .moo-h1 { color: #daffd1; font-weight: 700; font-size: 1.15em; display: inline-block; }
.moo-prompt-view .moo-h2 { color: #4ecdc4; font-weight: 700; }
.moo-prompt-view .moo-h3 { color: #a78bfa; font-weight: 600; }
.moo-prompt-view .moo-h4 { color: #93c5fd; font-weight: 600; }
.moo-prompt-view .moo-rule { color: #fbbf24; font-weight: 700; }
.moo-prompt-view .moo-warn { color: #fbbf24; background: rgba(251, 191, 36, 0.08); padding: 1px 4px; border-radius: 3px; display: inline-block; }
.moo-prompt-view .moo-bad { color: #fca5a5; background: rgba(239, 68, 68, 0.08); padding: 1px 4px; border-radius: 3px; display: inline-block; }
.moo-prompt-view .moo-good { color: #86efac; background: rgba(34, 197, 94, 0.08); padding: 1px 4px; border-radius: 3px; display: inline-block; }
.moo-prompt-view .moo-section { color: #4ecdc4; font-weight: 700; background: rgba(78, 205, 196, 0.06); padding: 2px 6px; border-radius: 3px; display: inline-block; }
.moo-prompt-view .moo-quote { color: #9ca3af; font-style: italic; }
.moo-prompt-view .moo-hr { color: rgba(255,255,255,0.18); }
.moo-prompt-view .moo-inlinecode {
  background: rgba(218, 255, 209, 0.08);
  color: #fef3c7;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 500;
}
.moo-prompt-view .moo-codeblock {
  display: block;
  background: rgba(0, 0, 0, 0.35);
  color: #fde68a;
  padding: 0 8px;
}
.moo-prompt-view .moo-codeblock-start,
.moo-prompt-view .moo-codeblock-end {
  display: block;
  color: #6b7280;
  background: rgba(0, 0, 0, 0.35);
  padding: 0 8px;
}
.moo-prompt-view .moo-tool {
  color: #4ecdc4;
  background: rgba(78, 205, 196, 0.10);
  padding: 0 4px;
  border-radius: 3px;
  font-weight: 600;
}
.moo-prompt-view .moo-id {
  color: #f0abfc;
  font-weight: 500;
}
.moo-prompt-view .moo-arrow {
  color: #ff8a65;
  font-weight: 700;
}
.moo-prompt-view .moo-bold {
  color: #ffffff;
  font-weight: 700;
}

.dump-modal .modal-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.dump-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #1e293b;
  overflow: hidden;
}

.dump-textarea {
  width: 100%;
  height: 100%;
  padding: 1rem;
  background-color: #1e293b;
  color: #a5b4fc;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 13px;
  border: 1px solid #334155;
  border-radius: 6px;
  resize: none;
  white-space: pre;
  overflow: auto;
}

.dump-textarea:focus {
  outline: none;
  border-color: #6366f1;
}










/* Create Variable Button Styles */
.create-variable-btn-link {
  text-decoration: none;
  display: block;
  width: 100%;
}

.create-variable-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  width: 100%;
  justify-content: flex-start;
}

.create-variable-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.create-variable-btn svg {
  flex-shrink: 0;
}

/* Dump Modal Tabs */
.modal-header.with-tabs {
  padding-bottom: 0;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-tabs {
  display: flex;
  gap: 1rem;
}

.modal-tabs button {
  background: none;
  border: none;
  padding: 1rem 0.5rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.modal-tabs button.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.modal-tabs button:hover:not(.active) {
  color: #374151;
}

/* Context Menu Styles */
/* Context menu CSS extracted to PlaygroundContextMenu.vue */

/* Global styles for Teleported Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex !important;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(2px);
}
.modal-overlay.dark-mode {
  background: rgba(10, 26, 22, 0.85);
}
.premium-modal {
  background: white;
  width: 100%;
  max-width: 480px;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05);
  overflow: hidden;
  animation: modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
}
@keyframes modalSlideUp {
from { opacity: 0; transform: translateY(20px) scale(0.98);
}
to { opacity: 1; transform: translateY(0) scale(1);
}
}
.premium-modal .modal-header {
  padding: 24px 24px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
}
.premium-modal .modal-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
}
.premium-modal .close-btn {
  background: #F3F4F6;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s;
}
.premium-modal .close-btn:hover {
  background: #E5E7EB;
  color: #374151;
}
.premium-modal .search-container {
  padding: 0 24px 20px;
}
.premium-modal .search-input-wrapper {
  position: relative;
}
.premium-modal .premium-search-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 44px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  font-size: 15px;
  color: #111827;
  transition: all 0.2s;
  box-sizing: border-box;
}
.premium-modal .premium-search-input:focus {
  background: white;
  border-color: #3B82F6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  outline: none;
}
.premium-modal .search-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
}
.premium-modal .modal-subtitle {
  padding: 0 24px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.premium-modal .agents-list {
  padding: 0 16px 24px;
  max-height: 360px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.premium-modal .agent-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  transition: background 0.2s;
  cursor: pointer;
  border: 1px solid transparent;
}
.premium-modal .agent-item:hover {
  background: #F9FAFB;
  border-color: #F3F4F6;
}
.premium-modal .agent-avatar-wrapper {
  position: relative;
  margin-right: 16px;
}
.premium-modal .agent-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%);
  color: #4338CA;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
.premium-modal .agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.premium-modal .agent-status-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
}
.premium-modal .agent-status-dot.active {
  background-color: #10B981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}
.premium-modal .agent-status-dot.inactive {
  background-color: #EF4444;
}
.premium-modal .agent-info {
  flex: 1;
  min-width: 0;
}
.premium-modal .agent-name {
  display: block;
  font-weight: 600;
  font-size: 15px;
  color: #111827;
  margin-bottom: 2px;
}
.premium-modal .agent-email {
  display: block;
  font-size: 13px;
  color: #6B7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.premium-modal .select-agent-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.premium-modal .select-agent-btn:hover {
  background: #111827;
  color: white;
  border-color: #111827;
}
.premium-modal .empty-search {
  text-align: center;
  padding: 40px 0;
  color: #9CA3AF;
  font-size: 14px;
}

/* === Dark Mode Overrides for Teleported Modal === */
.modal-overlay.dark-mode .premium-modal {
  background: #0A1A16;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}
.modal-overlay.dark-mode .premium-modal .modal-header h3 {
  color: #FFFFFF;
}
.modal-overlay.dark-mode .premium-modal .close-btn {
  background: rgba(255, 255, 255, 0.08);
  color: #6B7280;
}
.modal-overlay.dark-mode .premium-modal .close-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #FFFFFF;
}
.modal-overlay.dark-mode .premium-modal .premium-search-input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(218, 255, 209, 0.15);
  color: #FFFFFF;
}
.modal-overlay.dark-mode .premium-modal .premium-search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}
.modal-overlay.dark-mode .premium-modal .premium-search-input:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(218, 255, 209, 0.3);
  box-shadow: 0 0 0 4px rgba(218, 255, 209, 0.1);
}
.modal-overlay.dark-mode .premium-modal .search-icon {
  color: #daffd1;
}
.modal-overlay.dark-mode .premium-modal .modal-subtitle {
  color: #9CA3AF;
}
.modal-overlay.dark-mode .premium-modal .agent-item:hover {
  background: rgba(218, 255, 209, 0.08);
  border-color: rgba(218, 255, 209, 0.1);
}
.modal-overlay.dark-mode .premium-modal .agent-avatar {
  background: linear-gradient(135deg, rgba(218, 255, 209, 0.15) 0%, rgba(218, 255, 209, 0.08) 100%);
  color: #daffd1;
}
.modal-overlay.dark-mode .premium-modal .agent-status-dot {
  border-color: #0A1A16;
}
.modal-overlay.dark-mode .premium-modal .agent-name {
  color: #FFFFFF;
}
.modal-overlay.dark-mode .premium-modal .agent-email {
  color: rgba(218, 255, 209, 0.7);
}
.modal-overlay.dark-mode .premium-modal .select-agent-btn {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  color: #FFFFFF;
  box-shadow: none;
}
.modal-overlay.dark-mode .premium-modal .select-agent-btn:hover {
  background: #daffd1;
  color: #0A1A16;
  border-color: #daffd1;
}

</style>

<style scoped>
/* ======== Griot Training Indicator (Premium) ======== */
.griot-train-indicator {
  margin-right: auto;
  display: flex;
  align-items: center;
}

.griot-train-inner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 100px;
  background: #ffffff;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.griot-train-indicator.is-trained .griot-train-inner {
  background: #ffffff;
}

.griot-train-indicator.is-training .griot-train-inner {
  background: rgba(218, 255, 209, 0.1);
}

/* Status dot */
.griot-status-dot {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #143028; /* Peelo Dark Green */
  animation: griotPulseMint 2s infinite;
}

.griot-train-indicator.is-trained .griot-status-dot {
  background: #143028; /* Peelo Dark Green for finished */
  animation: none;
  border: none;
  box-shadow: 0 0 4px rgba(20, 48, 40, 0.2);
}

/* Bouncing ball (training) */
.griot-bounce-loader {
  width: 16px;
  height: 18px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-shrink: 0;
}

.griot-bounce-ball {
  width: 8px;
  height: 8px;
  background: #143028; /* Peelo Dark Green */
  border-radius: 50%;
  transform-origin: center bottom;
  animation: griotBounce 0.55s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite alternate;
}

@keyframes griotBounce {
  0% { transform: translateY(0) scaleX(1.4) scaleY(0.6); }
  100% { transform: translateY(-10px) scaleX(0.8) scaleY(1.2); }
}

/* Status label */
.griot-status-label {
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: #143028;
  transition: all 0.3s;
}

.griot-train-indicator.is-trained .griot-status-label {
  color: #143028 !important;
  font-weight: 600;
}

.griot-train-indicator.is-training .griot-status-label {
  color: #143028;
  font-family: 'Inter', system-ui, sans-serif;
  animation: griotShimmer 1.5s ease-in-out infinite alternate;
}

@keyframes griotPulseMint {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(218, 255, 209, 0.8); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(218, 255, 209, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(218, 255, 209, 0); }
}

@keyframes griotShimmer {
  0% { opacity: 0.5; }
  100% { opacity: 1; }
}

.flow-button {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  position: relative !important;
}

.block-name-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 5px; /* Reduced space to fit tighter */
}

.duplicate-block-icon-btn {
  background: white;
  border: 1px solid #e5e7eb;
  color: #6b7280;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  right: 32px; /* Position to the left of delete button */
  transform: translateY(-50%);
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.flow-button:hover .duplicate-block-icon-btn {
  opacity: 1;
}

.duplicate-block-icon-btn:hover {
  background-color: #f3f4f6;
  color: #111827;
  transform: translateY(-50%) scale(1.1);
  border-color: #d1d5db;
}

.duplicate-block-icon-btn svg {
  width: 12px;
  height: 12px;
}

.delete-block-icon-btn {
  background: white;
  border: 1px solid #fee2e2;
  color: #ef4444;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.flow-button:hover .delete-block-icon-btn {
  opacity: 1;
}

.delete-block-icon-btn:hover {
  background-color: #ef4444;
  color: white;
  transform: translateY(-50%) scale(1.1);
  border-color: #ef4444;
}

.delete-block-icon-btn svg {
  width: 12px;
  height: 12px;
}

.variable-item-pill .delete-var-btn {
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.variable-item-pill:hover .delete-var-btn {
  opacity: 1;
}

.variable-item-pill .delete-var-btn:hover {
  background: rgba(239, 68, 68, 0.1);
}

.ia-text-icon {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: currentColor;
  line-height: 1;
}

.hover-bg-white-10:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* ===== CONSTITUTION MODAL ===== */
.const-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.const-field {
  display: flex;
  flex-direction: column;
  background: rgba(218, 255, 209, 0.03);
  border: 1px solid rgba(218, 255, 209, 0.1);
  border-radius: 12px;
  padding: 14px;
}
.const-field:focus-within { border-color: rgba(218, 255, 209, 0.35); }
.const-field-full { grid-column: 1 / -1; }
.const-field label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #E5E7EB;
  margin-bottom: 4px;
}
.const-hint {
  font-size: 0.72rem;
  color: #9CA3AF;
  margin: 0 0 8px 0;
  line-height: 1.4;
}
.const-textarea {
  width: 100%;
  resize: vertical;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(218, 255, 209, 0.12);
  border-radius: 8px;
  padding: 9px 11px;
  color: #F3F4F6;
  font-size: 0.83rem;
  line-height: 1.5;
  font-family: inherit;
  outline: none;
}
.const-textarea:focus { border-color: rgba(218, 255, 209, 0.45); }
.const-textarea::placeholder { color: #6B7280; }
@media (max-width: 768px) {
  .const-grid { grid-template-columns: 1fr; }
}
</style>

<!-- Style GLOBAL (non scopé) : le widget agenttalk est injecté dans <body>, hors du composant.
     Sur mobile, on remonte la BULLE réduite au-dessus de la barre d'onglets (58px).
     On cible uniquement l'état réduit via [style*="width: 88px"] pour ne PAS toucher
     le chat ouvert (width: 100%), qui doit rester en plein écran. -->
<style>
@media (max-width: 768px) {
  #peelo-widget-iframe[style*="width: 88px"] {
    bottom: 74px !important;
  }
}
</style>